/**
 * THE WORDS THE PLACES SAY, in one file, so every line a client reads on Home, Schedule, Payments,
 * an invoice's own page, Messages, Feedback and Account can be read, and checked, together. `api/test/copy.test.ts` reads this file with the
 * rest of `web/src`, and every line answers to `faithfully-kept-business/docs/what-we-cannot-say.md`.
 *
 * Each line says where it comes from:
 *
 * - ARTBOARD: drawn in `faithfully-kept-business/design/home/`, word for word.
 * - SEAM: named in ops's contract, `docs/seams/home.md`, for a state no artboard draws.
 * - SHIPPED: already on a screen here, and reused rather than reworded.
 * - FOR THE OWNER: new, drawn on no artboard and named in no seam text, put to the owner on home
 *   issue 5, and accepted by the owner as written on 2026-09-28.
 * - NEW: drawn on no artboard and named in no seam text, written within `what-we-cannot-say.md` for
 *   a state the seam's final contract brings, which the owner accepts as written.
 * - WEBSITE: the website's own published words, quoted word for word with the file and line they
 *   stand at, from the pages its `docs/api/agreement-pages.md` promises to keep (Your agreement,
 *   round five, slice C2).
 *
 * The only words a screen shows that are not here are ops's: the office's, the cleaner's, and what
 * the person typed herself. The door, the sign-in steps and the page that says to call keep their
 * own words in their own files, as they were built, but for the lines the sign-in steps share with
 * the fresh-code step (`SIGN_IN_BUSY_BEFORE_PHONE`, `TOO_MANY_CODES_BEFORE_PHONE`,
 * `codesPausedBeforePhone` and `codeNotSentBeforePhone`) and the code step's lines after a code that
 * was not sent (`OTHER_WAY_IN`).
 *
 * Where the seam prints a line's words (ops's `docs/seams/home.md` at 0b51e49, section 5, "The words
 * this section points at"), the line here is those words with only what the push fills in changed,
 * and `contract-words.test.ts` holds it to them character for character.
 */
import { EMAIL_SHOWN, PHONE_SHOWN } from './contact.ts';
import type { CleansAskType, SendFailure, TooMany } from './api.ts';
import { dateInWords, shortDate, twelveHourTime, weekdayMonthDate } from './lib/dates.ts';
import { MARKS, type Mark } from './lib/month.ts';

/** A face on the Today card and on Feedback, as the seam names it for `visit.feedback`. */
export type Face = 'unhappy' | 'so_so' | 'happy' | 'delighted';

/** ARTBOARD (Main, Feedback): the four faces, in the order drawn, each with the seam's word for it. */
export const FACES: readonly { readonly face: Face; readonly label: string }[] = [
  { face: 'unhappy', label: 'Not good' },
  { face: 'so_so', label: 'Mixed' },
  { face: 'happy', label: 'Good' },
  { face: 'delighted', label: 'Very good' },
];

/** ARTBOARD (every place): the bottom nav's names, in the order drawn. Payments is a client's alone. */
export const NAV = {
  home: 'Home',
  schedule: 'Schedule',
  payments: 'Payments',
  messages: 'Messages',
} as const;

/**
 * NEW: the name a tab is read by while it carries a mark (provisional P49), its own name and then
 * what the mark says, so a mark is never a number, a dot or a colour alone. The badge draws the
 * count, "9+" past nine; the words say it, `NAV_MARKS.moreThanNine` past nine (`countInWords`).
 * Raised by the owner's walk of 2026-10-16: "there should be a bell or notification sign like
 * 1-2-3 whatever payments", and "not always clear if there is outstanding payment". No artboard
 * draws a mark; business issue 13's list carries it for one.
 */
export const NAV_MARKS = {
  /** "Payments, 2 to pay": the invoices she can pay from home now. */
  toPay: (count: string) => `${NAV.payments}, ${count} to pay`,
  /** "Messages, 3 new": what the office has said since she last opened Messages. */
  newMessages: (count: string) => `${NAV.messages}, ${count} new`,
  /** The Home tab while a clean of hers waits for "How was it?". */
  howWasIt: `${NAV.home}, a clean waits for your answer`,
  /** A count past nine, in words, which the badge draws as "9+". */
  moreThanNine: 'more than 9',
} as const;

/** ARTBOARD (Main): Home. */
export const HOME = {
  eyebrow: 'Your home',
  nextVisit: 'Next visit',
  today: 'Today',
  ready: 'Your home is ready',
  /**
   * NEW: the heading of Home's card of a clean ops pushed done on a day before today, under its day,
   * in place of "Your home is ready", which is said of today. In the past, as the month says a clean
   * gone by was done (`VISIT.wasDone`), and nothing of how long it took. Raised by the replay of the
   * owner's walk of 2026-10-17: yesterday's three cleans each still said "Your home is ready".
   * Messages keeps "Your home is ready" on the card it sent that day.
   */
  wasCleaned: 'Your home was cleaned',
  howWasIt: 'How was it?',
  writtenDown: 'What we have written down',
  theHouse: 'The house',
  leaveAlone: 'Leave alone',
  pets: 'Pets',
  gettingIn: 'Getting in',
  codes: 'Door and alarm codes stay with the office and never appear here.',
  ask: 'Ask us for something',
  answerComes: 'The answer comes to Messages.',
  /**
   * NEW: under the faces of a last clean, in place of "Sent to the office.", when ops recorded
   * nothing of her newest answer from home, because it already holds twelve from home on the clean
   * and its receipt says `recorded: false` (the seam, l.563 and l.602 at d1c7636). It says what an
   * invoice's page says in the same case (`INVOICE_FEEDBACK.alreadyBeforePhone`). The phone follows
   * it, and then a full stop.
   */
  alreadyBeforePhone: 'We already have your answers about this clean. To tell us more, call',
  /**
   * NEW: the button under `invoicesToPay` at the top of Home, which leads to Payments, where each
   * invoice is paid on its own (provisional P49).
   */
  goToPayments: 'Go to Payments',
  /**
   * NEW: at the top of Home, in place of `invoicesToPay`, for a client with nothing she can pay
   * from home now, once the household has come down and unless she owes an invoice the count
   * leaves out, which the saved method pays or no way from home can pay (provisional P49), with "Go
   * to Payments" under it. Payments' own "Nothing right
   * now." under To pay, said where no heading stands above it. Nothing she has just paid on
   * Stripe's page is counted, and it never says paid (binding rule 1). Raised by the owner's walk
   * of 2026-10-16, replayed: with nothing to pay, Home said nothing, so the answer to "is anything
   * owed?" was only an absence.
   */
  nothingToPay: 'Nothing to pay right now.',
} as const;

/**
 * NEW: at the top of Home, for a client with anything she can pay from home now, how many invoices
 * and what they come to, ops's balances added up and written as every amount Payments says
 * (`dollars`): "2 invoices to pay, $450", "1 invoice to pay, $212.50" (provisional P49). Raised by
 * the owner's walk of 2026-10-16: "not always clear if there is outstanding payment". Never for a
 * member, who is shown no money.
 */
export const invoicesToPay = (count: number, total: string) =>
  `${count} ${count === 1 ? 'invoice' : 'invoices'} to pay, ${total}`;

/**
 * NEW (round six, slice S3, `docs/seam.md` P85): at the top of Home, under `invoicesToPay` or
 * `HOME.nothingToPay`, for a client with an invoice owed whose payment is on its way, as Payments
 * draws it under On its way: one her household told the office it sent by Zelle, one ops says a bank
 * payment is clearing on, and one her browser says she has just paid on Stripe's page. How many, and
 * what they come to by ops's balances, written as every amount Payments says (`dollars`): "1 invoice
 * of $175 has a payment on its way. It shows as paid once the office has the payment." The amount
 * is the invoice's, never what she said she sent, which ops does not hold, and it never says paid
 * (binding rule 1). Raised by the replay of the owner's household on 2026-10-18, where Home said
 * "Nothing to pay right now." over her Zelle not yet matched, as calm as a settled household, and
 * would have gone back to "1 invoice to pay" with no word had the Zelle not been found.
 */
export const invoicesOnTheirWay = (count: number, total: string) =>
  count === 1
    ? `1 invoice of ${total} has a payment on its way. It shows as paid once the office has the payment.`
    : `${count} invoices, ${total} in all, have payments on their way. Each shows as paid once the office has its payment.`;

/** ARTBOARD (Main): "Rosa, arriving between 09:00 and 10:00". */
export const arriving = (cleaner: string, start: string, end: string) =>
  `${cleaner}, arriving between ${start} and ${end}`;

/**
 * NEW (the fifth client-journey walk, 2026-10-20): on Home's card for the last clean, where the
 * cleaner left no note, her first name alone, "Cleaned by Rosa", in place of "From Rosa", which
 * stood under nothing; and on Messages' "Your home is ready" card the same (the walk's leftovers,
 * 2026-10-20). Since home #18 (ops #98 phase 2), under a note too, in place of the artboard's
 * "From Rosa": the note is now the office's words for her, never the cleaner's own, and "From" her
 * would put them in her mouth. The first name only, as ops sends it.
 */
export const cleanedBy = (cleaner: string) => `Cleaned by ${cleaner}`;

/**
 * SEAM (ops's `docs/seams/home.md` section 14, ops #98 phase 3, business #47): the business's own
 * opening of the office's line for damage that was already there before the clean, in the language
 * the household reads, which such a line opens with and so carries as its own label.
 */
export const ALREADY_THERE = ['Already there:', 'Ya estaba:'] as const;

/**
 * ARTBOARD (States-HomeB, "10. Your home is ready, on Messages"): "Noticed: a dripping tap in the
 * guest bath.", a thing noticed on a clean, on Messages' "Your home is ready" card and Home's last
 * clean (home #19). The words are the office's line for her, as ops sent them (ops #98 phase 3),
 * never quoted and never credited to the cleaner. A line that opens with the business's own words
 * for damage already there (`ALREADY_THERE`), spaces before it aside, is drawn as it is, never
 * "Noticed: " before it: the opening decides, not the kind ops sent with it.
 */
export const noticedLine = (summary: string) =>
  ALREADY_THERE.some((opening) => summary.trimStart().startsWith(opening))
    ? summary
    : `Noticed: ${summary}`;

/**
 * ARTBOARD (Main): "4 bedrooms, 3 bathrooms", the rooms the office wrote down, each only when it
 * did, and one room as one room. Null when neither was written down.
 */
export function rooms(bedrooms?: number, bathrooms?: number): string | null {
  const parts: string[] = [];
  if (bedrooms !== undefined) parts.push(`${bedrooms} ${bedrooms === 1 ? 'bedroom' : 'bedrooms'}`);
  if (bathrooms !== undefined) {
    parts.push(`${bathrooms} ${bathrooms === 1 ? 'bathroom' : 'bathrooms'}`);
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

/** SEAM (section 3, `visit.status`): what a visit says when ops's status is one no artboard draws. */
export const VISIT = {
  /** `in_progress` on today's visit, with no time on it. */
  underWay: "Today's clean is under way.",
  /**
   * NEW: `in_progress` on a visit whose day is not today, with no time on it. The month draws the
   * days gone by, and ops may leave a clean under way past its day when its done push is late, so
   * the line says ops's state without naming a day, where "Today's" would be false.
   */
  underWayNotToday: 'This clean is under way.',
  /** `no_access`, which offers a message and nothing else. */
  couldNotGetIn: 'We could not get in, and the office will be in touch.',
  /**
   * NEW: `cancelled`, the line under the day Schedule crosses through, so the state is said in words
   * and never by the strike alone, which a screen reader does not reliably read out. It says nothing
   * of who cancelled, why, or what follows, since ops's status carries none of it.
   */
  cancelled: 'This visit is cancelled.',
  /**
   * SHIPPED (Main l.48, Messages l.40: the card of a visit done), for ops's `done` on a Schedule
   * card, with no window and no time on it.
   */
  done: HOME.ready,
  /**
   * NEW: ops's `done` on a visit whose day is before today, on the month's cards, which draw a year
   * of days gone by: "Your home is ready" is said of today, and a clean done weeks ago is said in
   * the past, in the key's own word. No window and no time. Raised by the final browser check of
   * 2026-10-17.
   */
  wasDone: 'This clean was done.',
  /**
   * NEW: ops's `no_access` on a visit whose day is before today: what happened, without the promise
   * that the office will be in touch, which the day it happened made and which a week later reads
   * as still to come. Raised by the final browser check of 2026-10-17.
   */
  couldNotGetInThen: 'We could not get in.',
  /**
   * NEW: ops's `cancelled` on a visit whose day is before today, in the past, under the day the
   * month names. Nothing of who cancelled, why, or what follows, as `cancelled` says nothing either.
   */
  wasCancelled: 'This visit was cancelled.',
} as const;

/**
 * NEW: a visit whose day is before today that ops still has as planned or confirmed, on the month's
 * cards: "Rosa was due between 09:00 and 10:00.", the artboard's line in the past. It says only what
 * was planned, and never that the clean was done or missed, since ops has not yet pushed what came
 * of it (binding rule 1).
 */
export const wasDue = (cleaner: string, start: string, end: string) =>
  `${cleaner} was due between ${start} and ${end}.`;

/**
 * FOR THE OWNER: while a first fill is still coming down, a place says so once and draws what it
 * has. Before anything at all has come, Home is the holding screen, whose words are already shipped.
 */
export const REST_ON_ITS_WAY = 'The rest is on its way.';

/**
 * NEW (provisional P106, ops #81): the holding screen, while she is signed in and home holds
 * nothing of her household yet. Raised by the owner's first sign-in to production on 2026-10-19,
 * who met "Your home is being set up" with nothing to say anything was happening, and asked for a
 * spinner, or a word on how long it takes. What it says of the time is ops's own account of a first
 * fill (its contract, section 3, "The first fill"): the household among the first rows, a small
 * household within minutes, one with a long history over hours. It promises no time, says nothing
 * of the cleaner, and keeps the way to call under it.
 */
export const HOLDING = {
  /** NEW: in the page's status region, beside the ring that turns, or stands still. */
  bringingOver:
    "We are bringing over your home's details. This usually takes a few minutes, and longer for a home with a long history.",
  /**
   * NEW: in place of `UPDATES_BY_ITSELF` once the page has read itself again for an hour in view
   * with nothing come, and stopped, as it does so it never reads for ever (`lib/readAgain.ts`).
   */
  stopped: 'This is taking longer than usual, so this page has stopped updating by itself.',
  /** NEW: the button under `stopped`, which reads once more and starts updating again. */
  checkAgain: 'Check again',
} as const;

/**
 * AN INVITATION THAT WAITS FOR HER CODE (found on staging, 2026-11-05; `docs/seam.md` P118). Ops
 * invites her address or number to another household while she is signed in, and only a code opens
 * an invitation (CLAUDE.md binding rule 3), so Home says one waits, above its content, the holding
 * screen and the page that says to call too, and offers the fresh code to the address or number she
 * signed in with (`components/InvitationWaits.tsx`). Every line here is NEW, for the owner to read
 * before clients are invited. None names the household, which home does not show before a code
 * opens it, and none says the office has granted anything (binding rule 1).
 */
export const INVITATION_WAITS = {
  /**
   * NEW: the notice, and why the code step asks, where she signed in by her address. "Something",
   * never "a new invitation" and never a household: the count is every unbound door on her contact,
   * so it is also her own household after ops changes a contact on it, which unbinds the row (the
   * review of 531f9d2).
   */
  emailed: 'Something is waiting for you here. To open it, we email you a fresh code.',
  /** NEW: the same, where she signed in by her mobile number and the code is texted (home #11). */
  texted: 'Something is waiting for you here. To open it, we text you a fresh code.',
  /** NEW: the notice's button, which draws the code step and asks for the code. */
  emailMe: 'Email me a code',
  /** NEW: the same, where the code is texted. */
  textMe: 'Text me a code',
  /** NEW: the code step's button, which opens the invitation once the code works. */
  submit: 'Open it',
  /**
   * NEW: no code can be asked for there: sign-in is closed, or she signed in by her number where no
   * code can be texted. The phone follows it, and then a full stop.
   */
  cannotBeforePhone: 'This cannot be opened here right now. Please call',
  /**
   * NEW: the code worked, so every invitation for the address or number is open, and the page reads
   * itself again. It says what home did, and nothing of what the office will do.
   */
  opened: 'Your code worked, and it is open now.',
} as const;

/**
 * NEW (provisional P106): after a line that says something is still coming, while the page reads
 * itself again (`lib/readAgain.ts`): on the holding screen, and on Home during a first fill.
 */
export const UPDATES_BY_ITSELF = 'This page updates by itself.';

/**
 * NEW (provisional P106, ops #81): on Home, under the household, while more of the first fill may
 * still be coming down (`firstFill`: until ops's household push says the fill is done, for a day
 * after the household first came at most, or six hours where the push says nothing). It says only
 * that more may come, never that anything is missing, since home cannot know what ops has still to
 * send. `UPDATES_BY_ITSELF` follows it while Home reads itself again.
 */
export const MAY_STILL_ARRIVE = 'More of your details may still be arriving.';

/** ARTBOARD (Schedule): the place, and the skip or move sheet drawn over it. */
export const SCHEDULE = {
  eyebrow: 'Schedule',
  skipOrMove: 'Skip or move',
  skip: 'Skip this visit',
  move: 'Ask to move it',
  whichDays: 'Which days would suit you instead?',
  send: 'Send the request',
  nothingChanges: 'Nothing changes until we confirm. Our answer comes to Messages.',
} as const;

/**
 * NEW: Schedule's month view, which no artboard draws, asked for on the owner's walk of 2026-10-16:
 * the switch at the top of the page between the artboard's cards and the month, the arrows either
 * side of the month's name, a chosen day with no visits, and the line under the grid before a day
 * is chosen, or for a month that holds none. The month's name is the calendar's, "October 2026"
 * (`monthInWords`, `lib/month.ts`).
 */
export const SCHEDULE_VIEW = {
  /** NEW: the switch's name, read before its two buttons. */
  showAs: 'Show visits as',
  /** NEW: the artboard's cards, one under the other. */
  list: 'List',
  /** NEW: the month, its days in weeks. */
  month: 'Month',
  /** NEW: the arrows' names, each drawn as a chevron alone. */
  previous: 'Previous month',
  next: 'Next month',
  /** NEW: under a chosen day that has no visit on it. */
  noVisitsThisDay: 'No visits this day.',
  /** NEW: under the grid, for a month that holds no visit. */
  noVisitsThisMonth: 'No visits this month.',
  /** NEW: under the grid, before a day of the month is chosen. */
  chooseADay: 'Choose a day to see its visits.',
} as const;

/**
 * NEW: each mark on the month in plain words, in the key under it, and said in each day's name.
 * "Planned" is said of ops's `planned` and `confirmed` alike, never "coming up", since a visit
 * before today that ops has not yet pushed done is still planned, and is not coming up. The others
 * are the words the List already says of each state, made short: under way, done, cancelled (with
 * a skip ops accepted, which it pushes as cancelled) and could not get in.
 */
export const MARK_WORDS: Readonly<Record<Mark, string>> = {
  planned: 'Planned',
  underWay: 'Under way',
  done: 'Done',
  cancelled: 'Skipped or cancelled',
  noAccess: 'Could not get in',
};

/**
 * NEW: a day on the month as a screen reader says it, its day in words (`dayInWords`), "today" on
 * today, then its visits: "Tuesday 27 October, 1 visit, planned"; "Tuesday 20 October, today, 2
 * visits, 1 planned, 1 done", each state once, in the key's order; and "Wednesday 21 October, no
 * visits". A visit whose state this build does not know is counted and given no state.
 */
export function dayOnTheMonth(
  day: string,
  marks: readonly (Mark | null)[],
  today: boolean,
): string {
  const parts = [day];
  if (today) parts.push('today');
  if (marks.length === 0) return [...parts, 'no visits'].join(', ');
  parts.push(marks.length === 1 ? '1 visit' : `${marks.length} visits`);
  const counted = MARKS.flatMap((mark) => {
    const count = marks.filter((each) => each === mark).length;
    return count === 0 ? [] : [{ words: MARK_WORDS[mark].toLowerCase(), count }];
  });
  const [only] = counted;
  if (only !== undefined && counted.length === 1 && only.count === marks.length) {
    parts.push(only.words);
  } else {
    for (const { words, count } of counted) parts.push(`${count} ${words}`);
  }
  return parts.join(', ');
}

/**
 * How often a home's standing schedule comes round, by ops's `cadence` (the seam, l.224 at b727ec1),
 * the first half of Schedule's heading. `one_time` is no standing schedule, so it has no line, nor
 * does a word ops adds later.
 */
const EVERY: ReadonlyMap<string, string> = new Map([
  /** NEW: "Every week, on Tuesdays". */
  ['weekly', 'Every week'],
  /** ARTBOARD (Schedule): "Every two weeks, on Tuesdays". */
  ['every_2_weeks', 'Every two weeks'],
  /** NEW: "Every four weeks, on Tuesdays". */
  ['every_4_weeks', 'Every four weeks'],
]);

/** ARTBOARD (Schedule): the day it comes on, by ops's `weekday`, as "on Tuesdays" writes it. */
const ON: ReadonlyMap<string, string> = new Map([
  ['monday', 'Mondays'],
  ['tuesday', 'Tuesdays'],
  ['wednesday', 'Wednesdays'],
  ['thursday', 'Thursdays'],
  ['friday', 'Fridays'],
  ['saturday', 'Saturdays'],
  ['sunday', 'Sundays'],
]);

/**
 * Schedule's heading for one home's standing schedule: ARTBOARD (Schedule) "Every two weeks, on
 * Tuesdays", on whichever weekday ops names; NEW "Every week, on Tuesdays" and "Every four weeks, on
 * Tuesdays" in the same form. Null for `one_time`, for a cadence or a weekday this build has no word
 * for, and when ops sent either as null, and the screen draws nothing for it.
 */
export function cadenceLine(cadence?: string, weekday?: string): string | null {
  const every = cadence === undefined ? undefined : EVERY.get(cadence);
  const on = weekday === undefined ? undefined : ON.get(weekday);
  return every === undefined || on === undefined ? null : `${every}, on ${on}`;
}

/**
 * How often an agreement comes round, on Your agreement (ops's section 12.1, `cadence` and
 * `weekday`, the same four cadences on every service): Schedule's heading where both have words
 * (`cadenceLine`); its first half alone, "Every two weeks", where ops gives no weekday or one this
 * build has no word for, which is still true; and WEBSITE "One time" (`AGREEMENT.oneTime`) for
 * `one_time`. Null for a cadence this build has no word for, and the screen says nothing of it.
 */
export function howOften(cadence?: string, weekday?: string): string | null {
  if (cadence === 'one_time') return AGREEMENT.oneTime;
  return (
    cadenceLine(cadence, weekday) ?? (cadence === undefined ? null : (EVERY.get(cadence) ?? null))
  );
}

/**
 * THE HOME EDIT SHEET, which no artboard draws, so it is built from drawn parts and is on the list
 * the owner reads before clients are invited: "Change something" on Home opens Schedule's sheet,
 * with Feedback's field for each of Home's own rows (`HOME.leaveAlone`, `HOME.pets` and
 * `HOME.gettingIn`), Home's codes line (`HOME.codes`) under them, and Schedule's "Send the request"
 * (`SCHEDULE.send`).
 */
export const EDIT_HOME = {
  /** ARTBOARD (Main): the button under "What we have written down", and the sheet's name. */
  change: 'Change something',
  /**
   * ARTBOARD (Schedule): the first sentence of the line under the sheet's button, alone. The second,
   * "Our answer comes to Messages.", is left out: the office's answer to an edit is shown on the
   * home it is about, and nothing ops sends says it comes to Messages as well.
   */
  nothingChanges: 'Nothing changes until we confirm.',
  /**
   * NEW (the owner's rule of 2026-10-20, never silent): said in the sheet, with the focus, when the
   * request door answers 404 for the home, which it does alike for a home gone and one that is not
   * hers to ask about (CLAUDE.md binding rule 2), so the words are the same either way; the sheet
   * stays open on it, even where Home read again no longer holds the home. The phone follows it,
   * and then a full stop.
   */
  notHereBeforePhone: 'This home can no longer be changed here. Please call',
} as const;

/**
 * A price in dollars as the artboards write one: "$35", and "$35.50" only when there are cents, on
 * Schedule's add-ons and on every amount Payments says. A credit is written as less, "-$21.50".
 */
export function dollars(cents: number): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

/**
 * An amount as ops's `formatCents` writes it, "$212.00", always with its cents and a comma between
 * thousands: what the contract fills `<the amount>` and `<amount>` with in the lines it prints (ops's
 * `docs/seams/home.md` at 0b51e49, section 5), and so the form of every amount in a sentence of
 * section 5 on an invoice's own page. An amount the artboards draw stays in their form (`dollars`).
 */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * ARTBOARD (Schedule): "Added: inside fridge, $35", a line from the card ops agreed on a visit, in
 * the card's own words, with its price only when the Worker gives one, which it does to a client
 * alone; without it the line ends at the label. NEW since provisional P112: how many where more than
 * one, as Your agreement says a standing add-on (`standingAddon`), "Added: Laundry, 2 at $25.50
 * each", and for a member, shown no money, "Added: Laundry, 2". Never a figure of home's own: the
 * price for one is the one ops pushed with the add-on.
 */
export function added(label: string, quantity?: number, unitCents?: number): string {
  return `Added: ${standingAddon(label, quantity, unitCents)}`;
}

/**
 * NEW (provisional P112): Schedule's add-on sheet, which no artboard draws, opened from "Add to this
 * visit" on a planned or confirmed visit's card, beside "Skip or move", and drawn as the skip or
 * move sheet is: the card's add-on lines to choose from, how many, the artboard's "Send the request"
 * (`SCHEDULE.send`), and under it that nothing changes until we confirm and that the price is the
 * card's. It asks; the office decides (ops's decision 13: it answers an add-on by phone), and the
 * visit shows an add-on only once ops pushes it agreed (`added`).
 */
export const ADD_TO_VISIT = {
  /** NEW: the card's button, under "Skip or move", which opens the sheet. */
  open: 'Add to this visit',
  /** NEW: the question over the card's add-on lines. */
  which: 'What would you like added?',
  /** NEW: the name of the choice of how many, 1 to 20 (ops's `quantity`). */
  howMany: 'How many?',
  /**
   * ARTBOARD (Schedule): the first sentence of the line under the sheet's button, alone, as Home's
   * edit sheet says it (`EDIT_HOME.nothingChanges`). The second, "Our answer comes to Messages.", is
   * left out: the office answers an add-on by phone (ops's decision 13), and its words are shown on
   * the visit.
   */
  nothingChanges: 'Nothing changes until we confirm.',
  /**
   * NEW: whose the price is, after it: the card's, never home's. Said only where no line offered is
   * a starting price, since a starting price is never the price (`startingPrice`).
   */
  priceIsTheCards: 'The price is the one on our published rate card.',
  /**
   * NEW (ops contract 12.1, `card.from`: "a starting price, never shown as exact"): in place of
   * `priceIsTheCards` wherever any line offered is a starting price ("Interior windows, from $60
   * each"), so a "from" figure is never read as the price promised. It says what a "from" price is,
   * and that the office settles the price before anything is added, which ops does when the office
   * agrees (decision 13).
   */
  startingPrice:
    'A price that says from is a starting price, and the office confirms it before anything is added.',
  /**
   * NEW: said in the sheet when the request door refuses the line she chose as no longer on the
   * card in force (`422 invalid`), and Schedule is read again with the card as it now stands. The
   * phone follows it, and then a full stop.
   */
  cardChangedBeforePhone:
    'Our rate card has changed since this page was opened. Please choose again, or call',
} as const;

/**
 * NEW (provisional P112): a card's add-on line as the add-on sheet offers it, in the card's own
 * words with the card's price for one as the artboards write a price (`dollars`): "Inside oven, $35
 * each"; and where the card shows the price as a starting price, never as exact, "Interior windows,
 * from $60 each".
 */
export const addonChoice = (label: string, unitCents: number, from: boolean): string =>
  `${label}, ${from ? 'from ' : ''}${dollars(unitCents)} each`;

/**
 * NEW (provisional P112): what an add-on asked to add, in the card's words, with how many where
 * more than one: "Laundry, 2".
 */
export const addonAsked = (label: string, quantity: number | undefined): string =>
  quantity !== undefined && quantity > 1 ? `${label}, ${quantity}` : label;

/**
 * NEW (provisional P112): her own add-on, which the office has and has not decided, as the
 * artboard's callout says a move: "You asked to add Laundry, 2. Waiting for us to confirm.", or
 * with no words for what, where the line is no longer on the card, "You asked to add to this
 * visit. Waiting for us to confirm."
 */
export const youAskedForAddon = (what: string | undefined): string =>
  `You asked to add ${what ?? 'to this visit'}. Waiting for us to confirm.`;

/**
 * NEW (provisional P112): an add-on somebody else on the household asked for, by the name the office
 * keeps, as `theyAskedToMove` says a move.
 */
export const theyAskedForAddon = (name: string, what: string | undefined): string =>
  `${name} asked to add ${what ?? 'to this visit'}. Waiting for us to confirm.`;

/**
 * ARTBOARD (Payments): the place, from the artboard's first and third frames. Its other lines are
 * not drawn, each for a reason:
 *
 * - "On Stripe's page, by card or from your bank account. The price is the same either way." The
 *   ways to pay are offered on the invoice's own page, Zelle first (the seam, section 5).
 * - "Your payment of $212 is recorded." It would say paid before ops does: an invoice reads paid
 *   only once ops pushes it so (the seam, l.682-687 at b727ec1).
 * - "The receipt goes to <address>." No view carries an address, and home is told of a receipt
 *   only when a saved method was charged (`chargedTo`).
 * - "From your bank account, 2 October" and "By card, today" are drawn since round five's slice B3,
 *   once ops says how and when each payment came (its section 11.2, `payments`): `paymentSaid`.
 */
export const PAYMENTS = {
  eyebrow: 'Payments',
  toPay: 'To pay',
  paid: 'Paid',
  /**
   * NEW: the heading of the section between To pay and Paid, for an invoice she has just paid on
   * Stripe's page, in any tab of her browser (P35), and one ops says a bank payment on is clearing
   * (`pay.bank_payment_clearing`), each drawn there with `PAYING.onItsWay` or `PAYMENTS.clearing`
   * and never with Pay, until ops's push says how it stands. It says what she did, or what ops
   * says, and never that anything is paid (binding rule 1). Raised by the owner's walk of
   * 2026-10-16, replayed: back from paying, Payments still drew the amount under To pay in its
   * largest type, while Home and the nav said nothing was owed.
   */
  onItsWay: 'On its way',
  /** ARTBOARD (Payments, third frame): under To pay, with nothing owed. */
  nothingNow: 'Nothing right now.',
  /** ARTBOARD (Payments): only once ops says it emailed one, for a charge to the saved method. */
  receiptEmailed: 'Receipt emailed.',
  /**
   * SEAM (section 5, l.741-742 at b727ec1): while a bank payment on the invoice is on its way, in
   * place of Pay, word for word.
   */
  clearing:
    'A bank payment for this invoice is on its way. There is nothing to pay here while it clears.',
  /**
   * NEW: an owed invoice with nothing to pay from here now, because a bank payment on it clears, the
   * saved method pays it, or ops offers no way to pay it from home, still leads to its own page,
   * where she says how the clean went.
   */
  see: 'See this invoice',
  /**
   * NEW: after Stripe's back arrow (`not-paid`), an outlined button under the return page's callout
   * to the invoice the tab left to pay, on the way she left it from, drawn only when the tab noted
   * one and only where Payments cannot be read, so nothing is drawn below the callout: elsewhere
   * the Pay on that invoice's card is the way back, as home's line in the callout says
   * (`HOME_HOW_TO_PAY`, section 11.4; slice M2 of round four). Raised by the owner's walk of
   * 2026-10-16.
   */
  backToInvoice: 'Back to your invoice',
} as const;

/**
 * HOW AND WHEN SHE PAID (ops's section 11.2, `payments`, round five's slice B3, P62), on an invoice's
 * own page and on Payments' rows, in the Payments artboard's form for a payment ("From your bank
 * account, 2 October. Receipt emailed." and "By card, today. Receipt emailed."), ops's `method`
 * worded by home as the contract asks ("ops's own words ... home words them"):
 *
 * - ARTBOARD: "By card" and "From your bank account".
 * - NEW: "By Zelle", "By check", "In cash", and "Another way" for `other`, and for a method ops adds
 *   later, which this build has no word for and still says the money came.
 */
export const PAYMENT_METHODS: Readonly<Record<string, string>> = {
  card: 'By card',
  bank_transfer: 'From your bank account',
  zelle: 'By Zelle',
  check: 'By check',
  cash: 'In cash',
  other: 'Another way',
};

/**
 * NEW (round five's slice B3): a refund ops recorded on the invoice, "Refunded, 30 October.", and a
 * correction the office made to what was paid, "The office's adjustment, 30 October.", each said as
 * what ops recorded and never as anything home worked out.
 */
export const PAYMENT_KINDS = {
  refund: 'Refunded',
  correction: "The office's adjustment",
} as const;

/**
 * What a payment line is made from: ops's kind, method, day and amount applied to the invoice, and
 * its receipt emailed.
 */
export interface PaymentToSay {
  readonly kind?: string;
  readonly method?: string;
  readonly receivedOn?: string;
  readonly amountCents?: number;
  readonly receiptEmailed: boolean;
}

/**
 * NEW (round five's slice B3): a payment ops recorded on an invoice, in one line: how it came and the
 * day the money arrived, "By Zelle, 28 October.", "today" in place of the day it is today, as the
 * artboard writes it; then " Receipt emailed." (`PAYMENTS.receiptEmailed`) only where ops says its
 * receipt email for that payment went. A refund says it was refunded, and a correction that it was
 * the office's adjustment, each with its day and never a receipt. Without a day, the line ends at how
 * it came. Null for a kind ops adds later, or none, which says nothing rather than something untrue.
 */
export function paymentSaid(payment: PaymentToSay, today: string): string | null {
  const how =
    payment.kind === 'payment'
      ? (PAYMENT_METHODS[payment.method ?? ''] ?? PAYMENT_METHODS.other!)
      : payment.kind === 'refund' || payment.kind === 'correction'
        ? PAYMENT_KINDS[payment.kind]
        : null;
  if (how === null) return null;
  const { receivedOn } = payment;
  const day =
    receivedOn === undefined ? null : receivedOn === today ? 'today' : dateInWords(receivedOn);
  const said = day === null ? `${how}.` : `${how}, ${day}.`;
  return payment.kind === 'payment' && payment.receiptEmailed
    ? `${said} ${PAYMENTS.receiptEmailed}`
    : said;
}

/**
 * NEW (round six, slice S1): how each of ops's methods is said after a payment's amount on Payments'
 * rows, "$50 by Zelle", the words of `PAYMENT_METHODS` that follow an amount; "paid another way"
 * for `other` and for a method ops adds later.
 */
const AFTER_AN_AMOUNT: Readonly<Record<string, string>> = {
  card: 'by card',
  bank_transfer: 'from your bank account',
  zelle: 'by Zelle',
  check: 'by check',
  cash: 'in cash',
  other: 'paid another way',
};

/**
 * NEW (round six, slice S1): a payment ops recorded, as a line on Payments' rows, led by what it came
 * to, "$50 by Zelle, 17 October.", so a part payment made before never reads as the whole beside the
 * row's own amount (the replay of 2026-10-18 read "By Zelle, 17 October." beside the $175 still owed
 * as if her Zelle had come). Then as `paymentSaid` says it: the day, "today" for today, and " Receipt
 * emailed." where ops says so; a refund as what went back, "$40 refunded, 6 October."; and a
 * correction by its amount as ops gives it, "The office's adjustment of $10, 6 October.". The amount
 * is ops's `amount_cents` applied to the invoice, written as `dollars` writes it, nothing worked
 * out. Without an amount, `paymentSaid`'s line; null for a kind this build has no words for.
 */
export function paymentWithAmount(payment: PaymentToSay, today: string): string | null {
  const { amountCents, receivedOn } = payment;
  if (amountCents === undefined) return paymentSaid(payment, today);
  const how =
    payment.kind === 'payment'
      ? `${dollars(amountCents)} ${AFTER_AN_AMOUNT[payment.method ?? ''] ?? AFTER_AN_AMOUNT.other!}`
      : payment.kind === 'refund'
        ? `${dollars(Math.abs(amountCents))} refunded`
        : payment.kind === 'correction'
          ? `${PAYMENT_KINDS.correction} of ${dollars(amountCents)}`
          : null;
  if (how === null) return null;
  const day =
    receivedOn === undefined ? null : receivedOn === today ? 'today' : dateInWords(receivedOn);
  const said = day === null ? `${how}.` : `${how}, ${day}.`;
  return payment.kind === 'payment' && payment.receiptEmailed
    ? `${said} ${PAYMENTS.receiptEmailed}`
    : said;
}

/**
 * SEAM (ops's `docs/seams/home.md` section 11.2, "Built, and what the building decided", item 12, at
 * `d8940353`): AN INVOICE'S DOCUMENTS, the words her invoice link's page draws
 * (`DOCUMENT_PAGE_WORDS` and `DOCUMENT_BEING_MADE`), which home's buttons say "word for word":
 * the part's heading, the invoice's own PDF, what the button says while it gets one, the line past
 * the poll before the office's number, which the page draws as a link and then a full stop, and the
 * line said while ops is still making a PDF, in place of any button. A receipt's button is
 * `receiptPdf`, and a cancellation's `cancellationPdf`. `contract-words.test.ts` holds each.
 */
export const DOCUMENTS = {
  heading: 'Documents',
  invoice: 'Download the invoice (PDF)',
  getting: 'Getting your PDF',
  notGotBeforePhone: 'We could not get the PDF just now. Try again in a minute, or call',
  beingMade: 'Your PDF is being made. It will be here in a few minutes.',
} as const;

/** SEAM (section 11.2, item 12): "Receipt FK-R7 (PDF)", a receipt's button by its own number. */
export const receiptPdf = (number: string) => `Receipt ${number} (PDF)`;

/**
 * NEW (round six, slice S1): a PDF by its name, said in its own place while ops says it is being
 * made, above ops's `DOCUMENTS.beingMade` word for word, and under ops's `DOCUMENTS.getting` on its
 * button while it is got, so neither line is ever about a PDF it does not name (the replay of
 * 2026-10-18 read one unnamed "Your PDF is being made." under the invoice's own button, which was
 * about the receipt). The names are the buttons' own, without "(PDF)": "Receipt FK-R7", "Invoice
 * FK-41, cancelled", and the invoice's own "Invoice FK-42" (`invoiceNumbered`), or "The invoice"
 * where ops gives it no number. Null for a kind this build does not offer, or one with no number.
 */
export function documentName(kind: string | undefined, number: string | undefined): string | null {
  switch (kind) {
    case 'invoice':
      return number === undefined ? 'The invoice' : invoiceNumbered(number);
    case 'receipt':
      return number === undefined ? null : `Receipt ${number}`;
    case 'cancellation':
      return number === undefined ? null : `Invoice ${number}, cancelled`;
    default:
      return null;
  }
}

/**
 * SEAM (section 11.2, item 12, pinned for home's door, which offers a cancellation where her link's
 * page never does): "Invoice FK-41, cancelled (PDF)", the invoice's number, which a cancellation
 * carries.
 */
export const cancellationPdf = (number: string) => `Invoice ${number}, cancelled (PDF)`;

/**
 * NEW (slice M2 of round four, ops's section 11.2): "Invoice FK-42", an invoice by the number ops
 * gives it at its first send, under its day on Payments' cards and rows and on its own page, so two
 * invoices of one day differ at a glance. Nothing for one sent before numbering began.
 */
export const invoiceNumbered = (number: string) => `Invoice ${number}`;

/** ARTBOARD (Payments): "Pay $212", what is still to pay on the invoice, as ops reads it. */
export const payAmount = (cents: number) => `Pay ${dollars(cents)}`;

/** ARTBOARD (Payments): "Visit of Tuesday 13 October", an invoice owed, by its clean's day. */
export const visitOf = (day: string) => `Visit of ${day}`;

/**
 * NEW: "Invoice of Wednesday 14 October", an invoice by the day it was issued, when ops could not
 * read its visit's day and sends none.
 */
export const invoiceOf = (day: string) => `Invoice of ${day}`;

/** Ops's names for the kinds of saved method (the seam, section 3, `method_type`). */
const CARD_METHOD = 'card';
const BANK_METHOD = 'us_bank_account';
const LINK_METHOD = 'link';

/** SEAM: the method in words and in a word, for a type ops does not know (`method_type` null). */
const SAVED_PAYMENT_METHOD = 'saved payment method';

/** A card or a bank account, whose words ops makes from Stripe's label. */
const namedByLabel = (methodType: string | undefined) =>
  methodType === CARD_METHOD || methodType === BANK_METHOD;

/**
 * SEAM (ops's `docs/seams/home.md` at 0b51e49, section 5, `savedMethodNoun`, Q3): the saved method
 * in a word, where the seam's lines say "card": "card", "bank account", "Link account", and "saved
 * payment method" for a type ops does not know. A type this build does not know reads as one ops
 * does not know.
 */
export function savedMethodNoun(methodType: string | undefined): string {
  if (methodType === CARD_METHOD) return 'card';
  if (methodType === BANK_METHOD) return 'bank account';
  if (methodType === LINK_METHOD) return 'Link account';
  return SAVED_PAYMENT_METHOD;
}

/** A label that ends in one or more spaces and then four digits, with something before them. */
const LAST_FOUR = /^(.+?) +([0-9]{4})$/;

/** A bank account's words that already end in the word account, in any case. */
const ENDS_IN_ACCOUNT = /\baccount$/i;

/**
 * SEAM (section 5 at 0b51e49, `savedMethodWords`, Q3): the saved method named in words wherever a
 * method is named, from Stripe's `display_label` and ops's `method_type`. Link is always "Link
 * account", and a type ops does not know always "saved payment method". For a card or a bank account
 * the label is trimmed; where it ends in one or more spaces and four digits with something before
 * them, the digits are the last four and the rest is what stands before the spaces, else the rest
 * is the whole label. A bank account's rest gains " account" unless it already ends in the word.
 * The words are "<the rest> ending <the last four>", or the rest alone: "Visa ending 4242",
 * "STRIPE TEST BANK checking account ending 6789", "Chase checking account".
 *
 * Home's own reading, for what the contract does not cover: a card or a bank account ops gives no
 * words for is named by its noun, "card" or "bank account", rather than by nothing.
 */
export function savedMethodWords(
  label: string | undefined,
  methodType: string | undefined,
): string {
  if (!namedByLabel(methodType)) return savedMethodNoun(methodType);
  const trimmed = label?.trim() ?? '';
  if (trimmed === '') return savedMethodNoun(methodType);
  const match = LAST_FOUR.exec(trimmed);
  const rest = match === null ? trimmed : match[1]!;
  const named =
    methodType === BANK_METHOD && !ENDS_IN_ACCOUNT.test(rest) ? `${rest} account` : rest;
  return match === null ? named : `${named} ending ${match[2]!}`;
}

/**
 * The saved method on its own, as Payments and its Stop sheet name it: "Visa ending 4242" where ops
 * gives Stripe's words for a card or a bank account (`savedMethodWords`), else "your" before them,
 * "your Link account" or "your saved payment method", so a line never reads as a name it is not.
 */
function methodNamed(label: string | undefined, methodType: string | undefined): string {
  const words = savedMethodWords(label, methodType);
  return namedByLabel(methodType) && label !== undefined && label.trim() !== ''
    ? words
    : `your ${words}`;
}

/**
 * SEAM (section 3, `household.autopay`): "Charges automatically: Visa ending 4242, since 12 Oct",
 * the method in ops's words for it (section 5, `savedMethodWords`, which names a method wherever one
 * is named, the household's included), and the day she agreed with no year. Without the day it ends
 * at the method.
 */
export function chargesAutomatically(
  label: string | undefined,
  methodType: string | undefined,
  since: string | null,
): string {
  const method = `Charges automatically: ${methodNamed(label, methodType)}`;
  return since === null ? method : `${method}, since ${since}`;
}

/**
 * STOP, beside the household's saved method on Payments, which asks ops to stop charging it
 * (`billing.autopay_stop`) after a fresh code (the seam, l.197-199 and l.540 at b727ec1, and
 * CLAUDE.md binding rule 5). The button is the seam's; the sheet it opens is drawn on no artboard
 * (business issue 13), so it is built from Schedule's sheet and the sign-in screen's code step
 * (`FRESH_CODE`), with NEW words for what neither says. Each says only what ops has said: a method
 * is stopped once ops's receipt or its push says so, and never because she pressed Stop.
 */
export const STOP = {
  /** SEAM: the button beside the saved method. */
  stop: 'Stop',
  /** NEW: the sheet's button that stops it, and the code step's once a code is typed. */
  yes: 'Yes, stop it',
  /** NEW: closes the sheet, asking nothing. */
  keep: 'Keep it',
  /** NEW: why the sheet asks for a code, said first on its code step. */
  freshCode: 'To stop it, we email you a fresh code first.',
  /**
   * NEW: ops's receipt says it stopped the method (`stopped: true`), or its push says nothing is
   * saved after the Stop she sent from this page, in place of the method.
   */
  stopped: 'Stopped. Nothing more is charged automatically.',
  /**
   * NEW: ops says nothing stands to stop (`409 nothing_saved`, the seam l.623, or a replay's
   * `stopped: false`), in place of the method.
   */
  nothingSaved: 'Nothing is saved to charge automatically.',
  /**
   * NEW: ops refused it because Stripe is off at ops (`409 payments_off`, l.618 and l.659-663), or
   * no fresh code can be asked for here: sign-in is closed. "Call" alone, as every line of home's
   * own says it. Said too, with the focus, when the request door answers 404 for the Stop, which it
   * does alike for a household gone and one not hers to ask about (CLAUDE.md binding rule 2), the
   * sheet staying open on it (the owner's rule of 2026-10-20, never silent). The phone follows it,
   * and then a full stop.
   */
  cannotBeforePhone: 'This cannot be stopped here right now. Please call',
  /**
   * NEW (slice M2 of round four, ops's section 11.5): ops refused her Stop as naming a method that
   * no longer stands (`409 method_changed`, final), said under the method the copy holds, in place
   * of Stop, until ops's push says which method stands. It says what ops said, that nothing was
   * stopped, and never which method stands now. The phone follows it, and then a full stop.
   */
  methodChangedBeforePhone:
    'Your saved payment method changed before your Stop reached us, so nothing was stopped. To stop the method you have now, please call',
} as const;

/**
 * NEW: the Stop sheet's question, "Stop charging Visa ending 4242 automatically?", the method in
 * ops's words for it (`savedMethodWords`).
 */
export const stopCharging = (label: string | undefined, methodType: string | undefined) =>
  `Stop charging ${methodNamed(label, methodType)} automatically?`;

/**
 * A FRESH CODE, where a door stands behind one (CLAUDE.md binding rule 5, `docs/identity.md` section
 * 4): emailed to the address she signed in with, and typed as she typed it to sign in. SHIPPED from
 * the sign-in screen's code step (`screens/SignIn.tsx`) wherever its words fit a person already
 * signed in, and NEW where they do not: she cannot use a different email here. Like sign-in, it
 * never says a code was sent, only that one may be on its way (binding rule 3).
 */
export const FRESH_CODE = {
  /** SHIPPED: the field's label. */
  code: 'Code',
  /** SHIPPED: asks for another code. */
  newCode: 'Send a new code',
  /** SHIPPED: once another code is asked for. */
  newCodeSent: 'If that is the email we have for you, a code is on its way.',
  /**
   * NEW (home #11): the same, where she signed in by her mobile number and the code is texted, as
   * the sign-in screen's texted code step says it.
   */
  newCodeTexted: 'If that is the mobile number we have for you, a code is on its way.',
  /** NEW: a code that did not work, in place of the sign-in screen's, which offers another email. */
  wrong: 'That code did not work. Type it again, or send a new code.',
  /**
   * NEW: a code that worked has stopped counting, fifteen minutes after it was typed, before what
   * it was typed for went through, so the door asks for another.
   */
  runOut: 'That code has run out. Please send a new code.',
} as const;

/** SHIPPED (`screens/SignIn.tsx`): "If k.morgan@example.com is the email we have for you, ...". */
export const codeOnItsWay = (email: string) =>
  `If ${email} is the email we have for you, a code is on its way.`;

/**
 * NEW (round four, slice M3, `docs/seam.md` P59): in place of `codeOnItsWay`, on a code step drawn
 * within ten minutes of the last code this tab asked for, which asks for none, since only the newest
 * code works and a new one would stop the one she may already have open (`lib/fresh.ts`). Like
 * `codeOnItsWay` it says one may be on its way, never that one was sent (binding rule 3), and
 * leaves "Send a new code" to her.
 */
export const codeAlreadyOnItsWay = (email: string) =>
  `If ${email} is the email we have for you, a code is already on its way. Use that one, or send a new code.`;

/**
 * NEW (home #11): `codeOnItsWay`, where she signed in by her mobile number and the fresh code is
 * texted to it, in the sign-in screen's texted words, the number as she reads one.
 */
export const codeOnItsWayByText = (number: string) =>
  `If ${number} is the mobile number we have for you, a code is on its way.`;

/** NEW (home #11): `codeAlreadyOnItsWay`, where the code is texted, as `codeOnItsWayByText`. */
export const codeAlreadyOnItsWayByText = (number: string) =>
  `If ${number} is the mobile number we have for you, a code is already on its way. Use that one, or send a new code.`;

/** How every line of why a fresh code is asked for ends: the code is emailed. */
const EMAILED_FIRST = 'we email you a fresh code first.';

/** NEW (home #11): how it ends where she signed in by her mobile number, and the code is texted. */
const TEXTED_FIRST = 'we text you a fresh code first.';

/**
 * Why a fresh code is asked for, where she signed in by her mobile number and the code is texted to
 * it (home #11): the line as it is, with "we text you" in place of "we email you", which every such
 * line ends with (`words.test.ts` holds each). The code step says it (`components/FreshCode.tsx`).
 */
export function freshWhyByText(why: string): string {
  return why.endsWith(EMAILED_FIRST)
    ? `${why.slice(0, why.length - EMAILED_FIRST.length)}${TEXTED_FIRST}`
    : why;
}

/**
 * NEW: a code asked for past the ceiling across every sign-in (`429 busy`, `CEILING` in
 * `api/src/lib/signInCode.ts`), which the Worker answers the same for every address before it looks
 * one up, so it says nothing of whether hers is on file. Said by the sign-in screen's two steps and
 * by the fresh-code step, the one line of the sign-in screen's kept here, because both say it. It
 * says that sign-in is busy, which the ceiling knows, and never that people are signing in, which
 * it does not: a script trying addresses fills it as well. Nothing was asked for, so she presses
 * again, with what she typed still there. The phone follows it, and then a full stop.
 */
export const SIGN_IN_BUSY_BEFORE_PHONE =
  'Sign-in is busy right now. Please try again in a minute, or call';

/**
 * NEW: a code asked for past the one limit left on asking (the owner's word of 2026-10-20, `429
 * too_many_codes`, `LIMITS.codesPerIdentifierHour` in `api/src/lib/signInCode.ts`): thirty codes
 * in an hour for one address or number. The Worker counts every ask for it, whether or not it is on
 * file, so the line says nothing of whether hers is, and names the address or number, never her.
 * Nothing was asked for, so it never says a code is on its way. Said by the sign-in screen's two
 * steps and by the fresh-code step; the phone follows it, and then a full stop.
 */
export const TOO_MANY_CODES_BEFORE_PHONE =
  'Too many codes were asked for this address or number in the last hour. Please try again later, or call';

/**
 * NEW (the owner's word of 2026-10-20: "User messages always gives info, never silent"): a code
 * typed while the guess lock holds (`429 codes_locked`, ten wrong tries in an hour on one network,
 * or past a hundred since the last success on every network), which the Worker refuses before it is
 * compared, the right code too. It says why, and until when, in Florida's time on the twelve-hour
 * clock the business writes its hours in (`twelveHourTime`), " tomorrow" after it where it falls
 * then, as the Worker says it, and "for a while" where it says no time this tab can read. The Worker
 * answers it the same whether or not the address or number is on file, so it says nothing of hers.
 * Said by the sign-in screen's code step and by the fresh-code step; the phone follows it, and then
 * a full stop.
 */
export function codesPausedBeforePhone(
  until: { readonly time: string; readonly tomorrow: boolean } | null,
): string {
  const time = until === null ? null : twelveHourTime(until.time);
  const when =
    time === null ? 'for a while' : `until ${time}${until?.tomorrow === true ? ' tomorrow' : ''}`;
  return `Too many wrong codes were tried, so for safety, sign-in codes are paused ${when}. You can call`;
}

/**
 * NEW (the same word): a code Microsoft or the phone system did not take, said in place of "on its
 * way" once what came of the send says so (`codeSendState`): the number asked not to get the
 * business's texts (`stopped`), too many codes went to it just now (`rate_limited`, the phone
 * system's thirty an hour), texts cannot reach it yet (`not_enabled`), or the code could not be sent
 * (`could_not_send`); by email, too many emails went out (`rate_limited`, Microsoft's own throttle),
 * email cannot reach the address (`not_enabled`, an address on a domain reserved for tests, which
 * home never emails, home issue 12), or it could not be sent, since Microsoft says nothing of an
 * address stopping emails. The owner accepted that a stranger could learn from it that the
 * address or number is a client's. `otherWay`
 * offers the other way in, her email for a text and her mobile number for an email, where the page
 * has one: the sign-in screen, and for an email only where texts can go. The fresh-code step offers
 * none, since a fresh code goes only where she signed in with. The phone follows it, and then a full
 * stop.
 */
export function codeNotSentBeforePhone(
  why: SendFailure,
  by: 'email' | 'text',
  otherWay: boolean,
): string {
  const instead =
    by === 'text' ? 'sign in with your email address, ' : 'sign in with your mobile number, ';
  const orInstead = otherWay ? instead : '';
  if (by === 'text' && why === 'stopped') {
    return `This number asked not to get our texts, so the code was not sent. Please ${orInstead}${otherWay ? 'or call' : 'call'}`;
  }
  if (by === 'text' && why === 'not_enabled') {
    return `Texts cannot reach this number yet, so the code was not sent. Please ${orInstead}${otherWay ? 'or call' : 'call'}`;
  }
  if (by === 'email' && why === 'not_enabled') {
    return `Email cannot reach this address, so the code was not sent. Please ${orInstead}${otherWay ? 'or call' : 'call'}`;
  }
  if (why === 'rate_limited') {
    const tooMany =
      by === 'text'
        ? 'Too many codes went to this number just now, so this one was not sent.'
        : 'Too many emails went out just now, so the code was not sent.';
    return `${tooMany} Please try again in a few minutes, ${orInstead}or call`;
  }
  return `We could not send the code just now. Please try again, ${orInstead}or call`;
}

/**
 * NEW (the fifth client-journey walk's review of 2026-10-20, findings 1 and 3, shots 016, 034 to
 * 036 and 043): the sign-in screen's code step once its code was not sent and
 * `codeNotSentBeforePhone` offers the other way in, keyed by what she asked with. `button` names the
 * button back to step 1, whose one field takes either, as the line names the way, in place of "Use
 * a different email" or "Use a different number", so the way the line offers is never behind a
 * button that names the other; `wrong` is said in place of the step's own line for a code that did
 * not work, naming that button as the step's own line names its own. Nothing is said of whether
 * the other is on file.
 */
export const OTHER_WAY_IN = {
  email: {
    button: 'Sign in with my mobile number',
    wrong: 'That code did not work. Send a new code or sign in with your mobile number.',
  },
  text: {
    button: 'Sign in with my email address',
    wrong: 'That code did not work. Send a new code or sign in with your email address.',
  },
} as const;

/**
 * NEW: "Charged to Visa ending 4242, 2 October.", how a paid invoice was paid, said on Payments only
 * when ops says the saved method charged it, in the artboard's form for a payment ("From your bank
 * account, 2 October."), with the method in ops's words for it (`savedMethodWords`). Without the
 * day it ends at the method.
 */
export function chargedTo(
  label: string | undefined,
  methodType: string | undefined,
  day: string | null,
): string {
  const method = `Charged to ${methodNamed(label, methodType)}`;
  return day === null ? `${method}.` : `${method}, ${day}.`;
}

/**
 * AN INVOICE'S OWN PAGE, where Pay on Payments leads. No artboard draws it (business issue 13), so
 * it is built from Payments' drawn parts, Feedback's faces, area and line, and the seam's words
 * (section 5, l.709-743 at b727ec1). Its heading is Payments' "Visit of Tuesday 13 October", under
 * the eyebrow "Your invoice", and a paid invoice says the artboard's "Paid" (`PAYMENTS.paid`). It
 * carries what ops's own invoice page carries, its total, the day it was issued and the day it is
 * due, since the seam asks that home be "the same page" (ops's `docs/seams/home.md` l.740-742 at
 * d1c7636), and the owner's walk of 2026-10-16 found it the card on Payments again, word for word.
 */
export const INVOICE = {
  /**
   * NEW: the eyebrow over the heading, and the page's heading where ops gives no day to name it by,
   * as ops's own invoice page heads one, in place of "Payments", so the page says what it is.
   */
  eyebrow: 'Your invoice',
  /** NEW: the row under the lines that gives ops's total, as ops's own page labels it. */
  total: 'Total',
  /** NEW: an invoice ops says is void or written off, in place of an amount. */
  nothingOwed: 'Nothing is owed on this invoice.',
  /**
   * NEW (round five's slice B3, ops's section 11.2): the heading over how and when each payment on
   * the invoice came, under its card (`paymentSaid`), drawn once ops says one did.
   */
  payments: 'Payments on this invoice',
  /**
   * NEW: where ops says there is something to pay from here now and sends no Zelle tag while Stripe
   * is off, and in place of Pay on the bank and the card once ops says it has made every page it
   * will for the invoice from home (`409 pay_pages_used_up`, the seam l.620, whose sentence "names
   * the office's number"). The phone follows it, and then a full stop.
   */
  payBeforePhone: 'To pay this invoice, please call',
  /**
   * NEW (ops's plan for clients reaching only home, section 7, ask 5; home's answer of 2026-10-20,
   * item 5): an invoice's page for an id home does not hold while her first fill is still arriving,
   * in the status under "Your invoice", beside the ring that turns, with `UPDATES_BY_ITSELF` after
   * it while the page reads itself again. It says only that the invoice is coming, never anything of
   * it, and never that one is missing.
   */
  notHereYet: 'This invoice is on its way to your page.',
  /**
   * NEW (the review of 7e33d61, finding 1): an invoice's page for an id none of her households holds
   * while a client's door ops pushed for the address or number she signed in with waits for her
   * code, said first in the code step under "Your invoice", as the step-up sheets say why a code is
   * asked for ("To stop it, we email you a fresh code first."), and ending as they do, so the step
   * says "we text you" where she signed in by her number (`freshWhyByText`). Nothing of the
   * invoice, and never that it is hers.
   */
  codeFirst: 'To see this invoice here, we email you a fresh code first.',
  /** NEW: the code step's button there, which goes on to read the invoice again. */
  codeContinue: 'Continue',
  /**
   * NEW: no code can be asked for there: sign-in is closed, or she signed in by her number where no
   * code can be texted. The phone follows it, and then a full stop.
   */
  codeCannotBeforePhone: 'This invoice cannot be opened here right now. Please call',
  /**
   * NEW (the owner's rule of 2026-10-20, never silent): said where she pressed "Yes, I sent it" or
   * "I have not sent it yet", when the request door answers 404 for the invoice, which it does
   * alike for an invoice gone and one not hers to act on (CLAUDE.md binding rule 2), so the words
   * are the same either way; the invoice is read again, which goes to Payments once it is not hers.
   * The phone follows it, and then a full stop.
   */
  notHereBeforePhone: 'This invoice can no longer be acted on here. Please call',
} as const;

/**
 * NEW: an invoice ops says is part paid, with what is still to pay as ops reads it, "Part paid. $80
 * of $212 still to pay.", and with the total ops pushed when it gave one: ops's `total_cents`,
 * written as `dollars` writes it, so the lines under it, which add up to it, agree with a figure
 * shown, and nothing is worked out here.
 */
export const partPaid = (amount: string, total?: string) =>
  total === undefined
    ? `Part paid. ${amount} still to pay.`
    : `Part paid. ${amount} of ${total} still to pay.`;

/** NEW: an invoice ops says is overdue, with the day it was due, "This was due on 21 October." */
export const wasDueOn = (day: string) => `This was due on ${day}.`;

/**
 * NEW: an invoice ops says is sent or part paid, with the day it is due, "Due on Friday 23
 * October.", under its amount on its card on Payments and on its own page, as ops's own page gives
 * "Due". The day is written by `dayInWords`.
 */
export const dueOnDay = (day: string) => `Due on ${day}.`;

/**
 * NEW: at the foot of an invoice's own page, the day ops issued it, "Issued on Friday 16 October.",
 * as ops's own page gives "Issued", wherever its heading names its clean's day rather than this
 * one. The day is written by `dayInWords`.
 */
export const issuedOnDay = (day: string) => `Issued on ${day}.`;

/** The automatic charge that paid an invoice, as the Worker gives it to an invoice's own page. */
export interface ChargedSaid {
  readonly label?: string;
  readonly methodType?: string;
  readonly amountCents?: number;
  /** The day in Florida it was charged on, `YYYY-MM-DD`. */
  readonly chargedOn?: string;
  readonly receiptEmailed: boolean;
}

/**
 * SEAM (section 5 at 0b51e49, `chargedWords`, Q2): what an invoice's own page says of the charge that
 * paid it, "Paid automatically: $212.00 was charged to your saved card, Visa ending 4242, on Tuesday,
 * October 27.", then " A receipt was emailed." only while ops says it was. The amount as
 * `formatCents` writes it. The method is "your saved payment method" for a type ops does not know;
 * "your saved <noun>" where its words say no more than its noun, as for Link; else "your saved
 * <noun>, <words>". The day is the one it was charged on in Florida, with a comma ahead of it after
 * a method named in words.
 *
 * Home's own reading, for what the contract does not cover: a charge ops gives no amount for says
 * "charged to" with none, and one with no day ends at the method. Ops always sends both.
 */
export function chargedWords(charged: ChargedSaid): string {
  const { label, methodType, amountCents, chargedOn, receiptEmailed } = charged;
  const noun = savedMethodNoun(methodType);
  const words = savedMethodWords(label, methodType);
  const inWords = noun !== SAVED_PAYMENT_METHOD && words.toLowerCase() !== noun.toLowerCase();
  const which =
    noun === SAVED_PAYMENT_METHOD
      ? `your ${SAVED_PAYMENT_METHOD}`
      : inWords
        ? `your saved ${noun}, ${words}`
        : `your saved ${noun}`;
  const day = chargedOn === undefined ? null : weekdayMonthDate(chargedOn);
  const when = day === null ? '' : `${inWords ? ',' : ''} on ${day}`;
  const charge =
    amountCents === undefined
      ? `charged to ${which}${when}`
      : `${formatCents(amountCents)} was charged to ${which}${when}`;
  const said = `Paid automatically: ${charge}.`;
  return receiptEmailed ? `${said} A receipt was emailed.` : said;
}

/**
 * SEAM (section 5, l.726-729 at b727ec1): "How did we do?" on an invoice's own page, above every way
 * to pay, word for word, with the four faces (`FACES`) and, after any but Very good, Feedback's
 * area, line and re-clean promise (`FEEDBACK`, `AREAS`), by the rule Feedback asks them by
 * (`asksWhatWasMissed`, `lib/faces.ts`, ops #72, slice V4). "Good" is a good face here, as the
 * seam words it: thanked with `thanks`, whether or not she named an area.
 */
export const INVOICE_FEEDBACK = {
  heading: 'How did we do?',
  tap: 'Tap the face that fits. Your answer goes to the office and nowhere else.',
  /** After a good face. */
  thanks: 'Thank you for telling us.',
  /** After a lesser face, with the ways to pay folded away behind `waysToPay`. */
  putRight: 'Thank you for telling us. The office will be in touch to put it right.',
  waysToPay: 'Ways to pay this invoice',
  /**
   * NEW: ops recorded nothing of her newest answer, because it already holds twelve from home on
   * the invoice and says `recorded: false` (the seam, l.537). The phone follows it, and then a full
   * stop.
   */
  alreadyBeforePhone: 'We already have your answers on this invoice. To tell us more, call',
  /**
   * NEW (round four, slice F1): what a face she has tapped and not yet sent is described by, to a
   * screen reader, beside its pressed state, while its question and Send wait under it. Drawn as a
   * ring and not the navy fill a sent face has, so a face chosen never reads as an answer the office
   * has. The replay of the owner's walk found Good filled on the tap, as Very good is once sent, with
   * its Send out of sight under the nav.
   */
  notSent: 'Not sent until you press Send.',
} as const;

/**
 * ZELLE, THE FIRST OF THE WAYS TO PAY on an invoice's own page, one screen at a time in the owner's
 * order (ops #46): Zelle, then the bank, then the card. Its data is all ops's (`pay.zelle`, the
 * seam, section 3). The seam's lines (section 5) are word for word, and so are the steps behind "How
 * do I use Zelle?", `zelleSteps`, and the "Stuck?" line beneath them, which the seam prints at
 * 0b51e49 (Q2).
 */
export const ZELLE = {
  /** SEAM: the screen's heading. */
  heading: 'Zelle is the easy one.',
  /**
   * SEAM: a plain link to `pay.zelle.qr_url`, the address the code encodes, opened in a new tab
   * (section 5 at 0b51e49, Q4).
   */
  openPage: "Open Zelle's page",
  /** SEAM: the steps, folded away behind it. */
  howTo: 'How do I use Zelle?',
  /** SEAM: the next screen, offered only while ops says the bank and card screens exist. */
  anotherWay: 'Pay another way',
  /**
   * SEAM: in place of `anotherWay`, where ops says the bank and card screens do not exist, so the
   * ways to pay are offered nowhere else. The phone sits between the two halves.
   */
  cantUseBeforePhone: "Can't use Zelle? Call or text the office at",
  cantUseAfterPhone: 'to pay another way.',
  /** NEW: the button beside the tag, and what it says once the tag is on her clipboard. */
  copy: 'Copy',
  copied: 'Copied.',
  /**
   * NEW: under Copy, with the tag ops pushes, "Open your bank's app, choose Zelle, and paste
   * faithfullykept.", copied from ops's own page for an invoice, which says it under the tag (as
   * home's build brief of the owner's walk of 2026-10-16 quotes it); the seam does not print it.
   * Drawn on any screen, so a phone, which shows no code, says what to do with the tag.
   */
  pasteInYourBank: (tag: string) => `Open your bank's app, choose Zelle, and paste ${tag}.`,
  /** NEW: under the code, drawn on a wider screen only. */
  scan: "Scan it from the Zelle screen in your bank's app.",
  /**
   * NEW: the brief's rule (section 7.6) that the published price is the price however it is paid,
   * said where Zelle is said to cost the business nothing, so that is never read as a price.
   */
  samePrice: 'The price is the same however you pay.',
  /** SEAM (`zelleSteps`): the first step. */
  openYourBank: "Open your bank's app and choose Zelle, or Send money.",
  /** SEAM (`zelleSteps`): the second step, up to the tag, which follows it after a space. */
  pasteBeforeTag: 'On Select recipient, paste',
  /**
   * SEAM (`zelleSteps`): the end of the second step, before its full stop. The screen draws it where
   * it draws the code, on a wider screen, so a phone, which shows no code, is not told to scan one.
   */
  orScan: ' or scan the code',
  /**
   * SEAM (`ZELLE_STUCK`): beneath the steps, either way. The phone follows it, a link to call it,
   * and then a full stop.
   */
  stuckBeforePhone: 'Stuck? Call or text the office at',
  /** SEAM: the bank screen's way back to Zelle. */
  backToZelle: 'Back to Zelle',
  /**
   * NEW (slice V2): at the foot of Zelle's screen, after the steps and above `sentIt`, on an
   * invoice's own ways to pay. Written after the walk was replayed (2026-10-17): after paying by
   * Zelle, home kept saying she owed it, with Home's line, the nav's count and Pay, because only the
   * office can match a Zelle payment, and nothing on the screen said what happens once she has sent
   * it. It says what the office does and what will show once it has, never that anything is paid
   * (CLAUDE.md binding rule 1); the invoice reads paid only once ops pushes it so.
   */
  matchedByHand:
    'The office matches Zelle payments by hand. This invoice will show as paid once it does.',
  /**
   * NEW (slice V2): the text link under `matchedByHand`, in the card with the tag, under the line
   * that says where to paste it (round four, slice M1, after the walk was replayed, where it was the
   * same box as "Pay another way" right under it). Pressed, it opens the step that asks her to
   * check what she sent and the day (`didYouSend`, `sentOnLabel`, `yesSent`, `notYet`); her yes is
   * reported to ops (ops's section 11.7, round five's slice B2, P53), so its Pay, the nav's count
   * and Home's line stand down on every device of every client of the household, and its card says
   * `sentByZelleOn`. Her fact, never read as paid.
   */
  sentIt: 'I sent it by Zelle',
  /**
   * NEW (round five, slice B2): the label of the picker in that step, which offers the days ops's
   * section 11.7 takes, from the day of the clean to today in Florida, at most the last seven,
   * today first and chosen (`sentOnChoice`).
   */
  sentOnLabel: 'The day you sent it',
  /**
   * NEW (round five, slice B2): the step's button that reports it, `invoice.zelle_sent` with the day
   * she picked. Drawn as the navy button, the one thing the step is for.
   */
  yesSent: 'Yes, I sent it',
  /** NEW (round five, slice B2): the step's text link that closes it, sending nothing. */
  notYet: 'Not yet',
  /**
   * NEW (round six, slice S1, in place of round five's "Sent it by mistake? Call or text"): under what
   * she told the office she sent, while ops has not answered her report yet, so there is nothing at
   * ops for her to take back (`notSentYet` once there is), for a word given by mistake and for a
   * client who pressed "Yes, I sent it" before she sent it, which the replay of 2026-10-18 found
   * "Sent it by mistake?" did not read as. The phone follows it, and then a full stop.
   */
  toldByMistakeBeforePhone: 'Told us this by mistake, or not sent it yet? Call or text',
  /**
   * NEW (ops's section 11.7, "Taking it back", live on both of its hosts from 2026-10-19, ops #71;
   * round four's own words for its own mark, back now that ops can take a report back): the text
   * link under what she told the office she sent, once ops holds her report, for the client who made
   * it alone. Pressed, it sends `invoice.zelle_withdrawn`, her word that she did not send it after
   * all; home's own mark ends at once, and ops's answer brings every way to pay back.
   */
  notSentYet: 'I have not sent it yet',
  /**
   * NEW (11.7, "Taking it back"): in place of `notSentYet` once the request door has her press, and
   * above the ways to pay where nothing else holds the invoice, while ops has not answered it. Says
   * what is happening and promises nothing ops has not said (binding rule 1).
   */
  takingBack: 'Telling the office you have not sent it yet.',
  /**
   * NEW (11.7, "Taking it back"): above every way to pay once ops has taken her report back, its
   * receipt `reported: false`, so the office stops looking for her Zelle (its task closes). Kept
   * while the invoice is owed and she has made no report since. Never that anything is paid.
   */
  takenBack: 'You told us you have not sent it yet, so the office will not look for it.',
  /**
   * NEW (11.7, "Taking it back"): in place of `notSentYet`, for a refusal of her taking it back the
   * contract gives no words (`zelleTakeBackRefusal`), and for one home gave up sending. A report may
   * still stand, so the office's number. The phone follows it, and then a full stop.
   */
  notTakenBackBeforePhone:
    'That did not go through. To tell the office you have not sent it, call or text',
  /**
   * NEW (11.7, "Taking it back"): after ops's `report_cleared`, "The office has already cleared your
   * Zelle report on this invoice.", which says the office has it, so she can ask the office about it.
   * The phone follows it, and then a full stop.
   */
  askAboutItBeforePhone: 'To ask about it, call or text the office at',
  /**
   * NEW (11.7, "Taking it back"): under another client's report on the household, and one ops holds
   * that home has no record of, in place of any way to take it back, which only the person who made
   * it has (ops's `reported_by_another`). The phone follows it, and then a full stop.
   */
  notSentAfterAllBeforePhone: 'Not sent after all? Call or text the office at',
  /**
   * NEW (round five, slice B2): where the link was, for a refusal of her report the contract gives
   * no words for (`zelleReportRefusal`), such as a day it would not take. The phone follows it, and
   * then a full stop.
   */
  notToldBeforePhone: 'That did not go through. To tell the office you sent it, call or text',
  /**
   * NEW (round six, slice S1, in place of round four's "What to send by Zelle"): the heading over what
   * she told the office she sent, the tag and the memo as a record (`whatYouSent`), kept under the
   * invoice's card once she says she sent it. The owner's walk of 2026-10-17 asked for the details to
   * stay; the replay of 2026-10-18 found them still read as an ask to send $175 with Copy, which a
   * hurried client could pay twice, so they now read as what she said she sent, with no amount,
   * since the balance moves under her report and ops keeps none of hers.
   */
  whatYouSentHeading: 'What you told us you sent',
} as const;

/**
 * NEW (round six, slice S1): what she told the office she sent by Zelle, under `ZELLE.whatYouSentHeading`,
 * as a fact and never an ask: the tag and the memo ops pushes, as the step she answered named them
 * (`didYouSend`), "A Zelle payment to faithfullykept with the memo FK Oct 27.", and while ops sends
 * no memo, "A Zelle payment to faithfullykept.". Never an amount: ops holds only the day she said
 * she sent it (section 11.7), and what is still to pay moves under her report while it stands, by
 * the office's adjustment or a match of part of a split Zelle, so a balance said here would be an
 * amount she never said. What is still to pay is ops's, on the card.
 */
export function whatYouSent(tag: string, memo?: string): string {
  const to = `A Zelle payment to ${tag}`;
  return memo === undefined ? `${to}.` : `${to} with the memo ${memo}.`;
}

/**
 * NEW (round five, slice B2): the question of the step "I sent it by Zelle" opens, naming exactly
 * what she was asked to send, the amount as `formatCents` writes it and the tag and the memo ops
 * pushes (`pay.zelle`): "Did you send $212.00 to faithfullykept with the memo FK Oct 27?", and while
 * ops sends no memo, "Did you send $212.00 to faithfullykept?". Answered by `ZELLE.yesSent` or
 * `ZELLE.notYet`, with the day she sent it picked under it.
 */
export function didYouSend(amount: string, tag: string, memo?: string): string {
  return memo === undefined
    ? `Did you send ${amount} to ${tag}?`
    : `Did you send ${amount} to ${tag} with the memo ${memo}?`;
}

/**
 * NEW (round five, slice B2): a day the step offers, "Saturday 17 October", and today, which comes
 * first and is chosen, "Today, Tuesday 20 October". `day` is the day in words (`dayInWords`).
 */
export const sentOnChoice = (day: string, today: boolean): string =>
  today ? `Today, ${day}` : day;

/**
 * SEAM (section 11.7 at ops ddd5081, `ZELLE_REPORT_REFUSAL_WORDS`): the message of the two refusals
 * of her report that she sent an invoice by Zelle that are the type's own, word for word, each said
 * where the link was; and, since ops 28f9237, the three of her taking it back (11.7, "Taking it
 * back", "The words"), which ops keeps in the same table, each said where "I have not sent it yet"
 * was or above the ways to pay (`zelleTakeBackRefusal`). `contract-words.test.ts` holds them to the
 * contract.
 */
export const ZELLE_REPORT_REFUSAL_WORDS = {
  not_paused: `Press "I'm paying by Zelle" first, so your saved payment method is not charged as well.`,
  zelle_reports_used_up: `You have told us about this invoice's Zelle as often as this page allows. Call or text the office at ${PHONE_SHOWN}.`,
  report_cleared: 'The office has already cleared your Zelle report on this invoice.',
  nothing_reported: 'There is no Zelle report on this invoice to take back.',
  reported_by_another: `Someone else in this household told us about this Zelle, so only they can take it back here. Call or text the office at ${PHONE_SHOWN}.`,
} as const;

/**
 * SEAM: what a refusal of her report says, in the contract's words (section 11.7): its own two
 * (`ZELLE_REPORT_REFUSAL_WORDS`), and section 5's for the others, `PAUSE_REFUSAL_WORDS` for an
 * invoice that owes nothing, a charge at Stripe and a pause lifted, and the line while a bank
 * payment clears (`PAYMENTS.clearing`). Null for a refusal the contract gives no words, which the
 * screen says in home's own line, `ZELLE.notToldBeforePhone`, with the phone.
 */
export function zelleReportRefusal(code: string | undefined): string | null {
  switch (code) {
    case 'not_paused':
    case 'zelle_reports_used_up':
      return ZELLE_REPORT_REFUSAL_WORDS[code];
    case 'invoice_not_open':
    case 'charge_in_flight':
    case 'pause_lifted':
      return PAUSE_REFUSAL_WORDS[code];
    case 'bank_payment_clearing':
      return PAYMENTS.clearing;
    default:
      return null;
  }
}

/**
 * SEAM, with one NEW sentence: what a refusal of her taking her report back says (section 11.7,
 * "Taking it back"), in the contract's words: `nothing_reported` and `reported_by_another` as ops
 * prints them, the second naming the office's number; `report_cleared` as ops prints it, then home's
 * own way to ask the office about it (`ZELLE.askAboutItBeforePhone`, NEW), since the office has it;
 * and section 5's for an invoice that owes nothing now, which the page says in place of every way
 * to pay until the push says how the invoice stands, as `PAYING.nothingToPay`. Null for a refusal
 * the contract gives no words, which the screen says in home's own line,
 * `ZELLE.notTakenBackBeforePhone`, with the phone.
 */
export function zelleTakeBackRefusal(code: string | undefined): string | null {
  switch (code) {
    case 'nothing_reported':
    case 'reported_by_another':
      return ZELLE_REPORT_REFUSAL_WORDS[code];
    case 'report_cleared':
      return `${ZELLE_REPORT_REFUSAL_WORDS.report_cleared} ${ZELLE.askAboutItBeforePhone} ${PHONE_SHOWN}.`;
    case 'invoice_not_open':
      return PAUSE_REFUSAL_WORDS.invoice_not_open;
    default:
      return null;
  }
}

/**
 * NEW (round five, slice B2, ops's section 11.7, P53; round four's slice M1 before it): on an invoice
 * somebody on the household reported sending by Zelle, right under its amount in place of the day it
 * is due, on its card on Payments and on its own page, with no Pay, while the Worker says her report
 * holds it on its way. It names the day she said she sent it, as she picked it, and, for another
 * client's report, never says who; where home holds no report of its own, as after a forget, it
 * names neither. It says what she told the office and what the office does with it, never that
 * anything is paid (binding rule 1): the invoice reads paid only once ops pushes it so.
 */
export function sentByZelleOn(day: string | null, own: boolean | undefined): string {
  const on = day === null ? '' : ` on ${day}`;
  const told =
    own === true
      ? `You told us you sent it by Zelle${on}.`
      : own === false
        ? `Someone in your household told us it was sent by Zelle${on}.`
        : 'We were told it was sent by Zelle.';
  return `${told} The office will look for it, and this invoice will show as paid once it is matched.`;
}

/**
 * SEAM (section 5): "We are two people, not a chain, and Zelle costs us nothing: $212.00 to
 * faithfullykept, memo FK Oct 27.", with what is still to pay as `formatCents` writes it, and the
 * tag and memo as ops pushes them. Without a memo the sentence ends after the tag.
 */
export function zelleCostsNothing(amount: string, tag: string, memo?: string): string {
  const to = `We are two people, not a chain, and Zelle costs us nothing: ${amount} to ${tag}`;
  return memo === undefined ? `${to}.` : `${to}, memo ${memo}.`;
}

/** SEAM (`zelleSteps`): the third step, "Check that it says FAITHFULLY KEPT LLC.". */
export const checkTheName = (name: string) => `Check that it says ${name}.`;

/**
 * SEAM (`zelleSteps`): the fourth step, "Send $212.00 with the memo FK Oct 27.", and while ops sends
 * no memo, "Send $212.00.".
 */
export function sendTheAmount(amount: string, memo?: string): string {
  return memo === undefined ? `Send ${amount}.` : `Send ${amount} with the memo ${memo}.`;
}

/**
 * SEAM (section 5 at 0b51e49, `zelleSteps`, Q2): the steps behind "How do I use Zelle?", in order,
 * filled with the tag, the name and the memo ops pushes (`pay.zelle`) and what is still to pay as
 * `formatCents` writes it:
 *
 * 1. Open your bank's app and choose Zelle, or Send money.
 * 2. On Select recipient, paste faithfullykept or scan the code.
 * 3. Check that it says FAITHFULLY KEPT LLC.
 * 4. Send $212.00 with the memo FK Oct 27.
 *
 * The fourth without the memo while ops sends none. Home's own reading, for what the contract does
 * not cover: the third is left out while ops sends no name for it to say.
 */
export function zelleSteps(
  zelle: { readonly tag: string; readonly name?: string; readonly memo?: string },
  amountCents: number,
): string[] {
  const { tag, name, memo } = zelle;
  return [
    ZELLE.openYourBank,
    `${ZELLE.pasteBeforeTag} ${tag}${ZELLE.orScan}.`,
    ...(name === undefined ? [] : [checkTheName(name)]),
    sendTheAmount(formatCents(amountCents), memo),
  ];
}

/**
 * THE BANK, THE SECOND OF THE WAYS TO PAY, a page of Stripe's that ops makes (the seam, section 5 at
 * 0b51e49), word for word, with the amount as `formatCents` writes it. The screen also says that the
 * price is the same, as the seam asks, in Zelle's shipped line (`ZELLE.samePrice`). Her tick, which
 * saves the account she pays from, is drawn only while ops offers it (`pay.offer_to_save`), with
 * `AUTOPAY_BANK_TERMS` beside it.
 */
export const BANK = {
  /** SEAM: the screen's heading. */
  heading: 'Your bank account is next best.',
  /**
   * SEAM (section 5, "Her tick", home's Q1): the tick, unticked at first, drawn only while ops
   * offers to save, with `AUTOPAY_BANK_TERMS` beside it.
   */
  tick: 'Charge my bank account automatically after each clean',
  /** SEAM: asks ops for the bank page. */
  pay: 'Pay from my bank account',
  /** SEAM: to the card. */
  cantPayByBank: "I can't pay by bank",
  /**
   * NEW: why a press with the tick asks for a code, said first on its code step: the tick saves
   * the account to be charged automatically, which changes how the household pays (CLAUDE.md
   * binding rule 5). It says what the paragraph beside the tick says, in fewer words.
   */
  freshCode:
    'Ticking the box saves your bank account to be charged automatically, so we email you a fresh code first.',
  /**
   * NEW: no fresh code can be asked for here, with the tick: sign-in is closed. Unticked, the bank
   * page asks for no code. The phone follows it, and then a full stop.
   */
  cannotBeforePhone:
    'Your bank account cannot be saved here right now. Untick the box to pay without saving it, or call',
} as const;

/**
 * SEAM (section 5 at 0b51e49, "Her tick", `AUTOPAY_BANK_TERMS`, home's Q1; its last sentence the one
 * section 12.5 of 2026-10-17 approved, naming the Stop on her account page, live on both hosts that
 * evening): the paragraph beside `BANK.tick`, word for word, and no other. A ticked press sends its
 * version, `AUTOPAY_BANK_TERMS_VERSION`, the hex SHA-256 of these words' UTF-8 bytes (12.3), which
 * ops records onto the method once the payment arrives, or refuses `409 terms_changed` where it is
 * not the current one. `contract-words.test.ts` hashes this line and holds it and the version to the
 * contract, so new words reach home only as a change to the contract, raised before ops releases
 * them.
 */
export const AUTOPAY_BANK_TERMS = `If you tick this, the bank account you pay this invoice from is saved, and Faithfully Kept will charge it for each clean's invoice after you tell us the visit went well, or once we have checked with you that it did, at the rate published for your home on faithfullykept.com. Each time, we email a receipt if we have an email address for you, unless you have asked us not to. If you tell us something was not right, nothing is charged until it is put right. We never charge anything else without asking you first. You can stop this at any time by calling or texting ${PHONE_SHOWN}, emailing hello@faithfullykept.com, or with Stop on your account page at home.faithfullykept.com.`;

/**
 * The version of `AUTOPAY_BANK_TERMS` a ticked bank press names as `terms_version` (section 12.3 and
 * 12.5's table): no word a client reads. `contract-words.test.ts` holds it to the hash of the words
 * above and to the value the contract prints.
 */
export const AUTOPAY_BANK_TERMS_VERSION =
  '71e63de6c115f33dba243e90665f53831eed41d19cacf23832d76340a5184844';

/**
 * SEAM (section 5): "Pay $212.00 from your checking or savings account through Stripe. It costs us
 * far less than a card.", with what is still to pay as ops reads it, as `formatCents` writes it.
 */
export const fromYourBank = (amount: string) =>
  `Pay ${amount} from your checking or savings account through Stripe. It costs us far less than a card.`;

/**
 * THE CARD, THE THIRD OF THE WAYS TO PAY, a page of Stripe's that ops makes (l.724-725). Her tick,
 * which saves the card she pays with (ops #70, section 5 at 7e2d7ae, "The card tick"), is drawn
 * only while ops offers it (`pay.offer_to_save`), unticked, with `AUTOPAY_CARD_TERMS` beside it, as
 * the bank's is.
 */
export const CARD = {
  /** SEAM: the screen's heading. */
  heading: 'Card, Apple Pay or Google Pay.',
  /**
   * SEAM (section 5 at 7e2d7ae, "The card tick", ops #70): the tick, unticked at first, drawn only
   * while ops offers to save, with `AUTOPAY_CARD_TERMS` beside it.
   */
  tick: 'Charge my card automatically after each clean',
  /** SEAM: asks ops for the card page. */
  pay: 'Pay by card',
  /**
   * NEW: the card screen's way back to the bank screen, named as the bank screen's own way back
   * names Zelle.
   */
  back: 'Back to bank account',
  /**
   * NEW (ops #70): why a press with her tick asks for a code, said first on its code step: the tick
   * saves the card to be charged automatically, which changes how the household pays (CLAUDE.md
   * binding rule 5). As the bank's tick says it (`BANK.freshCode`), in fewer words than the
   * paragraph beside the tick. A press without the tick asks for no code since round five's slice
   * A4: the card page it asks for saves nothing (ops's section 5 at dc63b80).
   */
  tickedFreshCode:
    'Ticking the box saves your card to be charged automatically, so we email you a fresh code first.',
  /**
   * NEW (slice A4): no fresh code can be asked for here, with the tick: sign-in is closed.
   * Unticked, the card page asks for no code. As the bank's says it (`BANK.cannotBeforePhone`). The
   * phone follows it, and then a full stop.
   */
  cannotBeforePhone:
    'Your card cannot be saved here right now. Untick the box to pay without saving it, or call',
} as const;

/**
 * SEAM (section 5 at ops 7e2d7ae, "Her card tick: AUTOPAY_CARD_TERMS", ops #70, approved by the
 * owner word for word on 2026-10-17; its last sentence the one section 12.5 approved the same day):
 * the paragraph beside `CARD.tick`, word for word, and no other. A ticked press sends its version,
 * `AUTOPAY_CARD_TERMS_VERSION`, made as the bank's is (12.3), which ops records onto the card once
 * the page her tick asked for completes paid. `contract-words.test.ts` hashes this line and holds it
 * and the version to the contract.
 */
export const AUTOPAY_CARD_TERMS = `If you tick this, the card you pay this invoice with is saved, and Faithfully Kept will charge it for each clean's invoice after you tell us the visit went well, or once we have checked with you that it did, at the rate published for your home on faithfullykept.com. Each time, we email a receipt if we have an email address for you, unless you have asked us not to. If you tell us something was not right, nothing is charged until it is put right. We never charge anything else without asking you first. You can stop this at any time by calling or texting ${PHONE_SHOWN}, emailing hello@faithfullykept.com, or with Stop on your account page at home.faithfullykept.com.`;

/**
 * The version of `AUTOPAY_CARD_TERMS` a ticked card press names as `terms_version` (section 12.3 and
 * 12.5's table): no word a client reads. `contract-words.test.ts` holds it to the hash of the words
 * above and to the value the contract prints.
 */
export const AUTOPAY_CARD_TERMS_VERSION =
  '426159e422cdd93849af765d30717547ea5598704cd62f3e7e230513739f1314';

/**
 * SEAM (section 12.5 of 2026-10-17, "The refusals' sentences", `terms_changed`, approved by the
 * owner that day): said above her tick, with the focus, once ops has refused a ticked press because
 * the words beside the tick were not the current ones and the page has been loaded afresh to draw
 * the ones that are (12.3).
 */
export const TERMS_CHANGED =
  'The words you were shown have changed. Please read the new ones and try again.';

/**
 * SEAM (section 12.5 of 2026-10-17, "The setup words, beside home's button", `AUTOPAY_SETUP_BANK_TERMS`,
 * place `setup_bank`, approved by the owner word for word that day): the paragraph beside "Save a
 * bank account" on How you pay (round five, slice A5), the opt-in she agrees to, word for word and
 * no other. A press sends its version, `AUTOPAY_SETUP_BANK_TERMS_VERSION`, the hex SHA-256 of these
 * words' UTF-8 bytes (12.2), which ops records onto the method once Stripe says it is saved, or
 * refuses `409 terms_changed`. `contract-words.test.ts` hashes it and holds both to the contract.
 */
export const AUTOPAY_SETUP_BANK_TERMS = `If you save a bank account here, Faithfully Kept will charge it for each clean's invoice after you tell us the visit went well, or once we have checked with you that it did, at the rate published for your home on faithfullykept.com. Nothing is charged today. Each time, we email a receipt if we have an email address for you, unless you have asked us not to. If you tell us something was not right, nothing is charged until it is put right. We never charge anything else without asking you first. You can stop this at any time by calling or texting ${PHONE_SHOWN}, emailing hello@faithfullykept.com, or with Stop on your account page at home.faithfullykept.com.`;

/** The version of `AUTOPAY_SETUP_BANK_TERMS` a press sends as `terms_version` (12.5's table). */
export const AUTOPAY_SETUP_BANK_TERMS_VERSION =
  'e59f75fc3ff1770285014c494cde3a79cfe9e3e896b4e02611e891d0b8c359fb';

/**
 * SEAM (section 12.5, `AUTOPAY_SETUP_CARD_TERMS`, place `setup_card`): "the same with "a card" for "a
 * bank account"", beside "Save a card".
 */
export const AUTOPAY_SETUP_CARD_TERMS = `If you save a card here, Faithfully Kept will charge it for each clean's invoice after you tell us the visit went well, or once we have checked with you that it did, at the rate published for your home on faithfullykept.com. Nothing is charged today. Each time, we email a receipt if we have an email address for you, unless you have asked us not to. If you tell us something was not right, nothing is charged until it is put right. We never charge anything else without asking you first. You can stop this at any time by calling or texting ${PHONE_SHOWN}, emailing hello@faithfullykept.com, or with Stop on your account page at home.faithfullykept.com.`;

/** The version of `AUTOPAY_SETUP_CARD_TERMS` a press sends as `terms_version` (12.5's table). */
export const AUTOPAY_SETUP_CARD_TERMS_VERSION =
  '7a4ea6023fe323be31c4eeadbaea6f660ff0c002cea1406961aff75d86b10a73';

/**
 * SAVING A WAY TO PAY WITH NOTHING OWED, on How you pay (ops's section 12.2, round five, slice A5),
 * drawn only where the Worker says ops's door for it is open. No artboard draws it (business issue
 * 13), so it is built from How you pay's drawn parts. Every line here is NEW, for the owner to read
 * before clients are invited; the words beside each button are ops's (`AUTOPAY_SETUP_BANK_TERMS`,
 * `AUTOPAY_SETUP_CARD_TERMS`, SEAM), and nothing here says a method is saved: ops says that.
 */
export const SAVE = {
  /** NEW: the heading over the offer, in the words ops's refusals use for it (12.5). */
  heading: 'Save a payment method',
  /** NEW: the button beside the bank's setup words, which asks ops for Stripe's page to save on. */
  bank: 'Save a bank account',
  /** NEW: the button beside the card's setup words. */
  card: 'Save a card',
  /**
   * NEW: under "One moment" in the panel paying draws (`Leaving.tsx`), once the door has the ask,
   * in place of "Taking you to Stripe to pay.", since nothing is paid on the page.
   */
  takingYouBank: 'Taking you to Stripe to save your bank account.',
  /** NEW: the same, for the card. */
  takingYouCard: 'Taking you to Stripe to save your card.',
  /**
   * NEW: why a press asks for a code, said first on its code step: saving changes how the household
   * pays (CLAUDE.md binding rule 5). As the ticks say it (`BANK.freshCode`, `CARD.tickedFreshCode`).
   */
  bankFreshCode:
    'Saving a bank account here means it is charged automatically, so we email you a fresh code first.',
  /** NEW: the same, for the card. */
  cardFreshCode:
    'Saving a card here means it is charged automatically, so we email you a fresh code first.',
  /**
   * NEW: no fresh code can be asked for here: sign-in is closed. The phone follows it, and then a
   * full stop.
   */
  bankCannotBeforePhone: 'Your bank account cannot be saved here right now. Please call',
  /** NEW: the same, for the card. */
  cardCannotBeforePhone: 'Your card cannot be saved here right now. Please call',
} as const;

/**
 * SEAM (section 12.5, "The refusals' sentences", `already_saved`, approved by the owner on
 * 2026-10-17): said in the panel when ops, or home's door from the household as ops last pushed it,
 * refuses a way to pay saved with nothing owed because one stands.
 */
export const ALREADY_SAVED =
  'A payment method is already saved for your household. Stop it first to save another.';

/**
 * SEAM (section 12.5, `setup_pages_used_up`): ten pages made for the household in thirty days. The
 * office's number follows it, a link to call it, and then a full stop.
 */
export const SETUP_PAGES_USED_UP_BEFORE_PHONE =
  'Ten pages have been made to save a payment method for your household this month. Call or text the office at';

/**
 * SEAM (section 12.5, "Home's return page after a setup page", `RETURN_PAGE_WORDS`'s new keys,
 * approved by the owner on 2026-10-17): what How you pay says when Stripe sends her back to it, with
 * `page=bank` or `page=card` and `saved=setup`, or with `not-saved` (`screens/AccountPaying.tsx`),
 * word for word, and nothing of a payment, since none was made. "questions: as today." is
 * `RETURN_PAGE_WORDS.questionsBeforePhone`. Ops records a method only once Stripe says it is saved,
 * from the event every destination carries, so a method this page calls saved is one ops has
 * recorded (12.2); how you pay draws it only once ops pushes it.
 */
export const SETUP_RETURN_WORDS = {
  /** SEAM: after `saved=setup`, first and in bold, by the page it was saved on. */
  savedForLater: {
    bank: "Your bank account is saved. Nothing was charged today. We will charge it for each clean's invoice after you tell us the clean went well. You can stop it at any time from your account page or by calling or texting the office.",
    card: "Your card is saved. Nothing was charged today. We will charge it for each clean's invoice after you tell us the clean went well. You can stop it at any time from your account page or by calling or texting the office.",
  },
  /** SEAM: after a bank account, the sentence about confirming one by small deposits. */
  confirmYourBankSetup:
    'If Stripe asked you to confirm your bank account by small deposits, it is saved once you do. Look for an email from Stripe.',
  /** SEAM: after `not-saved`, first and in bold. */
  notSaved: 'Nothing was saved.',
  /** SEAM: after it. */
  leftStripeSetup: "You left Stripe's page before saving, so nothing was saved.",
} as const;

/**
 * SEAM (section 5): "Pay $212.00 through Stripe by card, Apple Pay, Google Pay or Link.", with what
 * is still to pay as ops reads it, as `formatCents` writes it.
 */
export const byCard = (amount: string) =>
  `Pay ${amount} through Stripe by card, Apple Pay, Google Pay or Link.`;

/**
 * SEAM (section 5 at 0b51e49, `PAUSE_REFUSAL_WORDS`, Q2): the message of each refusal of "I'm paying
 * by Zelle", at her link and at home alike, word for word. The first three are `409`s, the last two
 * `400`s. Ops's page shows two as a stage rather than a sentence, and so does home's: `charge_in_flight`
 * as the charge at Stripe and `pause_lifted` as the pause lifted, each in that stage's own words
 * (`SAVED_METHOD_PAYS`), so those two are held here with the rest and drawn nowhere. The other three
 * are drawn as the refusal says them.
 */
export const PAUSE_REFUSAL_WORDS = {
  not_charged_automatically:
    'Your saved payment method is not charging this invoice, so there is nothing to pause. Open this page again to see how to pay it.',
  charge_in_flight: `Your saved payment method is being charged for this invoice now, so there is nothing to send by Zelle. If you have already sent one, call or text the office at ${PHONE_SHOWN}.`,
  pause_lifted: `The office has lifted the pause on this invoice. To pay it by Zelle after all, call or text the office at ${PHONE_SHOWN} first.`,
  invoice_not_open: 'Nothing is owed on this invoice now.',
  charge_changed: 'Your invoice changed while this was being sent. Open this page again.',
} as const;

/** Pressing Pay on the bank or the card, and waiting for ops to make Stripe's page (l.665-675). */
export const PAYING = {
  /**
   * SEAM (l.667): the first line of the panel drawn over the screen the moment Pay is pressed, while
   * the screen asks where the page stands (`components/StripeWay.tsx`).
   */
  oneMoment: 'One moment',
  /**
   * NEW: under "One moment" in that panel, so she knows where she is going while it is made. Written
   * after the owner's walk of 2026-10-16, where only the pointer over the button said anything was
   * happening, and he could not tell "if it is broken or slow or what". It says only what the screen
   * is doing, never that anything is paid.
   */
  takingYou: 'Taking you to Stripe to pay.',
  /**
   * NEW: added under those two lines once about eight seconds have gone and the page is still not
   * ready, from the same walk, so a wait reads as a wait and not as a fault.
   */
  longerThanUsual: 'This is taking longer than usual. Please keep this page open.',
  /**
   * NEW: the page is still not ready after six asks, eighteen seconds, said in that panel in place
   * of its lines. Reworded after the owner's walk of 2026-10-16 from "Stripe's page is still being
   * made. Please try again in a minute, or call", so it says plainly first that it did not go
   * through. Since slice V3 said only where the last of the six said the page is still being made:
   * where it did not go through, the panel says that, and where it never reached the Worker,
   * `connectionDroppedBeforePhone`. The phone follows it, and then a full stop.
   */
  slowBeforePhone:
    "That did not go through, because Stripe's page is still being made. Please try again in a minute, or call",
  /**
   * NEW (slice V3): said in that panel in place of its lines where the press never reached the
   * Worker, because the browser's own request was refused, which is how a browser says the
   * connection dropped, or no answer came while it says it is offline; and where the six asks are
   * spent and the last of them never reached it. Raised by the replay of the owner's walk of
   * 2026-10-16, which found a dropped connection said as Stripe's page still being made. It says
   * what home knows, that its request did not get through, and what she can do. The phone follows
   * it, and then a full stop.
   */
  connectionDroppedBeforePhone:
    'That did not go through, because the connection dropped. Please check your internet connection and try again, or call',
  /**
   * NEW: the button in that panel, once a press did not go through, that presses Pay again: a new
   * request, or the same one where the door could not be reached. From the same walk.
   */
  tryAgain: 'Try again',
  /**
   * SEAM: ops says the invoice owes nothing now (`400 invoice_not_open`), in place of the ways to
   * pay, until its push says how the invoice stands: the message the seam prints for that refusal
   * (`PAUSE_REFUSAL_WORDS`), one code saying one thing however it was pressed.
   */
  nothingToPay: PAUSE_REFUSAL_WORDS.invoice_not_open,
  /**
   * NEW: on an invoice she came back from paying on Stripe's page, in that tab or any other of her
   * browser's (P35), right under its amount in place of the day it is due, on its card on Payments
   * and on its own page, with no Pay, until ops's push says how it stands. It never says paid: that
   * is ops's alone (l.687). Reworded after the owner's walk of 2026-10-16 from "This invoice reads
   * paid once it arrives.", so it says plainly why the amount above it still stands. One sent by
   * Zelle says `sentByZelleOn`, from round four's slice M1 on.
   */
  onItsWay: 'Your payment is on its way. This invoice will show as paid once it reaches us.',
  /**
   * NEW: ops says her saved method pays this invoice (`409 saved_method_pays`, l.619), in place of
   * every way to pay, until its push says what the saved method will do. The office's number for a
   * Zelle already sent follows it (`SAVED_METHOD_PAYS.alreadySentBeforePhone`).
   */
  savedMethodPays: 'Your saved method pays this invoice.',
} as const;

/**
 * THE PAGE STRIPE SENDS HER BACK TO, `/payments/return`, drawn as the Payments artboard's third
 * frame, "Back in home": "Payments" over the heading, a callout, then To pay and Paid. Every line of
 * the heading and the callout is the seam's (section 5 at 0b51e49, `RETURN_PAGE_WORDS`, Q2), word for
 * word and in its order, each as the address back says (`screens/PaymentReturn.tsx`). The artboard's
 * own callout lines are not drawn: "Your payment of $212 is recorded." would say paid before ops
 * does, and no view carries the address a receipt goes to.
 */
export const RETURN_PAGE_WORDS = {
  /** SEAM: the heading after a payment. */
  paid: 'Thank you.',
  /** SEAM: the callout's first line after a payment. */
  onItsWay: 'Your payment is on its way to Faithfully Kept.',
  /** SEAM: unless she paid on the card page. */
  bankTakesDays: 'A bank payment can take a few days to arrive.',
  /** SEAM: with it, the sentence about confirming a bank account by small deposits. */
  confirmYourBank:
    'If Stripe asked you to confirm your bank account, your payment starts once you do. Look for an email from Stripe.',
  /** SEAM: after the bank page with her tick, `saves=account`, ending in how to stop. */
  savedAccount:
    "You asked us to charge your bank account automatically. Once this payment has cleared, we will charge it for each clean's invoice after you tell us the clean went well. You can stop it at any time by calling or texting the office.",
  /**
   * SEAM: after the card page or the office's with Stripe's box, `saves=box`, the same as an if,
   * ending in how to stop.
   */
  savedBox:
    "If you chose to save your payment details on Stripe's page, we will charge them for each clean's invoice after you tell us the clean went well. You can stop it at any time by calling or texting the office.",
  /** SEAM: the last line after a payment. */
  close: 'You can close this page.',
  /** SEAM: the heading after Stripe's back arrow, `not-paid`. */
  notPaid: 'You have not paid yet.',
  /** SEAM: after it, that no payment was made. */
  leftStripe: "You left Stripe's page before paying, so no payment was made.",
  /**
   * SEAM: then how to pay, on ops's own page, reached from a texted link. Home's page says
   * `HOME_HOW_TO_PAY` in its place signed in (section 11.4), where Pay is on the invoice under it,
   * or `INVOICE_HOW_TO_PAY` over an invoice's own ways to pay, and draws this line only where no
   * way to pay is under it, as on ops's page: signed out, above the
   * sign-in at an invoice's own page or at the page Stripe sends her back to (ops's plan for clients
   * reaching only home, section 7, ask 3), and signed in, at the top of an invoice's own page that
   * draws no invoice, while it is on its way, behind a code or not read (P110).
   */
  howToPay: 'To pay, open your invoice again from the message we sent you.',
  /**
   * SEAM: the page's last line either way. The phone follows it, a link to call it, and then a full
   * stop.
   */
  questionsBeforePhone: 'Questions? Call or text the office at',
} as const;

/**
 * SEAM (section 11.4 at ops 7e2d7ae, "Home's line after Stripe's back arrow", ops #71 item 4): on
 * home's return page after `not-paid`, in `RETURN_PAGE_WORDS.howToPay`'s place and nowhere else,
 * word for word, since Pay is on the invoice under it there and the office's message may not have
 * reached her. Every other line of the page is section 5's. The same words back, signed in at the
 * top of an invoice's own page, say `INVOICE_HOW_TO_PAY` in its place instead, since that page
 * draws the invoice's own ways to pay and no button named Pay (P110).
 */
export const HOME_HOW_TO_PAY = 'To pay, press Pay on the invoice below.';

/**
 * NEW (the fifth client-journey walk's review of 2026-10-20, finding 2, shot 124): signed in, at
 * the top of an invoice's own page after Stripe's back arrow, in `HOME_HOW_TO_PAY`'s place, only
 * where the invoice's ways to pay are drawn under the words (P110). The page is the invoice, and
 * its ways to pay open on Zelle, whose buttons are "I sent it by Zelle" and "Pay another way", so
 * a line sending her to press Pay on an invoice below names a button and an invoice she cannot
 * find there. Payments, where each card owed has its Pay, keeps `HOME_HOW_TO_PAY`.
 */
export const INVOICE_HOW_TO_PAY = 'To pay, choose a way to pay below.';

/**
 * AN INVOICE THE HOUSEHOLD'S SAVED METHOD PAYS, on its own page, in place of the ways to pay, since
 * ops offers no bank and no card then (the seam, section 5). No artboard draws it (business issue
 * 13), so it is built from Payments' drawn parts. The seam's lines are word for word, with the
 * business's hours as ops's `businessHoursWords` writes them (`lib/dates.ts`); where they say "card"
 * and "Visa ending 4242", the method's `savedMethodNoun` and `savedMethodWords` stand (section 5 at
 * 0b51e49, Q3), so "Your card is saved." is "Your bank account is saved." for an account. Each says
 * only what ops has said: the charge waits on her answer, the pause is in place, or the charge is at
 * Stripe, only once ops's push or its receipt says so (CLAUDE.md binding rule 1).
 */
export const SAVED_METHOD_PAYS = {
  /** SEAM: said only while ops says a receipt is emailed when it is charged. */
  receipt: 'A receipt is emailed when it is charged.',
  /** SEAM: asks ops not to charge the saved method for this invoice (`invoice.pay_by_zelle`). */
  payingByZelle: "I'm paying by Zelle",
  /**
   * SEAM: once the office has lifted the pause. The phone follows it, and then `liftedAfterPhone`.
   */
  liftedBeforePhone:
    'The office lifted the pause on paying this one by Zelle. To pay it by Zelle after all, call or text the office at',
  /**
   * NEW: once the charge is at Stripe, for a Zelle already sent, which the seam asks for as "the
   * office's number for a Zelle already sent" and does not print. The phone follows it, and then a
   * full stop.
   */
  alreadySentBeforePhone: 'If you already sent it by Zelle, call or text the office at',
} as const;

/**
 * The saved method as a sentence names it, "your Visa ending 4242", in ops's words for it
 * (`savedMethodWords`): "your Link account" for Link, and "your saved payment method" for a type ops
 * does not know.
 */
export function yourMethod(label: string | undefined, methodType: string | undefined): string {
  return `your ${savedMethodWords(label, methodType)}`;
}

/** SEAM: "Your card is saved.", the method's noun in place of "card" (`savedMethodNoun`). */
export const methodSaved = (methodType: string | undefined) =>
  `Your ${savedMethodNoun(methodType)} is saved.`;

/**
 * SEAM: "We will charge your Visa ending 4242 after you tell us the clean went well, during business
 * hours: Monday to Saturday, 9:00 am to 6:00 pm.", while the charge waits on her answer. NEW: without
 * ops's hours whole, it ends at "during business hours.".
 */
export function weWillCharge(method: string, hours: string | null): string {
  const charge = `We will charge ${method} after you tell us the clean went well, during business hours`;
  return hours === null ? `${charge}.` : `${charge}: ${hours}.`;
}

/**
 * NEW, for the seam's "Thank you. We will charge your ..." after a good face: "Thank you. We will
 * charge your Visa ending 4242 during business hours: Monday to Saturday, 9:00 am to 6:00 pm.",
 * ending at "during business hours." without ops's hours whole.
 */
export function thanksWeWillCharge(method: string, hours: string | null): string {
  const charge = `Thank you. We will charge ${method} during business hours`;
  return hours === null ? `${charge}.` : `${charge}: ${hours}.`;
}

/**
 * NEW, on Payments, the card of an invoice the saved method pays, so she does not pay it again: the
 * seam's sentence on its own page (`weWillCharge`, l.731-733) shortened to fit a card, while the
 * charge waits on her answer: "We will charge your Visa ending 4242 after you tell us the clean went
 * well."
 */
export const weWillChargeOnceTold = (method: string) =>
  `We will charge ${method} after you tell us the clean went well.`;

/**
 * NEW, on the same card once a good face counts: what its own page says then (`thanksWeWillCharge`)
 * without the thanks and the hours, "We will charge your Visa ending 4242 during business hours."
 */
export const weWillChargeInHours = (method: string) =>
  `We will charge ${method} during business hours.`;

/** SEAM, after a lesser face: "Nothing is charged to your Visa ending 4242 until ...". */
export const nothingCharged = (method: string) =>
  `Nothing is charged to ${method} until what you told us is put right.`;

/**
 * SEAM: "Would you rather pay this one by Zelle? Tell us first, and we will not charge your card for
 * this invoice.", the method's noun in place of "card" (`savedMethodNoun`).
 */
export const ratherByZelle = (methodType: string | undefined) =>
  `Would you rather pay this one by Zelle? Tell us first, and we will not charge your ${savedMethodNoun(methodType)} for this invoice.`;

/**
 * SEAM: the end of the lifted pause's sentence, after the phone, "first, so your card is not charged
 * as well.", the method's noun in place of "card" (`savedMethodNoun`).
 */
export const liftedAfterPhone = (methodType: string | undefined) =>
  `first, so your ${savedMethodNoun(methodType)} is not charged as well.`;

/** SEAM: "We are charging your Visa ending 4242 for this invoice now.", once it is at Stripe. */
export const chargingNow = (method: string) => `We are charging ${method} for this invoice now.`;

/**
 * Where a skip or a move stands, on the visit it is about. A decision is never one of these: once
 * ops has decided, the card shows the office's own words, and nothing of home's.
 */
export const REQUEST = {
  /** SEAM (section 4, and the worked example): written, and not yet had by the office. */
  sent: 'Sent to the office.',
  /**
   * ARTBOARD (Schedule): the callout's second sentence, alone, for a skip somebody else on the
   * household asked for, a move by somebody the household does not name, and an edit of a home,
   * whoever asked for it.
   */
  waiting: 'Waiting for us to confirm.',
  /** FOR THE OWNER: her own move, with more than one day that would suit. */
  youMove: 'You asked to move this. Waiting for us to confirm.',
  /** FOR THE OWNER: her own skip, which the office has. */
  youSkip: 'You asked to skip this visit. Waiting for us to confirm.',
  /** FOR THE OWNER: a request ops cancelled because the visit could no longer be changed. */
  closed: 'This request was closed, because the visit can no longer be changed.',
  /**
   * FOR THE OWNER: a request ops refused because the visit was no longer open, and a call-us line.
   * The seam (section 4, `409 not_open`) gives the refusal's meaning and none of these words. The
   * phone follows it, and then a full stop.
   */
  closedBeforePhone:
    'This request was closed, because the visit can no longer be changed here. Please call',
  /** FOR THE OWNER: a request ops closed as withdrawn. */
  withdrawn: 'This request was withdrawn.',
  /**
   * FOR THE OWNER: somebody else's request, which ops cancelled because it revoked that person's
   * membership (`membership_revoked`). The seam (section 4, "A request nobody can decide any more")
   * says the client sees the cancelled line, so the ask never goes quietly dead, and gives none of
   * the words. Never said of her own, which is never true of her while she reads it.
   */
  revoked: 'This request was closed, because the person who asked is no longer on the household.',
} as const;

/** ARTBOARD (Schedule): the callout, for her own move with the one day that would suit. */
export const youAskedToMoveTo = (day: string) =>
  `You asked to move this to ${day}. Waiting for us to confirm.`;

/** FOR THE OWNER: a move somebody else on the household asked for, by the name the office keeps. */
export const theyAskedToMove = (name: string) =>
  `${name} asked to move this. Waiting for us to confirm.`;

/**
 * FOR THE OWNER, the whole line, the sentence and the call-us line after it: the seam (section 4,
 * `409 not_open`) gives only the meaning, that the visit is not planned or confirmed, and none of
 * the words. The skip or move sheet and the add-on sheet say it when the request door finds the
 * visit no longer open, which it does before anything is written, so it names no request; and the
 * same words, never others, when the door answers 404 for the visit, which it does alike for a
 * visit gone and one that is not hers to ask about (CLAUDE.md binding rule 2), since every outcome
 * is said, never silent (the owner, 2026-10-20). The phone follows it, and then a full stop.
 */
export const NOT_OPEN_BEFORE_PHONE = 'This visit can no longer be changed here. Please call';

/**
 * FOR THE OWNER: Schedule with nothing on it from today on, where no home it names has regular
 * cleans; since round six's slice S3, only there (`NOTHING_SET_YET`, `NOTHING_WHILE_PAUSED`).
 */
export const NOTHING_SCHEDULED = 'Nothing is on the schedule right now. Call us to book a visit.';

/**
 * NEW (round six, slice S3, `docs/seam.md` P84): Schedule with nothing on it from today on, in place
 * of `NOTHING_SCHEDULED`, where a home it names has regular cleans that are not paused today: ops
 * holds the schedule and has not yet written the next visit, as after a resume or just after
 * booking. Raised by the replay of the owner's household on 2026-10-18, which found "Call us to book
 * a visit." under "Every two weeks, on Fridays", as if something had broken.
 */
export const NOTHING_SET_YET =
  'Nothing is on the schedule right now. The next visit shows here once the office has set it.';

/**
 * NEW (round six, slice S3, P84): the same, where every home it names with regular cleans has them
 * paused today, as ops pushed the pause; the pause itself is said under each home's line
 * (`pauseSaid`). Never said from the household's own `status`, which decides nothing about the
 * regular cleans (ops's 11.6, question 1; the production-readiness review of 2026-10-18, P1).
 */
export const NOTHING_WHILE_PAUSED =
  'Nothing is on the schedule right now. Your regular cleans are paused.';

/**
 * ARTBOARD (Messages): the place, its composer and the office's signature. The artboard's subtitle,
 * "Every reply is written by a person.", is not here: a confirmation the office sends from a
 * template may make it untrue, and the owner decided on 2026-09-28 to keep it out. The name before
 * the signature, drawn as "Victoria, Faithfully Kept", is `signedBy`'s, on a copy of a message ops
 * names a person in the office for.
 */
export const MESSAGES = {
  heading: 'Messages',
  /**
   * SHIPPED (Payments' artboard, third frame, `PAYMENTS.nothingNow`), reused rather than reworded:
   * under the heading once the household has come down and nothing is in her thread, the state a
   * new client starts in, so Messages says it is empty as Payments and Schedule do.
   */
  nothingNow: PAYMENTS.nothingNow,
  /** The composer's field, as its placeholder and as the name it is read by. */
  write: 'Write to Faithfully Kept',
  /**
   * The name the composer's arrow button is read by, which the artboard draws with no words: the
   * word on Feedback's button that does the same.
   */
  send: 'Send',
  /**
   * Under each thing the office said with no person named for it: an answer to her message, and a
   * copy of one the system composed. The artboard's "Victoria, Faithfully Kept" without the name.
   */
  office: 'Faithfully Kept',
  /**
   * NEW: drawn as a link to Payments in place of ops's `INVOICE_LINK_WORDS` in what the office
   * said, for a person shown money (provisional P48), where the copy names no invoice home holds,
   * so the link goes to the list, where every invoice owed is. Raised by the owner's walk of
   * 2026-10-16, where "Tap the link to tell us" stood beside a link that was dead text.
   */
  invoiceLink: 'See it in Payments',
  /**
   * NEW (round six, slice S3, `docs/seam.md` P86): the same link, to the invoice's own page, where
   * the copy names the invoice it carries the link to (`invoice_id`, ops's section 11.4) and her
   * copy holds it, so one tap lands on that invoice, its "How did we do?" and how it stands now.
   * Raised by the replay of the owner's household on 2026-10-18, whose link opened a list of three
   * invoices of one day, all paid by then, none of them said to be the one.
   */
  invoiceItself: 'See the invoice',
  /**
   * NEW (slice M1 of the phase 1 plan, `docs/seam.md` P104): a link of its own under ops's words in
   * a copy of the office's text that names the clean it is about (`visit_id`, ops's section 11.4),
   * where Schedule draws that clean, which it opens on: the clean's card on the List, or the month
   * on its day. For anybody who reads the copy, since a member sees her cleans too.
   */
  seeTheClean: 'See this clean',
  /**
   * NEW (slice M2 of the phase 1 plan, `docs/seam.md` P105): under the subject of the copy of an
   * email the office sent her, once Microsoft took it, the address it went to as ops cut it
   * (`to_masked`, ops's section 11.1), "Email to k@example.com", so she can tell it from a text's
   * copy and find it in her own inbox. Drawn to the person it was for alone, and only where ops sent
   * the address; the subject over it and every word under it are ops's.
   */
  emailTo: (address: string) => `Email to ${address}`,
  /**
   * NEW (the owner's rule of 2026-10-20, never silent): said over the composer, her words kept,
   * when the request door answers 404 for her message, the household not hers to write about now;
   * the place is read again, which goes to the page that says to call once she has none. The phone
   * follows it, and then a full stop.
   */
  notSentHereBeforePhone: 'This message could not be sent here. Please call',
} as const;

/**
 * SEAM (section 3 at d1c7636, `message`, l.412-414): the words ops writes into a message's copy in
 * place of every link to its own invoice page, because the link's token is its whole credential and
 * "home shows the invoice itself". Messages draws each, for a person shown money, as
 * `MESSAGES.invoiceLink` (P48); for anybody else they stay as ops wrote them.
 */
export const INVOICE_LINK_WORDS = '[your invoice link]';

/**
 * ARTBOARD (Messages): "Victoria, Faithfully Kept", under a copy of a message the office sent her,
 * with the first name of the person at the office who approved it, else who wrote it, as ops names
 * her (`author_first_name`, the seam, l.382-384 at b727ec1). "Faithfully Kept" alone,
 * `MESSAGES.office`, when ops names nobody, for a message the system composed.
 */
export const signedBy = (author?: string) =>
  author === undefined ? MESSAGES.office : `${author}, ${MESSAGES.office}`;

/**
 * ARTBOARD (Feedback): the page a face on the Today card leads to. Its heading is Home's "How was
 * it?", `HOME.howWasIt`, and its close button the X named by `CLOSE`.
 */
export const FEEDBACK = {
  whichArea: 'Which area?',
  whatWasMissed: 'In one line, what was missed?',
  /**
   * Word for word one of the things `what-we-cannot-say.md` lists as true to say, for every face
   * but Very good, which asks nothing more. SHIPPED on an invoice's "How did we do?" too, for the
   * same faces (slice V4, ops #72). Drawn on both only on the clean's own day and the day after, in
   * Florida's days (`recleanPromised`, `lib/faces.ts`, round four, slice F1): the replay of the
   * owner's walk found it on an invoice a week after its clean, where it either tells her it is too
   * late or promises what the business's 24-hour rule does not cover. Past that the page reads
   * without it, the question and Send as drawn. The owner chose on 2026-10-22 this one sentence for
   * the website's policy, its pricing questions and home alike (site #25, item 4): the line
   * `what-we-cannot-say.md` lists as true, with every way a client can tell the business named.
   */
  noCharge:
    'Tell us within 24 hours, on your client page, by phone or by email, and we clean it again at no charge.',
  send: 'Send',
  /**
   * NEW (round six, slice S3, `docs/seam.md` P86): after an answer that named what was missed (an
   * area sent, `areaSent`), on Feedback after "Sent to the office." and on an invoice after the
   * thanks, where ops's words for the face do not already say it (`INVOICE_FEEDBACK.putRight`):
   * what happens next, since naming an area starts the office's follow-up, Good included (ops #72).
   * It promises the office's call and no more: never that a clean is booked, which is the office's
   * to say. Raised by the replay of the owner's household on 2026-10-18, where Good with the back
   * porch named read only "Thank you for telling us."
   */
  inTouch: 'The office will be in touch to put it right.',
} as const;

/**
 * ONE VISIT'S REPORT, at `/visits/<visit_id>` (home #21, ops #86), where the visit-done email's "See
 * the report and photos" leads: the page frame of States-HomeA "4. Photos from a visit", and the
 * report of States-HomeB "10. Your home is ready, on Messages", whose heading, day, note, "Cleaned
 * by", things noticed and 24-hour line are already in words above (`HOME.ready`, `HOME.wasCleaned`,
 * `cleanedBy`, `noticedLine`, `FEEDBACK.noCharge`). The photos' words below: artboard 4's, with its
 * "The links last 30 days." said as it is true here, and the lines for where each photo stands.
 * An ask for them again, once the door has it or while one waits, says `REQUEST.sent`, as Feedback
 * does, and never that the photos are coming back, which is the office's to say (CLAUDE.md binding
 * rule 1); a refusal the door gives that no line below names says `FAILED_BEFORE_PHONE` or
 * `tooManyBeforePhone`, as every ask does.
 */
export const VISIT_REPORT = {
  /** ARTBOARD (States-HomeA, 4): "Home", the way back above the heading, the nav's own name. */
  home: NAV.home,
  /**
   * NEW: the heading for a visit ops has not pushed done, and for a read that did not go through,
   * where "Your home is ready" would say what ops has not. It says nothing of where the visit
   * stands, which the line under its day says.
   */
  heading: 'Your visit',
  /**
   * NEW: under the day of a visit ops has not pushed done, unless ops says it is cancelled or the
   * cleaner could not get in, which `VISIT` says instead: that the report comes after the clean.
   * It names no time and no words of the report, which are the office's to write.
   */
  afterTheClean: 'The report comes after the clean.',
  /**
   * NEW: for a visit the Worker does not find for her, such as one at a home that is not hers or
   * one not yet on her copy, before the phone, so a link from an email never moves her silently
   * (the owner, 2026-10-20: every outcome said). Home, above it, says where she stands.
   */
  notFound: 'We could not find this visit on your page. For help, call',
  /** ARTBOARD (States-HomeB, 10): the question over the four faces. */
  howDidWeDo: 'How did we do?',
  /**
   * SEAM (ops's `docs/seams/home.md` section 14, the photo report inside message 11; the business's
   * `docs/client-messages.md`, message 11, "When someone has already answered for the visit"): in
   * place of the four faces once her answer about the clean stands, as Home reads it (sent from
   * home on the clean or on its invoice, and recorded where ops's receipt says), word for word as
   * the visit-done email says it. It says what she did, and nothing of what the office will do.
   */
  thanks: 'Thank you for telling us how it went.',
  /**
   * ARTBOARD (States-HomeA, 4), its first sentence, and NEW, its second (home #21): under the photos
   * once every one is held, the business's day the first of their links runs out in place of the
   * artboard's "The links last 30 days.", which is not true here: ops's links last seven days from
   * the compose, and home holds what it read ten minutes, reading it again while a link holds.
   */
  forYouUntil: (day: string) =>
    `These photos are for you only. You can see them here until ${day}.`,
  /** ARTBOARD (States-HomeA, 4): its first sentence alone, where no photo says when its link ends. */
  forYou: 'These photos are for you only.',
  /**
   * NEW (home #21): under the photos while home's server is still reading one, which the page reads
   * itself again for. It says they are on their way to this page, and nothing of the office.
   */
  onTheirWay: 'The photos are on their way.',
  /**
   * NEW (home #21): under the photos where one did not load, as home's server read it or as the
   * browser drew it, or still had not come after the page read itself again for a while: an open of
   * the page later reads it again. The phone follows it, and then a full stop.
   */
  notLoadedBeforePhone: 'Some photos did not load. Open this page again in a few minutes, or call',
  /** NEW (home #21): on the tile of a photo that did not load, under its line. */
  didNotLoad: 'Did not load',
  /**
   * NEW (home #21): what a photo with no line of its own is read by, its place among the photos
   * drawn and the clean it is of, as "Photo 2 of 4 from your clean".
   */
  photoOf: (place: number, count: number) => `Photo ${place} of ${count} from your clean`,
  /**
   * ARTBOARD (States-HomeA, 4): the expired card, the visit's day in place of the artboard's
   * "September 29", as the page writes a day, "Tuesday 13 October".
   */
  expiredFrom: (day: string) => `The photos from ${day} have expired.`,
  /** NEW (home #21): the expired card of a visit with no day. */
  expired: 'These photos have expired.',
  /**
   * NEW (home #21): artboard 4's card where every photo of the clean is no longer on her page, ops
   * having taken them back or home being unable to read them (the Worker's `photosGone`), the
   * visit's day as the expired card writes it, rather than nothing said until their links run out.
   * It says what she sees, and nothing of why or of what the office will do.
   */
  goneFrom: (day: string) => `The photos from ${day} are not on your page any more.`,
  /** NEW (home #21): the same card for a visit with no day. */
  gone: 'These photos are not on your page any more.',
  /** ARTBOARD (States-HomeA, 4): the expired card's button, which asks for them again. */
  askAgain: 'Ask for them again',
  /**
   * NEW (home #21): in place of "Ask for them again" where the request door holds one already for
   * this clean (`409 already_asked`), anybody's on the household, since ops last brought the
   * photos: it holds one at a time, by home's own rule, so asking again is refused again. It says
   * the office has it, and nothing of what the office will do. The phone follows it, and then a
   * full stop.
   */
  askedAlreadyBeforePhone:
    'The office already has an ask for these photos, and has not answered it yet. To ask about it, please call',
  /**
   * NEW (home #21): in place of "Ask for them again" where the request door finds no link of the
   * clean's run out (`409 not_expired`), as when ops brought the photos again after the page was
   * read, so opening it again draws what stands now, as an invoice that changed under a press says
   * (`PAUSE_REFUSAL_WORDS.charge_changed`). The phone follows it, and then a full stop.
   */
  changedBeforePhone:
    'These photos changed while this was being sent. Open this page again, or call',
} as const;

/**
 * SEAM (ops's `docs/seams/home.md`, section 11.6 at 430c053, question 7, ops #72): the `area` sent
 * with her line, under the Other chip or with no chip chosen, "home sends `area` as `Other` with her
 * line, and the issue reads "Other: <her words>"". Section 4 at 9f10a8b says why: "An area is named
 * when `area` has words once trimmed: a `line` alone names none". Never drawn: the chip is drawn by
 * its label, which is the artboard's same word.
 */
export const OTHER_AREA = 'Other';

/**
 * ARTBOARD (Feedback): the areas, in the order drawn, each with the `area` it sends up. Any answer
 * that names an area starts the re-clean, whatever its face, as the owner decided on 2026-10-16 (ops
 * #72) and ops's contract holds at 9f10a8b (section 4). "Other" sends none of its own, as the owner
 * accepted on 2026-09-28: her line names it, as `OTHER_AREA` (section 11.6, question 7), under
 * Other or with no chip chosen, where until round four it sent none, so Good with Other and her line
 * started no re-clean though the page had just promised one (`areaSent`, `lib/faces.ts`). With
 * nothing written it still names none, since question 7 reads "Other" with a written line and no
 * more, and ops would open a missed-area issue that says nothing.
 */
export const AREAS: readonly { readonly label: string; readonly area: string | null }[] = [
  { label: 'Kitchen', area: 'Kitchen' },
  { label: 'Bathrooms', area: 'Bathrooms' },
  { label: 'Bedrooms', area: 'Bedrooms' },
  { label: 'Floors', area: 'Floors' },
  { label: 'Other', area: null },
];

/**
 * SEAM (section 4, `422 looks_like_a_code`): a line she wrote that looks like it holds a code, which
 * the request door refuses before anything is stored or sent up. The seam writes it in one run,
 * "please do not put a code here, call us and we will keep it safely"; here it is two sentences.
 */
export const LOOKS_LIKE_A_CODE =
  'Please do not put a code here. Call us and we will keep it safely.';

/**
 * FOR THE OWNER: the name a close button is read by. The button is the X drawn on Feedback, put on
 * every sheet too (`components/Sheet.tsx`), because a sheet with no way out but a tap outside it
 * traps anybody who cannot see where outside is. Since the owner's walk of 2026-10-16 it is also
 * the words of the button that closes the panel over the screen once a press for Stripe's page has
 * ended (`components/StripeWay.tsx`), which a tap outside never closes.
 */
export const CLOSE = 'Close';

/**
 * NEW: Messages, in place of the composer, while the household's last message is still open with
 * the office, whoever on it wrote it. Ops takes one open message a household at a time, counted
 * across every person on it, and refuses a second (the seam, section 4 at 0b51e49, "The caps, held
 * in the write", home's Q5), so asking again would not go through. It says "call" alone, as every
 * line here does: the link is the phone's, and texts wait on the carrier filing. The phone follows
 * it, and then a full stop.
 */
export const MESSAGE_OPEN_BEFORE_PHONE =
  'The last message from your household is still open with the office. To add to it, please call';

/**
 * SEAM (section 4 at 0b51e49, "The caps, held in the write", `alreadyAskedWords`, home's Q5): an ask
 * refused because one like it, anybody's on the household, still waits on the office, with the
 * first's `request_id` in place of the contract's `<request_id>`, as the request door names it.
 * Messages says it where the composer was, above `MESSAGE_OPEN_BEFORE_PHONE`, when the door
 * refuses her message so.
 */
export const alreadyAskedWords = (requestId: string) =>
  `The office already has an ask like this one and has not answered it yet. Its id is ${requestId}.`;

/**
 * SHIPPED (`screens/SignIn.tsx`): a place that could not be read. The phone follows it, and then a
 * full stop.
 */
export const FAILED_BEFORE_PHONE = 'That did not go through. Please try again, or call';

/**
 * NEW (home #23, ops #86 part 3): an answer about a clean refused because ops did not ask her about
 * it (`403 not_asked`), by the request door from the copy or by ops once the door had it. Said where
 * `FAILED_BEFORE_PHONE` would be, on Feedback, an invoice's "How did we do?", Home's last clean and
 * a visit's report, so the refusal is never silent. It says only that she was not asked, which is
 * true whether ops asked somebody else or nobody at all (`[]` on a void invoice, a household with
 * no visit contact), never who was, and offers the phone for what she wanted to say. The phone
 * follows it, and then a full stop.
 */
export const NOT_ASKED_BEFORE_PHONE =
  'The office did not ask you about this clean. To tell us how it went, please call';

/**
 * NEW: an ask the request door refused, writing nothing and sending nothing, because she or her
 * household has asked a lot in a short while (`429 too_many`, the door's own limits, `DOOR_LIMITS`
 * in `api/src/seam/requests.ts`). Said where `FAILED_BEFORE_PHONE` would be, by every place that
 * sends an ask. It says nothing went, that a later try may, and nothing of the numbers. The phone
 * follows it, and then a full stop.
 */
export const TOO_MANY_BEFORE_PHONE =
  'This did not go through, because your household has sent us a lot recently. Please try again later, or call';

/**
 * NEW (round six, slice S1): the door's own limits said with whose count it is and when a try may
 * go again (`TooMany`, as the Worker's `429` says it), in place of `TOO_MANY_BEFORE_PHONE` wherever
 * that is said. The replay of 2026-10-18 read "your household" and "later" under her Stop, where the
 * count was her own and "later" was an hour. Hers: "This did not go through, because you have sent
 * us a lot recently. Please try again after 7:06 pm, or call"; the household's: "... because your
 * household has sent us a lot recently. ..."; the time on the twelve-hour clock the business writes
 * its hours in (`twelveHourTime`), with " tomorrow" after it where it falls then, and "later" where
 * none is given, which for the household is `TOO_MANY_BEFORE_PHONE` word for word. A Worker that
 * says no more gets that line too. Nothing of the numbers. The phone follows it, then a full stop.
 */
export function tooManyBeforePhone(limit: TooMany | undefined): string {
  if (limit === undefined) return TOO_MANY_BEFORE_PHONE;
  const time = limit.after === null ? null : twelveHourTime(limit.after.time);
  if (limit.whose === 'household' && time === null) return TOO_MANY_BEFORE_PHONE;
  const who = limit.whose === 'you' ? 'you have' : 'your household has';
  const when =
    time === null ? 'later' : `after ${time}${limit.after?.tomorrow === true ? ' tomorrow' : ''}`;
  return `This did not go through, because ${who} sent us a lot recently. Please try again ${when}, or call`;
}

/**
 * NEW: Messages, under a copy of a message the office sent her that ops says `failed` (the seam,
 * section 3 at d1c7636, `message`, l.408-409). It says only that it did not arrive: never why, and
 * no promise of another, since what follows is the office's to decide. Drawn for `failed` alone,
 * so no screen says a message was delivered.
 */
export const NOT_REACHED = 'This message did not reach you when we sent it.';

/**
 * SHIPPED (`screens/Holding.tsx`, `screens/NoMembership.tsx`), and at the foot of Account, where it
 * moved from Home's foot (slice A1).
 */
export const SIGN_OUT = 'Sign out';

/**
 * ACCOUNT (slice A1): the page about her, which the header's Account button leads to from every
 * signed-in screen, at `/account`, with its two pages, How you pay at `/account/paying` and People
 * and sign-in at `/account/people`. No artboard in `design/home` draws it yet: it is built from the
 * three drawn in the Settings canvas in today's look (Settings-Main, Settings-Paying and
 * Settings-People), and goes on business issue 13's list. So every line here is NEW, for the owner
 * to read before clients are invited, but where it says otherwise. Home's own lines say "call"
 * alone, as every line of home's own does, and each that ends in the office's number is followed by
 * it and then a full stop. Nothing on it switches: everything she would change is the office's to
 * decide, and until ops has a request for it, the page says to call (binding rule 1).
 */
export const ACCOUNT = {
  /**
   * NEW: the header's button, left of Call, and the eyebrow over each Account page's heading. Not
   * "Settings", which promises switches, and nothing here switches.
   */
  name: 'Account',
  /** NEW: the mark and the name in the header, a link to Home, as a screen reader names it. */
  brandHome: 'Faithfully Kept, Home',
  /** NEW: the heading, when ops sent no name for her. */
  yourAccount: 'Your account',
  /** NEW: the row for the address she signed in with. */
  email: 'Email',
  /** NEW: the row for her number. */
  mobile: 'Mobile',
  /**
   * NEW: under her details, where no ask can be made here: the Worker offers none, as before ops
   * grants her (round six, slice S2). The phone follows it, and then a full stop.
   */
  changeBeforePhone: 'To change your name, email or mobile, please call',
  /**
   * NEW (round six, slice S2): under her details, where the Worker offers her ask to change them
   * (ops's section 12.4, `person.contact_change`). "People and sign-in", or "Sign-in" for a member,
   * follows it as a link to that page, and then a full stop: "To change your name, email or mobile,
   * ask in People and sign-in." The office's number is not in this line: a second link a line away
   * would reach over this one's box at a phone's width and take its taps, and Call is in the
   * header and in Help on the same page.
   */
  changeAskIn: 'To change your name, email or mobile, ask in',
  /** NEW: a member's second sentence under the heading, since a member is shown no money. */
  noInvoices: 'Invoices are not shown on your page.',
  /** NEW: the row that leads to How you pay, a client's alone, and that page's heading. */
  howYouPay: 'How you pay',
  /**
   * NEW: under How you pay, when ops says nothing is saved to charge automatically; and, since slice
   * A2, first in How you pay's card and in Payments' card where the saved method would be.
   */
  youPayEachInvoice: 'You pay each invoice yourself.',
  /** NEW: the row that leads to the household's people and her sign-in, for a client. */
  peopleAndSignIn: 'People and sign-in',
  /** NEW: the same row for a member, who is shown nobody else on the household. */
  signIn: 'Sign-in',
  /** NEW: she is the only person on the household. */
  onlyYou: 'Only you.',
  /** NEW: her sign-in is live in this browser alone. */
  signedInHereOnly: 'Signed in here only.',
  /** NEW: the heading over each home's standing schedule. */
  regularCleans: 'Your regular cleans',
  /** NEW: a home with no standing schedule, or a one-time one. */
  noRegularCleans: 'No regular cleans.',
  /**
   * NEW: under the regular cleans, for a client, where no home offers an ask about them (round
   * five, slice C3: `CLEANS.alsoByPhoneBeforePhone` where one does), so the office does it. The
   * phone follows it, and then a full stop.
   */
  changeCleansBeforePhone: 'To pause, change or end your regular cleans, please call',
  /** NEW: the heading over how to reach the office. */
  help: 'Help',
  /** SHIPPED (`screens/Holding.tsx`): the office's number on a button of its own. */
  call: `Call ${PHONE_SHOWN}`,
  /** NEW: the office's address, as a link that writes to it. */
  emailUs: `Email ${EMAIL_SHOWN}`,
  /** SHIPPED (`components/Footer.tsx`): the website's privacy page. */
  privacy: 'Privacy',
  /** SHIPPED (`components/Footer.tsx`): the website's terms page. */
  terms: 'Terms',
  /** NEW: the website's own name for its cancellation and re-clean policy page. */
  policy: 'Cancellation and re-clean policy',
  /** NEW: the way back from How you pay and from People and sign-in. */
  backToAccount: 'Back to Account',
} as const;

/**
 * WHAT WE SEND YOU (ops's `docs/seams/home.md` section 11.3, the draft ops builds with its phase 2;
 * home's slices H5 and H7, `docs/seam.md` P114): Account's page of her choices of what we send her,
 * at `/account/sending`, offered only once ops has pushed her choices. No artboard draws it yet
 * (business issue 13's `design/home/Settings.dc.html`, the proposal's B2): it is built from the page
 * home's proposal sketches (`docs/proposal-messages-and-documents.md`, section 4.2), in today's look.
 * Every line here is NEW, for the owner to read before clients are invited, and says where its words
 * come from: the proposal's (sections 2.2, 2.5, 4.2 and 4.4, and the addendum's A1 and A2) where it
 * gives them, 11.3's own where it does, and home's own otherwise. The page says "emails" and
 * "texts", never anything else for them. Each line that ends in the office's number is followed by
 * it and then a full stop. Nothing here says a choice is made before ops's push says so (CLAUDE.md
 * binding rule 1).
 */
export const SENDING = {
  /** NEW (the proposal, 4.1): Account's row that leads to the page, and the page's heading. */
  heading: 'What we send you',
  /** NEW (home's own): under Account's row. */
  row: 'Choose which emails we send you.',
  /**
   * NEW (the proposal, 4.2's last line of its heading, "Choose what you would like, and how.", made
   * to say what the page does, since texts are one switch): under the heading.
   */
  intro:
    'Choose which emails we send you. A change goes to the office, and shows here once the office has saved it.',
  /** NEW (home's own): over her choices for every home, where ops sent choices for one home too. */
  everyHome: 'All your homes',
  /** NEW (the proposal, 4.2): what each kind holds, by 11.3's category. */
  titles: {
    visit_changes: 'Changes to your visits',
    visit_reminders: 'Reminders',
    visit_day: 'Your home is ready',
    home_checks: 'Home check reports',
    invoices: 'Invoices',
    receipts: 'Receipts',
  },
  /** NEW (the proposal, 4.2): what each kind holds, under its title. Reminders say their days. */
  about: {
    visit_changes:
      'When a clean is booked, moved, skipped or cancelled, when a different cleaner is coming, and if we are running late or cannot get in.',
    visit_day:
      'On the day of each clean, the note the cleaner leaves, and our question: how did we do?',
    home_checks: 'After each check of your home, what we saw.',
    invoices: 'Each invoice with its PDF, and anything about paying it.',
    receipts: 'A receipt for every payment, with its PDF.',
  },
  /** NEW (home's own): the switch's visible word, beside whether it is on or off. */
  email: 'Email',
  on: 'On',
  off: 'Off',
  /** NEW (home's own): a kind ops sends her by text alone, where email is not offered for it. */
  notByEmail: 'Not by email.',
  /** NEW (the proposal, 4.2): under a kind that reaches her by text too. */
  byText: 'Also by text while your texts are on.',
  /** NEW (the proposal, 4.2): under a kind locked one way, email or texts (11.3's `one_way`). */
  oneWay: 'One way stays on, so you always hear about a change.',
  /** NEW (the proposal, 4.2): under invoices locked to email (11.3's `email`), held on. */
  invoicesLocked: 'Always on, because invoices for your household come to you.',
  /** NEW (home's own, after the proposal's line for invoices): changes locked to email, held on. */
  changesLocked: "Always on, because changes to your household's visits come to you.",
  /**
   * NEW (home's own): under any other kind held on by its lock. The phone follows it, and then a
   * full stop.
   */
  lockedBeforePhone: 'This stays on for you. To change it, please call',
  /** NEW (ops's section 11.3, word for word, "One guard, not a removal"). */
  officeCalls: 'With email and texts off for this, the office will call you.',
  /** NEW (home's own): the reminders' row, its fields and its button. */
  when: 'When',
  timeOfDay: 'Time of day',
  changeReminders: 'Change reminders',
  /** NEW (home's own): how many days before each clean a reminder may go, as 11.3's 0 to 3. */
  leadDays: ['On the day', 'The day before', 'Two days before', 'Three days before'],
  /** NEW (home's own): the time of day a reminder may go at, as 11.3's three words. */
  sendTimes: { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' },
  /** NEW (the proposal, 4.4): under a switch while its change has not yet reached the office. */
  waiting: 'Waiting to reach the office.',
  /** NEW (home's own): under the reminders while a change of their days or time is on its way. */
  changingReminders: 'Changing when reminders come.',
  /**
   * NEW (the proposal, 4.4): said once ops's push shows the change she asked for, and only then:
   * for a kind, its email as she asked, and for reminders their days and time of day too; for her
   * texts, once they are no longer on.
   */
  saved: 'Saved.',
  /**
   * NEW (home's own): ops's push answered her change with the kind as it was, the office having
   * kept it. Under the switch and in the page's status region. The phone follows it, and then a
   * full stop.
   */
  keptBeforePhone: 'The office kept this as it was. To ask about it, please call',
  /**
   * NEW (home's own): her texts' turning off came back with them still on, the office having kept
   * them, or ops saying nothing stood to stop. The phone follows it, and then a full stop.
   */
  textsKeptBeforePhone: 'The office kept your texts as they were. To ask about them, please call',
  /**
   * NEW (home's own): her change is on its way, and the page's read after it did not go through,
   * so the switch stays held rather than offered again. The phone follows it, and then a full stop.
   */
  unreadBeforePhone:
    'Your change is on its way, but this page could not be updated just now. It will try again, or you can call',
  /**
   * NEW (the proposal, 4.4, its words for `locked`, made to fit every kind): ops refused turning
   * one off, `409 locked`. The phone follows it, and then a full stop.
   */
  refusedLockedBeforePhone:
    'This stays on, because one way to reach you about it stays open. To change who gets it, please call',
  /** NEW (home's own): ops refused it as not offered her, `409 not_offered`. Then the phone. */
  refusedNotOfferedBeforePhone: 'That cannot be changed here. Please call',
  /**
   * NEW (the proposal, 4.4, "Refused, anything else"): any other refusal, or a change home gave up
   * sending. The phone follows it, and then a full stop.
   */
  refusedBeforePhone: 'That did not save. Please try again, or call',
  /** NEW (home's own): the always-on emails, for a client. */
  alwaysClient:
    'Some emails always come to you, whatever you choose here: the sign-in codes you ask for by email, and a note when someone new can see your home page or when how you pay changes.',
  /** NEW (home's own): the always-on emails, for a member. */
  alwaysMember:
    'The sign-in codes you ask for by email always come to you, whatever you choose here.',
  /**
   * NEW (the proposal, 4.4): her email has no address on file, a mark holds it, or it is not
   * arriving. Each is followed by the phone, and then a full stop. A mark is 11.3's `marked`, any
   * do-not-contact mark on her address or her person, so it never says she asked for it.
   */
  noEmailBeforePhone: 'We have no email address for you. To add one, please call',
  emailMarkedBeforePhone: 'Emails to you are off. To turn them back on, please call',
  /**
   * NEW (home's own, after ops #76 of 2026-10-23, `email.state` `no_consent`, "an address on file
   * with no yes to email on the register"): no email on this page goes to her until she says yes,
   * and the way to say it. The phone follows it, and then a full stop. "On this page", since the
   * always-on line below still holds: the sign-in codes she asks for come all the same.
   */
  emailNoConsentBeforePhone:
    'The emails on this page need your yes before we send them. To give it, please call',
  /**
   * NEW (the proposal, 4.4): kept for a word ops sends in no build yet (`bouncing`, ops #76 of
   * 2026-10-23, since ops keeps no bounce register), said plainly should it ever come.
   */
  emailBouncingBeforePhone:
    'Our emails to you are not arriving. To give us the right address, please call',
  /** NEW (the proposal, 4.2): the texts' heading. */
  texts: 'Texts',
  /** NEW (home's own): her texts are on. */
  textsOn: 'Your texts are on.',
  /**
   * NEW (the proposal, 4.2): how texts come back, said before she turns them off. Texts she turns
   * off here are off by her choice, which the website's box turns back on (`turn_on_by`
   * `website_box`, ops #76 of 2026-10-23), so it is said whatever ops names while they are on.
   */
  textsStayOffUntilTicked:
    'If you turn texts off, they stay off until you tick the box on our quote form again.',
  /** NEW (the proposal, 4.2). */
  replyStop: 'Reply STOP to any text to stop them all.',
  /** NEW (home's own): the button that turns every text off, and its confirmation. */
  turnOffTexts: 'Turn off texts',
  turnOffEveryText: 'Turn off every text from us?',
  keepTexts: 'Keep texts',
  /** NEW (home's own): while her texts' turning off has not yet reached the office. */
  turningOffTexts: 'Turning off your texts.',
  /** NEW (the proposal, 4.4): her texts, off by her choice. */
  textsOff: 'Texts are off.',
  /**
   * NEW (the proposal, 4.4): the way back, the website's own box by its own words. The phone
   * follows it, and then a full stop.
   */
  tickTheBoxBeforePhone:
    "To turn them back on, tick 'Text me about my cleaning visits.' on our quote form at faithfullykept.com/book/, or call",
  /** NEW (home's own): the way back where no box is named. The phone follows it, then a stop. */
  textsBackBeforePhone: 'To turn them back on, please call',
  /**
   * NEW (the proposal, 4.4): texts off by a mark the office recorded, 11.3's `marked`, any
   * do-not-contact mark, so it never says she asked for it. Then the phone.
   */
  textsMarkedBeforePhone: 'Texts to you are off. To turn them back on, please call',
  /** NEW (the proposal, 4.4): no texting consent. */
  noTexts: 'We do not text you.',
  /**
   * NEW (the proposal, 4.4, "No texting consent, today"): the way to opt in, the website's own box
   * by its own words. The phone follows it, and then a full stop.
   */
  optInBeforePhone:
    "To get texts about your visits, tick 'Text me about my cleaning visits.' on our quote form at faithfullykept.com/book/, with your mobile number. It does not book anything. Or call",
  /** NEW (home's own): no texting consent, and no box named. The phone follows it, then a stop. */
  askForTextsBeforePhone: 'To get texts about your visits, please call',
  /** NEW (the proposal, 4.4): no mobile number on file. */
  noMobile: 'We have no mobile number for you.',
  /** NEW (home's own): after `noMobile`. The phone follows it, and then a full stop. */
  addMobileBeforePhone: 'To add one, please call',
} as const;

/**
 * NEW (the proposal, 2.7 and 4.4; ops #76 of 2026-10-23, `turn_on_by` `keyword`, "their START
 * lifting their own STOP"): the way back where it is her START, to the business line her texts
 * come from, `PHONE_SHOWN` of `contact.ts`, drawn as a link (`WithPhone`).
 */
export const TEXT_START = `To turn them back on, text START to ${PHONE_SHOWN}.`;

/**
 * NEW (the proposal, 2.7 and 4.4): her texts, off because she replied STOP, and the one way back,
 * her START (`TEXT_START`). The office's number is in it, drawn as a link (`WithPhone`).
 */
export const TEXTS_OFF_BY_STOP = `Texts are off, because you replied STOP. ${TEXT_START}`;

/** The words for a reminder's days before, by 11.3's 0 to 3, as the reminders' line says them. */
const LEAD_LINES = [
  'On the day of each clean',
  'The day before each clean',
  'Two days before each clean',
  'Three days before each clean',
] as const;

/**
 * NEW (the proposal, 4.2, "Two days before each clean."): when her reminders come, by the days
 * before and the time of day ops holds, "Two days before each clean, in the morning."; null where
 * ops says neither in a way home knows.
 */
export function remindersWhen(
  leadDays: number | undefined,
  sendTime: string | undefined,
): string | null {
  const lead = leadDays === undefined ? undefined : LEAD_LINES[leadDays];
  const time =
    sendTime === 'morning' || sendTime === 'afternoon' || sendTime === 'evening'
      ? `in the ${sendTime}`
      : undefined;
  if (lead === undefined)
    return time === undefined ? null : `${time[0]!.toUpperCase()}${time.slice(1)}.`;
  return time === undefined ? `${lead}.` : `${lead}, ${time}.`;
}

/**
 * NEW (the proposal, 2.5 and 4.4, "Turning off reminders by email."): under a switch while its
 * change has not yet been tried, "Turning off email for Reminders."
 */
export const turningEmail = (choice: 'on' | 'off', title: string) =>
  `Turning ${choice} email for ${title}.`;

/** NEW (the proposal, 4.2, "Email for Changes to your visits"): a switch's name, read aloud. */
export const emailFor = (title: string) => `Email for ${title}`;

/**
 * NEW (the addendum's A2, "The office has paused reminders for this home until 15 March."): the
 * office's hold on one kind, read-only, its days as 11.3 gives them, "The office has paused these
 * from 10 Jan to 15 Mar."; null where ops gives neither day.
 */
export function heldLine(from: string | undefined, until: string | undefined): string | null {
  const start = from === undefined ? null : shortDate(from);
  const end = until === undefined ? null : shortDate(until);
  if (start !== null && end !== null) return `The office has paused these from ${start} to ${end}.`;
  if (end !== null) return `The office has paused these until ${end}.`;
  if (start !== null) return `The office has paused these from ${start}.`;
  return null;
}

/**
 * NEW (the proposal, 2.6, "The office changed this on October 21, when you asked on a call."): who
 * last changed a kind, where it was the office on her word, by 11.3's `how`, "The office changed
 * this on 21 Oct, when you asked on a call."; and the proposal's 2.2 line where the system turned
 * an email back on because her texts are off (`lock`). NEW (home's own, after ops #76 of
 * 2026-10-23, a row "changed by a STOP carries `changed: { by: "self", how: "stop" }`"): her own
 * STOP, "You turned off texts on 20 Oct by replying STOP.", as `TEXTS_OFF_BY_STOP` says it. Null
 * for any other change of her own, and for anything home has no words for.
 */
export function changedLine(changed: {
  readonly on?: string;
  readonly by?: string;
  readonly how?: string;
}): string | null {
  if (changed.by === 'system' && changed.how === 'lock') {
    return 'Email for this is back on, because texts to you are off. One way stays on, so you always hear about a change.';
  }
  if (changed.by === 'self' && changed.how === 'stop') {
    const day = changed.on === undefined ? null : shortDate(changed.on);
    return day === null
      ? 'You turned off texts by replying STOP.'
      : `You turned off texts on ${day} by replying STOP.`;
  }
  if (changed.by !== 'office') return null;
  const day = changed.on === undefined ? null : shortDate(changed.on);
  const said = day === null ? 'The office changed this' : `The office changed this on ${day}`;
  const asked: Readonly<Record<string, string>> = {
    call: 'when you asked on a call',
    email: 'when you asked by email',
    text: 'when you asked by text',
    in_person: 'when you asked in person',
  };
  const how = changed.how === undefined ? undefined : asked[changed.how];
  return how === undefined ? `${said}.` : `${said}, ${how}.`;
}

/**
 * NEW (the addendum's A1, "Pause messages from one day to another"): her pause while it has not
 * ended, "Messages are paused from 18 Oct until 25 Oct.", and for one home "Messages for Pool house
 * are paused ...". Nothing about what still reaches her meanwhile, which 11.3 does not say.
 */
export function pausedLine(paused: {
  readonly from?: string;
  readonly until?: string;
  readonly home?: string;
}): string {
  const start = paused.from === undefined ? null : shortDate(paused.from);
  const end = paused.until === undefined ? null : shortDate(paused.until);
  const what = paused.home === undefined ? 'Messages are' : `Messages for ${paused.home} are`;
  const days =
    start !== null && end !== null
      ? `from ${start} until ${end}`
      : end !== null
        ? `until ${end}`
        : `from ${start ?? 'now'}`;
  return `${what} paused ${days}.`;
}

/**
 * NEW (home's own, after 11.3's `quiet_hours`, a window inside 8 am to 8 pm, read as the hours a
 * text may come in): "We text you only between 9:00 am and 5:00 pm."; null where either is not a
 * time.
 */
export function textHoursLine(from: string, until: string): string | null {
  const start = twelveHourTime(from);
  const end = twelveHourTime(until);
  return start === null || end === null ? null : `We text you only between ${start} and ${end}.`;
}

/**
 * NEW (the addendum's A2, "the choice between each message and a daily summary, with its time"):
 * how her messages come, each as it happens or gathered once a day.
 */
export function summaryLine(every: 'each' | 'daily', time: string | undefined): string {
  if (every === 'each') return 'We send each message as it happens.';
  const at = time === undefined ? null : twelveHourTime(time);
  return at === null
    ? 'We gather them into one summary a day.'
    : `We gather them into one summary a day, at ${at}.`;
}

/** NEW: her number on file by its last four digits, "ending 0142", beside "Mobile". */
export const mobileEnding = (lastFour: string) => `ending ${lastFour}`;

/**
 * NEW: how many phones and computers her sign-in is live on, more than one, as the nav says a count
 * (`countInWords`): "Signed in on 2 phones or computers.", or "on more than 9".
 */
export const signedInOn = (count: string) => `Signed in on ${count} phones or computers.`;

/** Words in a list, as a sentence lists them: "A", "A and B", "A, B and C". */
function listed(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)!}`;
}

/**
 * NEW: the household's people by their first names, three at most, hers first, and how many more:
 * "Rosa, Daniel and Ellen.", "Rosa, Daniel, Ellen and 2 more.". Null with nobody to name.
 */
export function peopleNamed(names: readonly string[], more: number): string | null {
  if (names.length === 0) return null;
  return `${listed(more > 0 ? [...names, `${more} more`] : names)}.`;
}

/**
 * NEW: a member's first sentence under the heading: "You can see visits and messages for Palm Way,
 * and write to us.", her homes by their labels, or "You can see visits and messages, and write to
 * us." for a member who sees every home. `ACCOUNT.noInvoices` follows it.
 */
export function memberSees(homes?: readonly string[]): string {
  const where = homes === undefined || homes.length === 0 ? '' : ` for ${listed(homes)}`;
  return `You can see visits and messages${where}, and write to us.`;
}

/**
 * NEW: a home's standing schedule on Account, Schedule's heading (`cadenceLine`) as a sentence,
 * "Every two weeks, on Fridays.". Never the window: with no word of arriving, two times on a page
 * about the standing schedule would read as when a clean starts and finishes.
 */
export const regularly = (cadence: string) => `${cadence}.`;

/**
 * NEW: a pause of a home's regular cleans that has not ended, the days in words with no year:
 * "Paused from 1 November until 15 March.", or "Paused from 1 November." with no end from ops.
 */
export const pausedFrom = (from: string, until: string | null) =>
  until === null ? `Paused from ${from}.` : `Paused from ${from} until ${until}.`;

/**
 * PEOPLE AND SIGN-IN (slice A3), Account's page at `/account/people`, built from the Settings
 * canvas's Settings-People artboard in today's look, with the asks ops's section 12.4 made live on
 * both hosts on 2026-10-17 in place of the artboard's "please call" line: a person to add (section
 * 4, `client` or `member`), a person taken off (`person.remove`) or seeing other homes
 * (`person.change`), and her own name, email or mobile changed (`person.contact_change`) or her own
 * door ended (`person.leave`), each the office's to decide, after a fresh code. Every line here is
 * NEW, for the owner to read before clients are invited, but where it says otherwise. Each says what
 * she asked and where ops has it, never that it was done (CLAUDE.md binding rule 1); a person is
 * said by name, ops's word for her and what her door lets her do, never by an address, a number or
 * an id; and a phone or computer by words from a fixed list, never what its browser sent.
 */
export const PEOPLE = {
  /** NEW: the heading over the household's people, a client's alone. */
  household: 'Your household',
  /** NEW: a person ops granted a door. */
  canSignIn: 'Can sign in here.',
  /** NEW: a person with no door ops invited or granted. */
  notInvited: 'Not invited to this page.',
  /** NEW: on another person's row, a client's ask to change which homes the person sees. */
  changeWhatTheySee: 'Ask to change what they see',
  /** NEW: on another person's row, a client's ask to take the person off the household. */
  takeThemOff: 'Ask to take them off',
  /** NEW: on her own row, her ask to change her own name, email or mobile. */
  changeMine: 'Ask to change my name, email or mobile',
  /**
   * NEW: on her own row, her ask to end her own door, and its sheet's name. Never offered to the
   * household's only client, whose ask ops refuses (`409 last_client`). Round six, slice S2: "this
   * page", where "Take me off home" read as her house.
   */
  takeMeOff: 'Ask to remove my access to this page',
  /** NEW: the button under the household's people, as the Settings canvas draws it. */
  add: 'Ask to add someone',
  /**
   * NEW (round six, slice S2): in place of `add`, under her own ask to add someone while it waits:
   * ops holds one open ask to add a person a household (its section 4, "The caps").
   */
  addAgainLater: 'You can ask to add another person once we have answered this one.',
  /** NEW: in place of `add`, while somebody else's ask to add a person waits on the office. */
  addHeld:
    'Someone on your household has asked us to add a person. You can ask again once we have answered.',
  /**
   * NEW: under the household's people, while somebody else's ask to take a person off, or to change
   * what a person sees, waits on the office, which holds every ask of its kind on the household.
   */
  aboutHeld:
    'Someone on your household has asked us to take a person off, or to change what they see. You can ask again once we have answered.',
  /** NEW: her ask to change what a person sees, on that person's row while it is said. */
  askedToChange: 'You asked us to change what they see.',
  /** NEW: her ask to take a person off, on that person's row while it is said. */
  askedToTakeOff: 'You asked us to take them off.',
  /** NEW: her own contact change, on her own row while it is said. */
  askedToChangeMine: 'You asked us to change your name, email or mobile.',
  /** NEW: her own leave, on her own row while it is said, in `takeMeOff`'s words. */
  askedToTakeMeOff: 'You asked us to remove your access to this page.',
  /** NEW: the heading over where she is signed in, as the Settings canvas draws it. */
  whereSignedIn: 'Where you are signed in',
  /** NEW: under that heading, as the Settings canvas draws it. */
  noPassword: 'There is no password. We email you a code each time you sign in.',
  /**
   * NEW (home #11): in place of that, where she signed in by her mobile number, which only a host
   * that can text takes, as the sign-in screen's first step says it.
   */
  noPasswordByText: 'There is no password. We email or text you a code each time you sign in.',
  /**
   * NEW: the outlined button under that list, as the Settings canvas draws it; the name of the sheet
   * its code step is drawn in, and that step's button once a code is typed (slice P2).
   */
  signOutElsewhere: 'Sign out everywhere else',
  /** NEW: once the Worker has ended them, before the list is read again. */
  signedOutElsewhere: 'Signed out everywhere else.',
  /**
   * NEW (slice P2 of the pre-launch fixes): why signing out everywhere else asks for a fresh code,
   * said first on its code step, as each ask's own line says it. The Worker asks for one, so a copy
   * of her cookie can never end the sessions she signs in with.
   */
  signOutElsewhereFreshCode: 'To sign out everywhere else, we email you a fresh code first.',
  /**
   * NEW (slice P2 of the pre-launch fixes): no fresh code can be asked for here to sign out
   * everywhere else: sign-in is closed. The phone follows it, and then a full stop.
   */
  signOutElsewhereCannotBeforePhone:
    'Signing out everywhere else cannot be done here right now. Please call',
  /** NEW: the shipped sign-out, of this browser alone, as the Settings canvas draws it. */
  signOutHere: 'Sign out here',
  /**
   * NEW: no fresh code can be asked for here for one of these asks: sign-in is closed. Said too,
   * with the focus, when the request door answers 404 for an ask about the household or about
   * herself, to add someone, to change her own details or to remove her own access, the sheet
   * staying open on it (the owner's rule of 2026-10-20, never silent). The phone follows it, and
   * then a full stop.
   */
  cannotBeforePhone: 'This cannot be asked for here right now. Please call',
  /**
   * NEW (the owner's rule of 2026-10-20, never silent): said in the sheet of an ask about a person
   * on the household, to take them off or to change what they see, with the focus, when the request
   * door answers 404, which it does alike for a person no longer on the household and one it never
   * names (CLAUDE.md binding rule 2), so the words are the same either way; the sheet stays open on
   * it, even where the page read again no longer lists the person. The phone follows it, and then a
   * full stop.
   */
  personNotHereBeforePhone: 'This person can no longer be asked about here. Please call',
} as const;

/**
 * THE SHEETS OF PEOPLE AND SIGN-IN (slice A3), each built as Home's edit sheet is, on Schedule's
 * sheet with Feedback's fields, Schedule's "Send the request" and the first sentence of the line
 * under it (`EDIT_HOME.nothingChanges`), and the code step (`FRESH_CODE`). Every line here is NEW,
 * for the owner to read before clients are invited, but where it says otherwise.
 */
export const PEOPLE_SHEETS = {
  /** NEW: the field for the name of a person to add. */
  theirName: 'Their name',
  /** NEW: the field for her address. */
  theirEmail: 'Their email',
  /** NEW: the field for her number, which goes in place of an address or beside it. */
  theirMobile: 'Or their mobile number',
  /** NEW: the question of the role a person to add is asked for with. */
  whatCanTheyDo: 'What can they do here?',
  /** NEW: the seam's `member`. */
  member: 'See visits and messages, and write to us',
  /**
   * NEW: the seam's `client`, since ops's section 12 (decision 15, 2026-10-17), while every home she
   * may name is chosen, or none yet.
   */
  client: 'Also pay invoices and change visits, as you can',
  /**
   * NEW (round six, slice S2): the same, while only some of her homes are chosen: one who pays then
   * sees and changes visits at those homes alone, so cannot do all she can.
   */
  clientSomeHomes: 'Also pay invoices and change visits',
  /**
   * NEW (round six, slice S2): under the homes, for one who pays. Her homes limit what she sees and
   * which visits she may change, never what she may pay: a client door reads every invoice of the
   * household (`invoicesFor`, `api/src/db/copy.ts`), and ops takes `invoice.pay` from a client for
   * any invoice of it (its section 4).
   */
  payerPaysAll: 'They can pay every invoice of your household, whichever homes they see.',
  /** NEW: the question of the homes a person to add, or a person changed, sees. */
  whichHomes: 'Which homes can they see?',
  /**
   * NEW: under the fields of a person to add. Ops invites her once the office has added her, and
   * the invitation is an email home sends (`docs/seam.md` section 6).
   */
  invitation:
    'We email them an invitation once the office has added them, so please ask them first.',
  /** NEW: why a person to add asks for a fresh code, said first on its code step. */
  addFreshCode: 'To add someone, we email you a fresh code first.',
  /** NEW: no fresh code can be asked for here for a person to add. The phone follows it. */
  addCannotBeforePhone: 'Someone cannot be added here right now. Please call',
  /** NEW: the name of a person to add, left empty. */
  noName: 'Please type their name.',
  /** NEW: neither an address nor a number for a person to add. */
  noContact: 'Please type their email or their mobile number.',
  /** NEW: an address the door would refuse. */
  badEmail: 'That email does not look right.',
  /** NEW: a number the door would refuse. */
  badMobile: 'That number does not look right. Type it with its area code.',
  /** NEW: no home chosen. */
  noHomes: 'Please choose at least one home.',
  /**
   * NEW: under the homes of a person whose homes are changed. Ops invites her again with the new
   * homes, which ends her old door, and home emails the invitation again (its section 12.4).
   */
  newInvitation:
    'Once the office has made the change, we email them a new invitation to sign in with.',
  /** NEW: why changing what a person sees asks for a fresh code. */
  changeFreshCode: 'To change what someone sees, we email you a fresh code first.',
  /** NEW: what taking a person off comes to, once the office has done it. */
  takeOffLine: 'Once the office has done it, they are off your household and cannot sign in here.',
  /** NEW: why taking a person off asks for a fresh code. */
  takeOffFreshCode: 'To take someone off, we email you a fresh code first.',
  /** NEW: the field for her own name, as the office typed it, to change. */
  yourName: 'Your name',
  /** NEW: the field for a new address of her own. */
  yourNewEmail: 'Your new email',
  /** NEW: the field for a new number of her own. */
  yourNewMobile: 'Your new mobile number',
  /**
   * NEW: under her own details. A changed email means signing in with the new one, which ops says
   * the office's answer tells her too (its section 12.4).
   */
  newEmailSignIn: 'Once the office has changed your email, you sign in with the new one.',
  /** NEW: why changing her own details asks for a fresh code. */
  contactFreshCode: 'To change your details, we email you a fresh code first.',
  /** NEW: what her leave comes to, once the office has done it. */
  leaveLine: 'Once the office has done it, you can no longer sign in here.',
  /** NEW: why her leave asks for a fresh code, in `PEOPLE.takeMeOff`'s words (round six, slice S2). */
  leaveFreshCode: 'To remove your access to this page, we email you a fresh code first.',
} as const;

/**
 * SEAM (ops's section 12.5 of 2026-10-17, "The refusals' sentences", approved by the owner that
 * day): `409 last_client`, her leave as the household's only client at home, or taking that client
 * off, word for word up to the office's number, which the screen makes a link to call, followed by
 * a full stop. `contract-words.test.ts` holds it to the contract.
 */
export const LAST_CLIENT_BEFORE_PHONE =
  'You are the only person who can manage this household at home, so this cannot be done here. Call or text the office at';

/**
 * NEW: ops's word for a person on the household (its section 3, `household.people`), said first on
 * her row. `second_household`, the word for a person added from home, has none: what her door lets
 * her do says it.
 */
const HOUSEHOLD_ROLE_WORDS: ReadonlyMap<string, string> = new Map([
  ['primary', 'Main contact.'],
  ['billing', 'Billing contact.'],
  ['emergency', 'Emergency contact.'],
  ['property_manager', 'Property manager.'],
]);

/**
 * NEW: what a person's door lets her do here: a client pays and changes visits; a member sees visits
 * and messages, for her homes by their labels when her door is limited to some. Null with no door.
 */
export function canDo(door: string | undefined, homes?: readonly string[]): string | null {
  if (door === 'client') return 'Pays and changes visits.';
  if (door !== 'member') return null;
  const where = homes === undefined || homes.length === 0 ? '' : ` for ${listed(homes)}`;
  return `Sees visits and messages${where}.`;
}

/**
 * NEW: a person's line on the household: ops's word for her, then what her door lets her do, "Main
 * contact. Pays and changes visits."; either alone where there is only one; null with neither.
 */
export function personLine(
  householdRole: string | undefined,
  door: string | undefined,
  homes?: readonly string[],
): string | null {
  const said = [
    householdRole === undefined ? undefined : HOUSEHOLD_ROLE_WORDS.get(householdRole),
    canDo(door, homes) ?? undefined,
  ].filter((part): part is string => part !== undefined);
  return said.length === 0 ? null : said.join(' ');
}

/**
 * NEW: a person ops invited and has not granted: "Invited on 12 Oct. Has not signed in yet.", the
 * day from the membership push's `invited_at`, or "Invited." alone where ops said no day.
 */
export function invitedLine(day: string | undefined): string {
  const on = day === undefined ? null : shortDate(day);
  return `${on === null ? 'Invited.' : `Invited on ${on}.`} Has not signed in yet.`;
}

/** NEW: her own name on her row, "Rosa Alvarez (you)", or "You" where ops sent none. */
export const yourName = (name: string | undefined) =>
  name === undefined ? 'You' : `${name} (you)`;

/** NEW: her own ask to add someone, by the name she gave, while it is said. */
export const youAskedToAdd = (name: string | undefined) =>
  `You asked us to add ${name ?? 'someone'}.`;

/**
 * NEW (round six, slice S2): on another person's row, in place of "Ask to take them off", while her
 * own ask to take someone else off waits, which holds every ask of its kind on the household (ops's
 * section 12.4, one open a household): "To take them off as well, ask once we have answered your
 * ask about Luis Alvarez.", by the name the office typed, or "your other ask" where it sent none.
 */
export const takeOffAsWell = (name: string | undefined) =>
  `To take them off as well, ask once we have answered ${name === undefined ? 'your other ask' : `your ask about ${name}`}.`;

/** NEW (round six, slice S2): the same, in place of "Ask to change what they see". */
export const changeAsWell = (name: string | undefined) =>
  `To change what they see as well, ask once we have answered ${name === undefined ? 'your other ask' : `your ask about ${name}`}.`;

/** NEW: the browsers home names (`api/src/lib/device.ts`, `BROWSERS`), each by its own name. */
const BROWSER_WORDS: ReadonlyMap<string, string> = new Map([
  ['safari', 'Safari'],
  ['chrome', 'Chrome'],
  ['firefox', 'Firefox'],
  ['edge', 'Edge'],
  ['samsung', 'Samsung Internet'],
  ['opera', 'Opera'],
]);

/** NEW: the phones, tablets and computers home names (`SYSTEMS`), as a sentence says each. */
const SYSTEM_WORDS: ReadonlyMap<string, string> = new Map([
  ['iphone', 'an iPhone'],
  ['ipad', 'an iPad'],
  ['android_phone', 'an Android phone'],
  ['android_tablet', 'an Android tablet'],
  ['mac', 'a Mac'],
  ['windows', 'a Windows computer'],
  ['chromebook', 'a Chromebook'],
  ['linux', 'a Linux computer'],
]);

/**
 * NEW: a phone or computer she is signed in on, from the words kept when its code was typed:
 * "Safari on an iPhone", "Chrome on a Windows computer"; the system alone, "An iPad", for a browser
 * the list does not name; and "A phone or computer" for anything else, a session made before the
 * words were kept among them. Never what its browser sent, a place or a network.
 */
export function deviceWords(browser: string | undefined, system: string | undefined): string {
  const on = system === undefined ? undefined : SYSTEM_WORDS.get(system);
  if (on === undefined) return 'A phone or computer';
  const named = browser === undefined ? undefined : BROWSER_WORDS.get(browser);
  return named === undefined ? `${on[0]!.toUpperCase()}${on.slice(1)}` : `${named} on ${on}`;
}

/** NEW: this browser's session, "This one. Signed in 17 Oct.", as the Settings canvas draws it. */
export const thisOne = (signedIn: string) =>
  `This one. Signed in ${shortDate(signedIn) ?? signedIn}.`;

/**
 * NEW, as the Settings canvas draws it: another phone or computer, "Signed in 2 Oct. Last used 15 Oct.", and
 * "Last used today." for one used on the business's day the page was read.
 */
export function usedLine(signedIn: string, lastUsed: string, today: string): string {
  const used = lastUsed === today ? 'today' : (shortDate(lastUsed) ?? lastUsed);
  return `Signed in ${shortDate(signedIn) ?? signedIn}. Last used ${used}.`;
}

/**
 * NEW: how many more of her other sessions there are than those listed, "And 3 more.", and "And at
 * least 91 more." where the Worker stopped counting.
 */
export const andMore = (count: number, atLeast: boolean) =>
  atLeast ? `And at least ${count} more.` : `And ${count} more.`;

/**
 * HOW YOU PAY (slice A2), Account's page at `/account/paying`, a client's alone, built from the
 * Settings canvas's Settings-Paying artboard in today's look, around Payments' own card for the
 * saved method and its Stop sheet (`components/SavedMethod.tsx`). Every line here is NEW, for the
 * owner to read before clients are invited, but where it says SEAM. Where a line names a tick or
 * "I'm paying by Zelle", it names them in the seam's own words (`BANK.tick`, `CARD.tick`,
 * `SAVED_METHOD_PAYS.payingByZelle`). Each says only what ops has said, and what she can do: a
 * method saves, stops or changes only as ops decides (CLAUDE.md binding rule 1), and automatic
 * payments start by her tick as she pays an invoice, on its bank screen or, since ops #70 (P57), on
 * its card screen, each drawn only while ops offers it (`pay.offer_to_save`), and, since round
 * five's slice A5, by saving a bank account or a card with nothing owed, where the Worker says ops's
 * door for it is open on its host (`SAVE`, ops's section 12.2). The lines that say how to start name
 * both ticks; the one invoice How you pay offers opens on its bank screen, the way ops lists before
 * the card.
 */
export const HOW_YOU_PAY = {
  /**
   * NEW: under "You pay each invoice yourself.", while an invoice says Stripe is on at ops: how
   * automatic payments start, by her tick on the bank screen or on the card screen.
   */
  howToStart: `Automatic payments start when you pay an invoice from your bank account and tick “${BANK.tick}”, or by card and tick “${CARD.tick}”.`,
  /**
   * NEW (slice A5): in `howToStart`'s place while How you pay offers to save a way to pay with
   * nothing owed (ops's section 12.2), which it names first, the two ticks after it as before.
   */
  howToStartOrSave: `Automatic payments start when you save a bank account or a card below, or when you pay an invoice from your bank account and tick “${BANK.tick}”, or by card and tick “${CARD.tick}”.`,
  /** NEW: after `howToStart`, while Stripe is on and nothing at all is owed. */
  nextInvoice: 'You can do that when your next invoice comes.',
  /**
   * NEW: the button under the newest owed invoice whose bank screen offers the tick, which opens
   * the invoice on its bank screen with the tick unticked: home never ticks it for her.
   */
  payFromBank: 'Pay it from your bank account',
  /**
   * NEW: under "Stopped. Nothing more is charged automatically.", how to start again, by either
   * tick.
   */
  startAgain: `To start again, tick “${BANK.tick}” the next time you pay an invoice from your bank account, or “${CARD.tick}” the next time you pay one by card.`,
  /**
   * NEW: the first line of how a saved bank account is charged, the bank tick's terms
   * (`AUTOPAY_BANK_TERMS`), which she agreed to, said of the account she has saved, with the clean
   * named as the return page names it. The two lines after it are the terms' own sentences.
   */
  bankCharged:
    "We charge it for each clean's invoice after you tell us the clean went well, or once we have checked with you that it did.",
  /** SEAM: a sentence of `AUTOPAY_BANK_TERMS`, word for word. */
  bankReceipt:
    'Each time, we email a receipt if we have an email address for you, unless you have asked us not to.',
  /** SEAM: a sentence of `AUTOPAY_BANK_TERMS`, word for word. */
  bankNotRight: 'If you tell us something was not right, nothing is charged until it is put right.',
  /**
   * NEW (round six, slice S4): under the heading of how the saved method is charged, before the
   * words ops holds for the version she agreed under (12.1, `terms_words`), which are said in its
   * place's lines where ops holds them, as ops holds them: so How you pay never says less than the
   * words she agreed to (the replay of 2026-10-18). It names the block in 12.1's own words, "the
   * words we hold for your automatic payment", as Your agreement heads it (`AGREEMENT.wordsWeHold`).
   */
  inTheWordsWeHold: 'In the words we hold for your automatic payment:',
  /** NEW: how to pay one invoice by Zelle instead, before the link to Payments. */
  zelleInsteadBefore: 'To pay one invoice by Zelle instead, open it in',
  /** NEW: after the link to Payments, the button in the seam's words. */
  zelleInsteadAfter: `before it is charged, and press “${SAVED_METHOD_PAYS.payingByZelle}”.`,
  /**
   * NEW: the heading over how to change to a different account, while a method stands that is not
   * a card: a bank account, Link or a type ops does not know.
   */
  differentAccount: 'A different account',
  /**
   * NEW (round six, slice S4): the same heading while the method that stands is a card, which the
   * replay of 2026-10-18 found headed "A different account" (`differentMethod`).
   */
  differentCard: 'A different card',
  /**
   * NEW: under it. Ops has no way to replace a saved method without paying an invoice, so it is a
   * Stop, then either tick the next time she pays an invoice from her bank account or by card.
   */
  stopThisFirst: `Stop this one first. Then tick “${BANK.tick}” the next time you pay an invoice from your bank account, or “${CARD.tick}” the next time you pay one by card.`,
  /** NEW: the heading over the ways to pay, while nothing is saved. */
  waysToPay: 'Ways to pay each invoice',
  /** NEW: under the ways to pay while Stripe is on, where she chooses among them. */
  chooseEachTime: "You choose each time, on the invoice's own page.",
} as const;

/** SEAM: the noun of a saved method of a type ops does not know, as `savedMethodNoun` says it. */
const UNKNOWN_METHOD = savedMethodNoun(undefined);

/**
 * NEW: the heading over how the saved method is charged, by its noun (`savedMethodNoun`): "How your
 * saved bank account is charged", "How your saved card is charged", "How your saved Link account is
 * charged", and "How your saved payment method is charged" for a type ops does not know.
 */
export function howCharged(methodType: string | undefined): string {
  const noun = savedMethodNoun(methodType);
  return noun === UNKNOWN_METHOD
    ? `How your ${noun} is charged`
    : `How your saved ${noun} is charged`;
}

/** The heading over how to change to a different one, by the method she has (`HOW_YOU_PAY`). */
export const differentMethod = (methodType: string | undefined): string =>
  methodType === CARD_METHOD ? HOW_YOU_PAY.differentCard : HOW_YOU_PAY.differentAccount;

/**
 * How the saved method is charged, in its own terms, under `howCharged`, where ops holds no words
 * for it (12.1, `terms_words`); where it holds them, How you pay says them instead, after
 * `HOW_YOU_PAY.inTheWordsWeHold` (round six, slice S4). A bank account: the bank
 * tick's terms, which she agreed to (`HOW_YOU_PAY.bankCharged`, then two of `AUTOPAY_BANK_TERMS`'s
 * sentences, word for word). Any other method, a card, Link or a type ops does not know, which no
 * such terms cover: the lines ops prints for a saved method on an invoice, SEAM
 * `weWillCharge` without the hours, which the household's method does not carry, and the sad face's
 * SEAM line, "Nothing is charged to your Visa ending 4242 until what you told us is put right.",
 * after a NEW clause that says when it applies, "If you tell us a clean was not right,". Never a
 * receipt, which ops says per invoice alone for such a method. A card may be saved on the office's
 * page or by the card tick (`AUTOPAY_CARD_TERMS`, P57), and the household's method does not say
 * which, so a card is said in these lines either way.
 */
export function chargedHow(label: string | undefined, methodType: string | undefined): string[] {
  if (methodType === BANK_METHOD) {
    return [HOW_YOU_PAY.bankCharged, HOW_YOU_PAY.bankReceipt, HOW_YOU_PAY.bankNotRight];
  }
  const method = yourMethod(label, methodType);
  const nothing = nothingCharged(method);
  return [
    weWillCharge(method, null),
    `If you tell us a clean was not right, ${nothing[0]!.toLowerCase()}${nothing.slice(1)}`,
  ];
}

/**
 * NEW: the newest owed invoice that offers the tick, in one line above "Pay it from your bank
 * account": its day, its home's name when she sees several homes, and what is still to pay, "Friday
 * 16 October, Walk home D, $225.".
 */
export function owedInOneLine(
  day: string | null,
  home: string | undefined,
  amount: string,
): string {
  return `${[day, home, amount].filter((part) => part !== null && part !== undefined).join(', ')}.`;
}

/**
 * YOUR AGREEMENT (round five, slice C2), Account's page at `/account/agreement`, reached from
 * Account's "Your regular cleans": what was agreed for each home and service, as ops's section 12.1
 * of its revision of 2026-10-17 sends it (`docs/seam.md` section 10 and P63). No artboard in
 * `design/home` draws it: it is built from Account's own parts and Home's "What we have written
 * down", a label over a value on each row, and goes on business issue 13's list. So every line here
 * is NEW, for the owner to read before clients are invited, but where it says otherwise:
 *
 * - SEAM: the words section 12.1 gives home for what it pushes, held by `contract-words.test.ts`:
 *   "the published price" and "quoted after a walkthrough" for how a price was set, never a term of
 *   home's own, "the words we hold for your automatic payment" for the block of words ops holds, and
 *   the pricing page's sentence on its rates, which 12.1 asks home to draw from the website.
 * - WEBSITE: the website's own words, quoted and never reworded, each with its file and line at
 *   site `c087a1b`, from the sections its `docs/api/agreement-pages.md` (version 1, 2026-10-17)
 *   promises to keep, and before it changes a sentence there it opens an issue on home. The business
 *   publishes one rule, "no signup fee, no cancellation fee, no minimum term", as ops's 12.1 puts it,
 *   and home quotes the Terms' own two sentences that say it.
 *
 * It never sounds like a contract: nothing here says she signed or names the company's legal suffix.
 * Nobody is named as having agreed, and no line says "you agreed": ops records the person a link was
 * made for, who may not be the person who pressed (the check of the agreement plan, 2026-10-17), so
 * each line says what was agreed, where and on which day. Never a visit's window, its start, its
 * finish or its length, and never "since" before a year.
 */
export const AGREEMENT = {
  /**
   * NEW: the page's heading, its name on Account under "Your regular cleans", and the heading of a
   * read that did not go through: the name ops's section 12.1 and the website's
   * `agreement-pages.md` give the page.
   */
  heading: 'Your agreement',
  /**
   * NEW: the row for the day of the household's first agreement (12.1, `booked_on`), its value the
   * day with its year (`fullDate`), so the year never follows "since".
   */
  withUsSince: 'With us since',
  /** NEW: the heading of an agreement for a service this build has no word for. */
  anotherService: 'Another service',
  /** WEBSITE (`src/pages/pricing.astro` l.48, a column of the price table): `one_time`. */
  oneTime: 'One time',
  /** NEW: ops's `active`. */
  active: 'Active.',
  /** NEW: ops's `paused`, said alone only where ops gives no days for the pause. */
  paused: 'Paused.',
  /** NEW: ops's `ended`, where ops gives no last day. */
  ended: 'Ended.',
  /** NEW: the row for the agreed price of one visit, a client's alone. */
  priceOfOneVisit: 'Price of one visit',
  /** SEAM (12.1, `price_source`): how the three `card_*` sources are worded, after the price. */
  published: 'the published price',
  /** SEAM (12.1, `price_source`): how `quoted_walkthrough` is worded, after the price. */
  walkthrough: 'quoted after a walkthrough',
  /** NEW: the row for the card's row and column as published (12.1, `card.label`). */
  line: 'Line on the price list',
  /**
   * NEW: under the line, a client's alone, where the pricing page publishes the line's figure as
   * "from" (12.1, `card.from`, "a starting price, never shown as exact").
   */
  startingPrice: 'The price list shows this line as a starting price.',
  /** NEW: the row for a home watch season (12.1, `season`). */
  season: 'Season',
  /** NEW: the row for the day it is in force from (12.1, `starts_on`). */
  inForceFrom: 'In force from',
  /** NEW: the row for its planned last day, on one still in place (12.1, `ends_on`). */
  lastDay: 'Last day',
  /** NEW: the row for the day ops recorded the yes (12.1, `agreed_on`). */
  agreedOn: 'Agreed on',
  /** NEW: the row for the standing add-ons, drawn as a list (12.1, `addons`). */
  addedToEachVisit: 'Added to each visit',
  /** NEW: in that row while there is none, as in every agreement of ops's 12.1 revision. */
  nothingAdded: 'Nothing',
  /** NEW: the row for when invoices are due, a client's alone (12.1, `invoice_terms_days`). */
  invoices: 'Invoices',
  /**
   * NEW: the heading over the written scope the agreement pins (12.1, `scope`), in the words of
   * `what-we-cannot-say.md`'s line "You get a written scope of work before we start".
   */
  scope: 'The written scope of work',
  /** NEW: a scope whose words are not here. The phone follows it, and then a full stop. */
  scopeBeforePhone: 'To read it, please call',
  /** NEW: the heading over the agreements that ended in the twelve months ops sends. */
  endedHeading: 'Ended in the last twelve months',
  /**
   * NEW (round six, slice S4): where the agreements are at several homes, the button under what
   * each one says first, its home, service, state, how often, a pause and its price, that opens the
   * rest, one agreement at a time; it says whether it is open by its chevron and `aria-expanded`,
   * never by changing its words, and is named with the home and the service it opens.
   */
  details: 'The details',
  /** NEW: once the agreements have come, while none is in place. */
  noneInPlace: 'No agreement is in place right now.',
  /** NEW: the heading over the household's saved method, a client's alone. */
  automaticPayment: 'Your automatic payment',
  /**
   * SEAM (12.1, on `household`: "Home words the block "the words we hold for your automatic
   * payment""): the heading over `terms_words`, which are drawn as ops sends them, under it.
   */
  wordsWeHold: 'The words we hold for your automatic payment',
  /**
   * NEW: where ops sends no words for the saved method, as for one saved before ops kept its words.
   * The phone follows it, and then a full stop.
   */
  noWordsBeforePhone:
    'The words for your automatic payment are not shown here. To ask about them, please call',
  /** NEW: the heading over the website's terms and its pages. */
  publishedTerms: 'The published terms',
  /** NEW: before the Terms' two sentences, a client's alone. */
  ourTermsSay: 'Our terms say:',
  /** WEBSITE (`src/pages/terms.astro` l.117, section `#payment`). */
  noSignupFee: 'No signup fee, no minimum term, and no notice period to stop.',
  /** WEBSITE (`src/pages/terms.astro` l.184, section `#ending`). */
  eitherSideCanStop: 'Either side can stop at any time, for any reason, with no fee.',
  /**
   * NEW (round six, slice S4): before the pricing page's sentence, a client's alone, with the link
   * to that page straight under the sentence: the sentence points to "the foot of this page", and
   * this names the page it means, the one the link opens, whose foot prints the date, never Your
   * agreement's, which shows none (the replay of 2026-10-18).
   */
  pricingPageSays: 'The pricing page, linked below, says:',
  /**
   * WEBSITE (`src/pages/pricing.astro` l.310-311, section `#billing`), SEAM too: 12.1 asks home to
   * draw it from the website it links, beside the page ops read the price from (`pricing_url`).
   */
  ratesReviewed:
    'Rates were last reviewed on the date shown at the foot of this page and hold for twelve months from it.',
  /** WEBSITE (`src/site.config.ts` l.532, the website's own menu): the link to the pricing page. */
  pricing: 'Pricing',
  /**
   * NEW: under the links. Ops holds no date of the three pages, so home links the current version of
   * each (12.1, the owner's decision 14 (a) of 2026-10-17).
   */
  asItReadsToday: 'Each link opens the page as it reads today.',
} as const;

/**
 * WEBSITE (`src/site.config.ts` l.272, l.286 and l.301, the services' names): an agreement's service
 * (12.1, `service`), as its heading; NEW "Another service" for a word this build does not know.
 */
const SERVICES: ReadonlyMap<string, string> = new Map([
  ['house_cleaning', 'House cleaning'],
  ['home_watch', 'Home watch'],
  ['turnover', 'Short-term rental turnover'],
]);

export const serviceName = (service?: string): string =>
  (service === undefined ? undefined : SERVICES.get(service)) ?? AGREEMENT.anotherService;

/**
 * An agreement's state in words: NEW "Active." and "Paused.", and "Ended on 5 October 2026." with its
 * last day, "Ended." without. Null for a state this build has no word for.
 */
export function agreementState(state: string | undefined, endedOn: string | null): string | null {
  if (state === 'active') return AGREEMENT.active;
  if (state === 'paused') return AGREEMENT.paused;
  if (state === 'ended') return endedOn === null ? AGREEMENT.ended : `Ended on ${endedOn}.`;
  return null;
}

/**
 * The price of one visit, a client's alone: the figure as the artboards write one (`dollars`), and
 * after it how it was set, in 12.1's two words alone, "$212, the published price" and "$60, quoted
 * after a walkthrough"; the figure alone where ops says neither.
 */
export function priceOfOneVisit(
  cents: number,
  priced: 'published' | 'walkthrough' | undefined,
): string {
  const how =
    priced === 'published'
      ? AGREEMENT.published
      : priced === 'walkthrough'
        ? AGREEMENT.walkthrough
        : null;
  return how === null ? dollars(cents) : `${dollars(cents)}, ${how}`;
}

/**
 * NEW: a home watch season: "16 visits, quoted at $960 in all" for a client, "16 visits" for a
 * member, who is given no total. Null with neither.
 */
export function seasonWords(visits?: number, totalCents?: number): string | null {
  const total = totalCents === undefined ? null : dollars(totalCents);
  if (visits === undefined) return total === null ? null : `Quoted at ${total} in all`;
  const count = `${visits} ${visits === 1 ? 'visit' : 'visits'}`;
  return total === null ? count : `${count}, quoted at ${total} in all`;
}

/**
 * NEW: a standing add-on in the list, in the card's own words: "Inside the fridge, $30" with its
 * price for a client, and how many where more than one, "Laundry, 2 at $25.50 each"; its words
 * alone, with how many, for a member.
 */
export function standingAddon(label: string, quantity?: number, unitCents?: number): string {
  const many = quantity !== undefined && quantity > 1;
  if (unitCents === undefined) return many ? `${label}, ${quantity}` : label;
  return many
    ? `${label}, ${quantity} at ${dollars(unitCents)} each`
    : `${label}, ${dollars(unitCents)}`;
}

/** NEW: when invoices are due, a client's alone: "Due 7 days after they are issued". */
export const invoicesDue = (days: number) =>
  `Due ${days} ${days === 1 ? 'day' : 'days'} after they are issued`;

/**
 * NEW: the day the office recorded that the first visit's deep clean was said before booking (12.1,
 * `deep_clean_disclosed_on`), a client's alone, the business saying it as 12.1 allows: "We said on 2
 * October 2026 that the first visit is priced as a deep clean."
 */
export const deepCleanSaid = (day: string) =>
  `We said on ${day} that the first visit is priced as a deep clean.`;

/**
 * NEW: which written scope it is, by ops's version and the day it holds from: "Version 1, in use
 * from 14 September 2026.", "Version 2." or "In use from 14 September 2026.". Null with neither.
 */
export function scopeVersion(version?: number, from?: string | null): string | null {
  const day = from ?? null;
  if (version === undefined) return day === null ? null : `In use from ${day}.`;
  return day === null ? `Version ${version}.` : `Version ${version}, in use from ${day}.`;
}

/**
 * NEW: where the saved method was agreed (12.1, `household.autopay.agreed_through`), with each tick
 * named in the seam's words (`BANK.tick`, `CARD.tick`), and never who agreed. Null for a place this
 * build has no word for, and for none.
 */
const AGREED_THROUGH: ReadonlyMap<string, string> = new Map([
  ['stripe_box', "Agreed in the box to save it on Stripe's payment page."],
  ['bank_tick', `Agreed by ticking “${BANK.tick}” while paying an invoice from a bank account.`],
  ['card_tick', `Agreed by ticking “${CARD.tick}” while paying an invoice by card.`],
  ['setup_bank', 'Agreed while saving a bank account from the account page, with nothing paid.'],
  ['setup_card', 'Agreed while saving a card from the account page, with nothing paid.'],
]);

export const agreedThrough = (through?: string): string | null =>
  (through === undefined ? undefined : AGREED_THROUGH.get(through)) ?? null;

/**
 * HER ASKS ABOUT HER REGULAR CLEANS (round five, slice C3, ops's section 12.4 of its revision of
 * 2026-10-17, live on both of its hosts from that evening): "Ask to pause", "Ask to resume", "Ask to
 * change" and "Ask to end", under each home's regular cleans on Account and on Your agreement, each
 * opening a sheet like the skip or move sheet (`components/CleansSheet.tsx`). No artboard draws any
 * of them: they are built from Schedule's sheet, Feedback's chips and its field, and go on business
 * issue 13's list. So every line here is NEW, for the owner to read before clients are invited, but
 * where it says otherwise. Each says what was asked and where it stands, and never that anything has
 * changed: a pause, a change or an ending is drawn only once ops's `home` and `agreement` pushes say
 * so (CLAUDE.md binding rule 1). A new cadence's price is the office's to say, in its own message,
 * never home's. Home's own lines say "call" alone, as every line of home's own does, and each that
 * ends in the office's number is followed by it and then a full stop.
 */
export const CLEANS = {
  /** NEW: the button under a home's regular cleans, and the name of the sheet it opens. */
  pause: 'Ask to pause',
  /** NEW: in place of `pause`, while ops says the regular cleans are paused. */
  resume: 'Ask to resume',
  /** NEW: the button, and its sheet's name. */
  change: 'Ask to change',
  /** NEW: the button, and its sheet's name. */
  end: 'Ask to end',
  /** NEW: the pause sheet's first day (ops's `from`). */
  pauseFrom: 'Pause from',
  /**
   * NEW: the pause sheet's last day (ops's `until`), as Account says a pause, "Paused from 1
   * November until 15 March."
   */
  until: 'Until',
  /** NEW: in place of a last day, the seam's reading of `until` null. */
  noReturnDate: 'No return date yet',
  /** NEW: the resume sheet's day (ops's `from`). */
  resumeFrom: 'Start again from',
  /** NEW: the change sheet's question about the cadence, and the line that says it was asked. */
  howOften: 'How often',
  /** NEW: the change sheet's question about the weekday, and the line that says it was asked. */
  whichDay: 'Which day',
  /** NEW: the first choice of each question, chosen until she chooses another (the seam's `null`). */
  keep: 'Keep as it is',
  /** NEW: the arrival window, in the line that says it was asked. */
  arrivalWindow: 'Arrival window',
  /** NEW: the change sheet's field for the arrival window, in her own words (ops's `window`). */
  windowInYourWords: 'Arrival window, in your words',
  /** NEW: under that field. */
  windowKept: 'Leave it empty to keep it as it is.',
  /** NEW: the change sheet's first day (ops's `from`). */
  changeFrom: 'From',
  /**
   * NEW: under how often. A new cadence comes at the price published for it, and the office's
   * message names it (ops's section 12.4): home never says a price for it.
   */
  priceInMessage:
    "If how often changes, the office's message will tell you the price of each clean.",
  /**
   * NEW: in place of how often, while ops says the regular cleans are paused: ops takes no new
   * cadence on a paused schedule (`409 already_paused`), and takes a new weekday or window. The
   * phone follows it, and then a full stop.
   */
  pausedCadenceBeforePhone:
    'While these regular cleans are paused, to change how often they come, please call',
  /** NEW: the end sheet's question, or its one answer, about which service ends (ops's `service`). */
  whichService: 'Which service',
  /** NEW: the end sheet's day (ops's `last_day`). */
  lastDay: 'Last day',
  /**
   * NEW (the fifth client-journey walk, 2026-10-20): the same day on the sheet that cancels what
   * does not repeat (`cancelAsk`), ops's `last_day` as ops reads it, "every later clean off the
   * calendar" (its section 12.4), in place of `lastDay`, which said end of a clean booked once.
   * Never where the sheet names the clean it cancels, which asks no day (`cancelVisitAsked`).
   */
  cancelAfter: 'Cancel every clean after',
  /**
   * NEW: under it. Ops says the office ends it "as she asked or after the call it will want to
   * make" (its section 12.4).
   */
  mayCall: 'The office may call you about it first.',
  /** NEW: every sheet's last field, for a note she may leave (ops's `note`). */
  note: 'A note for the office, if you like',
  /** NEW: on Your agreement, the heading over what she may ask about each home. */
  heading: 'Ask for a change',
  /**
   * NEW: an ask ops closed because the agreement it was about ended, or its home was taken off,
   * before the office decided it (`cancelled`, `schedule_ended`, ops's section 12.4).
   */
  closedEnded: 'This request was closed, because the agreement it was about has ended.',
  /**
   * NEW: under Account's regular cleans, for a client, in place of `ACCOUNT.changeCleansBeforePhone`
   * where a home offers an ask. The phone follows it, and then a full stop.
   */
  alsoByPhoneBeforePhone: 'You can also ask by calling',
  /**
   * NEW (the owner's rule of 2026-10-20, never silent): said in an ask's sheet, with the focus,
   * when the request door answers 404 for the home whose regular cleans it is about, which it does
   * alike for a home gone and one not hers to ask about (CLAUDE.md binding rule 2), so the words
   * are the same either way; "cleans", so it fits an ending that cancels a clean booked once too.
   * The sheet stays open on it, even where the page read again no longer holds the home. The phone
   * follows it, and then a full stop.
   */
  notHereBeforePhone: 'These cleans can no longer be changed here. Please call',
} as const;

/**
 * NEW (the fifth client-journey walk, 2026-10-20, where a home whose only live agreement was a
 * one-time clean drew "No regular cleans." and then "Ask to end"): in place of `CLEANS.end`, the
 * button and the name of its sheet where an ending ends nothing that repeats (`cancelOf`), the
 * service in the website's words as Your agreement heads it (`serviceName`), inside the sentence as
 * `endAsked` says it: "Ask to cancel house cleaning"; with several, "Ask to cancel house cleaning or
 * short-term rental turnover". The ask is the same `service.end`.
 *
 * NEW (ops's answer on ops #76, 2026-10-22: a one-time agreement's `starts_on` is not the day of its
 * clean, and the cancel "should name the open visit's scheduled_date and kind"): where a service it
 * cancels has an open visit at the home, that visit in place of the service, by its kind
 * (`visitKindWords`) and its day in words, "Ask to cancel the deep clean on Tuesday 27 October";
 * with several, each so, "Ask to cancel the deep clean on Tuesday 27 October or short-term rental
 * turnover". The service alone, as before, where it has none, or one of a kind this build has no
 * words for. Never a time of day, nor how long a visit lasts.
 */
export function cancelAsk(
  services: readonly string[],
  visits: ReadonlyMap<string, { readonly kind: string; readonly day: string }> = new Map(),
): string {
  const names = services.map((service) => {
    const visit = visits.get(service);
    const kind = visit === undefined ? null : visitKindWords(visit.kind);
    return visit === undefined || kind === null
      ? serviceName(service).toLowerCase()
      : `${kind} on ${visit.day}`;
  });
  const last = names.pop();
  if (last === undefined) return 'Ask to cancel';
  return names.length === 0
    ? `Ask to cancel ${last}`
    : `Ask to cancel ${names.join(', ')} or ${last}`;
}

/**
 * NEW (ops's answer on ops #76, 2026-10-22): a visit by its kind (ops's section 3, `visit.kind`), as
 * a cancel names it (`cancelAsk`), in the words home already uses for each: "the clean" for a plain
 * house cleaning, `one_time` or `recurring`, as every line of home's says a clean; "the deep clean",
 * as `deepCleanSaid` says one; "the home watch visit" and "the short-term rental turnover", the
 * services' names as Your agreement heads them (`serviceName`, WEBSITE), the first as the website's
 * home watch pages say a visit of it (`src/components/VisitReport.astro` l.3, "home watch visit");
 * and "the move-out clean", WEBSITE (`src/pages/deep-cleaning.astro` l.88, "move-out clean"), the one
 * kind home had no words for. Null for a kind this build has no words for, which is named by its
 * service instead.
 */
const VISIT_KINDS: ReadonlyMap<string, string> = new Map([
  ['recurring', 'the clean'],
  ['one_time', 'the clean'],
  ['deep_clean', 'the deep clean'],
  ['move_out', 'the move-out clean'],
  ['home_watch', 'the home watch visit'],
  ['turnover', 'the short-term rental turnover'],
]);

export const visitKindWords = (kind: string): string | null => VISIT_KINDS.get(kind) ?? null;

/**
 * NEW (ops #76, 2026-10-22, and the review of 2621dcd): on the cancel sheet, in place of its day
 * (`CLEANS.cancelAfter`), where the button names the one open visit the ask cancels (`cancelAsk`):
 * which clean the office is asked to cancel, by its kind (`visitKindWords`) and its day in words,
 * "This asks the office to cancel the deep clean on Tuesday 27 October." The sheet sends ops's
 * `last_day` as the day before it, so the office ends the agreement before that clean (ops's
 * section 12.4). Never a time of day, nor how long a visit lasts. Null for a kind this build has no
 * words for, where the sheet asks the day as before.
 */
export function cancelVisitAsked(kind: string, day: string): string | null {
  const words = visitKindWords(kind);
  return words === null ? null : `This asks the office to cancel ${words} on ${day}.`;
}

/**
 * NEW (round six, slice S2): under the pause sheet's Until, when the day typed is before the day the
 * pause starts, which ops refuses (`422 invalid` naming `until`), and which Until then starts from:
 * "Choose a day on or after Tuesday 10 November."
 */
export const untilOnOrAfter = (from: string) => `Choose a day on or after ${from}.`;

/**
 * NEW (round six, slice S2): under a day of a sheet about her regular cleans that is before today or
 * past day 365, which ops refuses, the last day with its year: "Choose a day from today to 20
 * October 2027."
 */
export const dayFromTodayTo = (last: string) => `Choose a day from today to ${last}.`;

/**
 * WHAT THE OFFICE DECIDED, where it answered an ask with none of its own words (round six, slice S2):
 * said from the `request` push's state, which is ops's own, so binding rule 1 holds, in place of a
 * line that read the same whether it agreed or not. Her asks about her regular cleans say both; her
 * people asks say the second, since one ops accepted has happened and the next push shows it.
 */
export const ANSWERED = {
  /**
   * NEW (the production-readiness review of 2026-10-18, P1): ops's `accepted` of an ask about her
   * regular cleans, with no words of the office's, after what was asked without its terms
   * (`askedWithoutTerms`). Ops accepts only once the thing has happened, as she asked or after a
   * call with her (its 12.4 and 12.7), so the office did act; what it settled is the home's line
   * above, as ops pushed it. In place of "The office agreed to this.", which said her asked days
   * were what the office did.
   */
  actedOn: 'The office has acted on this. What is in place now is shown above.',
  /**
   * NEW: ops's `declined`, with no words of the office's. The phone follows it, and then a full
   * stop.
   */
  notDoneBeforePhone: 'The office could not do this. To hear why, please call',
} as const;

/**
 * SEAM (ops's section 12.5 of its revision of 2026-10-17, "The refusals' sentences", approved by the
 * owner that day): the message of each refusal of an ask about her regular cleans, word for word,
 * each a `409`. The request door answers each first, from the home and the agreements ops last
 * pushed, writing nothing, and ops answers each as final; either way the sheet or the home says it,
 * with the office's number made a link where it names it (`components/SaidWithPhone.tsx`).
 */
export const SCHEDULE_REFUSAL_WORDS = {
  no_schedule: `This home has no standing schedule to change. Call or text the office at ${PHONE_SHOWN}.`,
  already_paused: `This schedule is paused. Call or text the office at ${PHONE_SHOWN} to change it.`,
  schedule_not_paused: 'This schedule is not paused.',
} as const;

/** One of ops's codes for an ask about her regular cleans refused, `SCHEDULE_REFUSAL_WORDS`' keys. */
export type ScheduleRefusal = keyof typeof SCHEDULE_REFUSAL_WORDS;

export const isScheduleRefusal = (code: string | undefined): code is ScheduleRefusal =>
  code !== undefined && Object.hasOwn(SCHEDULE_REFUSAL_WORDS, code);

/**
 * NEW: a cadence as the change sheet offers it, Schedule's heading's first half ("Every week", ARTBOARD
 * "Every two weeks", "Every four weeks"), or null for a word this build has none for.
 */
export const cadenceChoice = (cadence: string): string | null => EVERY.get(cadence) ?? null;

/** ARTBOARD (Schedule): a weekday as the change sheet offers it, as "on Tuesdays" says it. */
export const weekdayChoice = (weekday: string): string | null => ON.get(weekday) ?? null;

/**
 * NEW: who asked, at the start of the line that says what was asked: "You" for her own; for another
 * person on the household, the name the household lists, as Schedule names who asked to move a
 * visit; and "Someone on your household" where it lists none.
 */
export const whoAsked = (mine: boolean, name?: string): string =>
  mine ? 'You' : (name ?? 'Someone on your household');

/**
 * NEW: a pause asked, its days in words: "You asked to pause from Monday 2 November until Monday 30
 * November.", or "... from Monday 2 November, with no return date yet."
 */
export const pauseAsked = (who: string, from: string, until: string | null) =>
  until === null
    ? `${who} asked to pause from ${from}, with no return date yet.`
    : `${who} asked to pause from ${from} until ${until}.`;

/** NEW: a resume asked: "You asked to start again from Tuesday 1 December." */
export const resumeAsked = (who: string, from: string) =>
  `${who} asked to start again from ${from}.`;

/**
 * NEW (the production-readiness review of 2026-10-18, P1): what was asked about her regular cleans
 * without its days, cadence, weekday, window or last day, said only where ops accepted it with no
 * words of the office's (`ANSWERED.actedOn`), since the office may have settled other terms on a
 * call with her: "You asked to pause the regular cleans.", "You asked to start the regular cleans
 * again.", "You asked to change the regular cleans." and "You asked to end house cleaning.", the
 * service in the website's words (`serviceName`).
 */
export function askedWithoutTerms(
  who: string,
  type: CleansAskType,
  service: string | undefined,
): string {
  switch (type) {
    case 'schedule.pause':
      return `${who} asked to pause the regular cleans.`;
    case 'schedule.resume':
      return `${who} asked to start the regular cleans again.`;
    case 'schedule.change':
      return `${who} asked to change the regular cleans.`;
    case 'service.end':
      return `${who} asked to end ${serviceName(service).toLowerCase()}.`;
  }
}

/**
 * NEW: a change asked: "You asked to change the regular cleans from Tuesday 3 November.", then what
 * it changes, each as the sheet asked it, "How often: every week.", "Which day: Fridays." and her
 * window in her own words, which only her own ask carries, ending its sentence as the others do:
 * "Arrival window: “Mornings if possible”.", and with no stop of home's after her own, "Arrival
 * window: “Mornings, please.”" (round six, slice S2).
 */
export function changeAsked(
  who: string,
  from: string,
  change: { readonly cadence?: string; readonly weekday?: string; readonly window?: string },
): string {
  const said = [`${who} asked to change the regular cleans from ${from}.`];
  if (change.cadence !== undefined)
    said.push(`${CLEANS.howOften}: ${change.cadence.toLowerCase()}.`);
  if (change.weekday !== undefined) said.push(`${CLEANS.whichDay}: ${change.weekday}.`);
  if (change.window !== undefined) {
    const ended = /[.?!]$/.test(change.window.trimEnd());
    said.push(`${CLEANS.arrivalWindow}: “${change.window}”${ended ? '' : '.'}`);
  }
  return said.join(' ');
}

/**
 * NEW: an ending asked, the service in the website's words (`serviceName`) inside the sentence:
 * "You asked to end house cleaning, with Thursday 31 December as the last day."
 */
export const endAsked = (who: string, service: string | undefined, lastDay: string) =>
  `${who} asked to end ${serviceName(service).toLowerCase()}, with ${lastDay} as the last day.`;

/**
 * NEW (the fifth client-journey walk's leftovers, 2026-10-20, where a cancel sent from "Ask to cancel
 * house cleaning" was said as "You asked to end house cleaning, with ... as the last day."): in
 * place of `endAsked`, a cancel asked, said only where its button said cancel (`cancelAsk`) when she
 * asked, as the ask itself carries it (its `cancel`), and still once ops has ended the agreement and
 * answered her (the review of those leftovers), the service in the website's words
 * (`serviceName`), with the day she gave under `CLEANS.cancelAfter` as its sheet named it: "You
 * asked to cancel house cleaning: every clean after Wednesday 4 November."; and without the day, in
 * place of `askedWithoutTerms`, where ops accepted it with no words of the office's (P1): "You asked
 * to cancel house cleaning.". What was asked, never what the office did (CLAUDE.md binding rule 1).
 */
export function cancelAsked(who: string, service: string | undefined, after?: string): string {
  const asked = `${who} asked to cancel ${serviceName(service).toLowerCase()}`;
  return after === undefined ? `${asked}.` : `${asked}: every clean after ${after}.`;
}
